import { forwardRef, type ComponentProps } from 'react'

type BaseIconProps = ComponentProps<'svg'>
export type IconProps = Omit<BaseIconProps, 'children'>
type IconComponent<T> = React.FunctionComponent<T & React.RefAttributes<SVGSVGElement>>

export const BaseIcon: IconComponent<BaseIconProps> = forwardRef(function BaseIcon(
    { className, ...props }: BaseIconProps,
    ref
): JSX.Element {
    const customClassName = className ? ` ${className}` : ''
    return (
        <svg
            ref={ref}
            className={'LemonIcon' + customClassName} // We use .LemonIcon for compatibility with existing app icons, but this may change
            width="100%"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            {...props}
        />
    )
})

export const IconActivity: IconComponent<IconProps> = forwardRef(function IconActivity(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M8.043 2.789c.296-.936 1.621-.93 1.91.007L14.99 19.17l2.077-6.923A1.75 1.75 0 0 1 18.744 11h3.506a.75.75 0 0 1 0 1.5h-3.506a.25.25 0 0 0-.24.178l-2.55 8.5c-.283.946-1.622.95-1.913.006L8.991 4.77l-2.055 6.507A1.75 1.75 0 0 1 5.267 12.5H1.75a.75.75 0 0 1 0-1.5h3.517a.25.25 0 0 0 .239-.175L8.043 2.79Zm.476.449.478-.148-.478.147Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconAdvanced: IconComponent<IconProps> = forwardRef(function IconAdvanced(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.324 3h13.352c.258 0 .494 0 .692.016.213.018.446.057.677.175.329.168.596.435.764.765.118.23.157.463.175.676.016.198.016.434.016.692v13.352c0 .258 0 .494-.016.692a1.775 1.775 0 0 1-.175.677 1.75 1.75 0 0 1-.765.764c-.23.118-.463.157-.676.175-.198.016-.434.016-.692.016H5.324c-.258 0-.494 0-.692-.016a1.775 1.775 0 0 1-.676-.175 1.75 1.75 0 0 1-.765-.765 1.774 1.774 0 0 1-.175-.676A9.053 9.053 0 0 1 3 18.676V5.324c0-.258 0-.494.016-.692.018-.213.057-.446.175-.676a1.75 1.75 0 0 1 .765-.765c.23-.118.463-.157.676-.175C4.83 3 5.066 3 5.324 3ZM4.63 4.53h.001Zm.002-.001a.626.626 0 0 1 .121-.018c.13-.01.304-.011.596-.011h13.3c.292 0 .467 0 .596.011a.625.625 0 0 1 .12.018.25.25 0 0 1 .105.104.638.638 0 0 1 .018.121c.01.13.011.304.011.596v13.3c0 .292 0 .467-.011.596a.638.638 0 0 1-.018.12.25.25 0 0 1-.105.105.636.636 0 0 1-.12.018c-.13.01-.304.011-.596.011H5.35c-.292 0-.467 0-.596-.011a.638.638 0 0 1-.12-.018.25.25 0 0 1-.105-.105.625.625 0 0 1-.018-.12 8.336 8.336 0 0 1-.011-.596V5.35c0-.292 0-.467.011-.596a.626.626 0 0 1 .018-.12.25.25 0 0 1 .104-.105Zm-.103.102v.001Zm0 14.738v-.001Zm.1.101h.002Zm14.74 0h-.002Zm.1-.1v-.002Zm0-14.74v.002Zm-.102-.1h.001ZM15.18 8.79c-1.715-1.72-4.65-1.72-6.362 0a4.528 4.528 0 0 0 0 6.389 4.49 4.49 0 0 0 5.787.487l-.135-.134-.002-.002-1.95-1.95a.75.75 0 0 1 1.061-1.06l1.948 1.948.002.002.136.135a4.53 4.53 0 0 0-.485-5.815ZM7.757 7.73c2.298-2.307 6.185-2.307 8.486 0a6.028 6.028 0 0 1 0 8.508 5.99 5.99 0 0 1-8.486 0 6.028 6.028 0 0 1 0-8.507Z"
            />
        </BaseIcon>
    )
})

export const IconAI: IconComponent<IconProps> = forwardRef(function IconAI(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M5 13.163a5.585 5.585 0 0 1-.552-.223 3.094 3.094 0 0 1-.844-.565C3.335 12.113 3 11.65 3 11s.335-1.112.604-1.375c.266-.26.578-.439.844-.565.54-.255 1.227-.446 1.955-.591C7.883 8.173 9.86 8 12 8c2.139 0 4.117.173 5.598.469.727.145 1.413.336 1.954.591.266.126.578.305.844.565.269.263.604.726.604 1.375 0 .65-.335 1.113-.604 1.375-.266.26-.578.44-.844.565a5.58 5.58 0 0 1-.552.223V19c0 .615-.348 1.1-.755 1.447-.41.349-.959.63-1.572.85C15.443 21.74 13.79 22 12 22c-1.79 0-3.442-.26-4.673-.703-.613-.22-1.162-.501-1.572-.85C5.348 20.101 5 19.615 5 19v-5.837Zm13.332-1.359h.003c.738-.233 1.165-.508 1.165-.804 0-.295-.427-.57-1.165-.803l-.003-.001C17.002 9.778 14.662 9.5 12 9.5c-2.663 0-5.001.278-6.332.696l-.003.001c-.738.232-1.165.508-1.165.803 0 .296.427.571 1.165.803l.003.001c1.33.419 3.67.696 6.332.696 2.663 0 5.001-.277 6.332-.696Zm-.832 1.747c-1.47.284-3.408.45-5.5.45s-4.03-.166-5.5-.45V19c0 .006.002.112.227.304.223.19.59.394 1.108.58 1.03.371 2.502.615 4.165.615 1.663 0 3.135-.244 4.165-.614.519-.187.885-.392 1.108-.581.225-.192.227-.298.227-.305v-5.45Z"
                clipRule="evenodd"
            />
            <path d="M13.553 1.895a.5.5 0 0 1 .894 0c.496.991.667 1.162 1.659 1.658a.5.5 0 0 1 0 .894c-.992.496-1.163.667-1.659 1.659a.5.5 0 0 1-.894 0c-.496-.992-.667-1.163-1.659-1.659a.5.5 0 0 1 0-.894c.992-.496 1.163-.667 1.659-1.658ZM8.553 3.895a.5.5 0 0 1 .894 0c.174.348.31.484.659.658a.5.5 0 0 1 0 .894c-.349.175-.485.31-.659.659a.5.5 0 0 1-.894 0c-.174-.349-.31-.484-.659-.659a.5.5 0 0 1 0-.894c.349-.174.485-.31.659-.658Z" />
        </BaseIcon>
    )
})

export const IconAIText: IconComponent<IconProps> = forwardRef(function IconAIText(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M3 5.75A.75.75 0 0 1 3.75 5h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 5.75Zm14 4a.75.75 0 0 1 .67.415l1.389 2.776 2.776 1.388a.75.75 0 0 1 0 1.342l-2.776 1.388-1.388 2.776a.75.75 0 0 1-1.342 0l-1.388-2.776-2.776-1.388a.75.75 0 0 1 0-1.342l2.776-1.388 1.388-2.776A.75.75 0 0 1 17 9.75Zm0 2.427-.83 1.658a.75.75 0 0 1-.335.336L14.177 15l1.658.83a.75.75 0 0 1 .336.335L17 17.823l.83-1.658a.75.75 0 0 1 .335-.336L19.823 15l-1.658-.83a.75.75 0 0 1-.336-.335L17 12.177ZM3 12a.75.75 0 0 1 .75-.75h5.5a.75.75 0 0 1 0 1.5h-5.5A.75.75 0 0 1 3 12Zm0 6.25a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconApp: IconComponent<IconProps> = forwardRef(function IconApp(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M6.5 9a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1ZM10 9a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm3.5 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Z" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.5 9.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm3.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm3.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm-7-.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1ZM10 9a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm3-.5a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 5.75C2 4.784 2.784 4 3.75 4h16.5c.966 0 1.75.784 1.75 1.75v6.5a.75.75 0 0 1-1.5 0v-6.5a.25.25 0 0 0-.25-.25H3.75a.25.25 0 0 0-.25.25v13.5c0 .138.112.25.25.25h8.5a.75.75 0 0 1 0 1.5h-8.5A1.75 1.75 0 0 1 2 19.25V5.75Zm11.47 7.72a.75.75 0 0 1 .75-.187l7 2.154a.75.75 0 0 1 .115 1.388l-3.007 1.503-1.503 3.007a.75.75 0 0 1-1.388-.114l-2.154-7a.75.75 0 0 1 .187-.751Zm1.663 1.663 1.187 3.857.778-1.556a.75.75 0 0 1 .336-.336l1.556-.778-3.856-1.187Z"
            />
        </BaseIcon>
    )
})

export const IconApps: IconComponent<IconProps> = forwardRef(function IconApps(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M10.25 3.75H11a.75.75 0 0 0-.75-.75v.75Zm0 6.5V11a.75.75 0 0 0 .75-.75h-.75Zm-6.5 0H3c0 .414.336.75.75.75v-.75Zm.546-6.391.34.668-.34-.668Zm-.437.437.668.34-.668-.34Zm9.891-.546V3a.75.75 0 0 0-.75.75h.75Zm6.5 6.5V11a.75.75 0 0 0 .75-.75h-.75Zm-6.5 0H13c0 .414.336.75.75.75v-.75Zm5.954-6.391-.34.668.34-.668Zm.437.437-.668.34.668-.34ZM3.75 13.75V13a.75.75 0 0 0-.75.75h.75Zm6.5 0H11a.75.75 0 0 0-.75-.75v.75Zm0 6.5V21a.75.75 0 0 0 .75-.75h-.75Zm-5.954-.109.34-.668-.34.668Zm-.437-.437.668-.34-.668.34Zm16.74 1.955a.75.75 0 0 0 1.06-1.06l-1.06 1.06Zm-1.297-2.357-.531-.53.531.53ZM5.35 4.5h4.9V3h-4.9v1.5Zm4.15-.75v6.5H11v-6.5H9.5Zm.75 5.75h-6.5V11h6.5V9.5Zm-5.75.75v-4.9H3v4.9h1.5ZM5.35 3c-.268 0-.513 0-.718.016a1.774 1.774 0 0 0-.676.175l.68 1.336c-.016.009-.002-.006.118-.016.13-.01.304-.011.596-.011V3ZM4.5 5.35c0-.292 0-.467.011-.596.01-.12.025-.134.016-.117l-1.336-.681a1.77 1.77 0 0 0-.175.676C3 4.837 3 5.082 3 5.35h1.5Zm-.544-2.16a1.75 1.75 0 0 0-.765.766l1.336.68a.25.25 0 0 1 .11-.109l-.681-1.336ZM13.75 4.5h4.9V3h-4.9v1.5Zm5.75.85v4.9H21v-4.9h-1.5Zm.75 4.15h-6.5V11h6.5V9.5Zm-5.75.75v-6.5H13v6.5h1.5Zm4.15-5.75c.292 0 .467 0 .596.011.12.01.134.025.117.016l.681-1.336a1.774 1.774 0 0 0-.676-.175C19.164 3 18.918 3 18.65 3v1.5Zm2.35.85c0-.268 0-.513-.016-.718a1.775 1.775 0 0 0-.175-.676l-1.336.68c-.009-.016.006-.002.016.118.01.13.011.304.011.596H21Zm-1.637-.823a.25.25 0 0 1 .11.11l1.336-.681a1.75 1.75 0 0 0-.765-.765l-.68 1.336ZM3.75 14.5h6.5V13h-6.5v1.5Zm5.75-.75v6.5H11v-6.5H9.5Zm.75 5.75h-4.9V21h4.9v-1.5Zm-5.75-.85v-4.9H3v4.9h1.5Zm.85.85c-.292 0-.467 0-.596-.011-.12-.01-.134-.025-.117-.016l-.681 1.336c.23.118.463.157.676.175.205.017.45.016.718.016v-1.5ZM3 18.65c0 .268 0 .514.016.718.018.213.057.446.175.677l1.336-.682c.009.017-.006.003-.016-.117a8.337 8.337 0 0 1-.011-.596H3Zm1.636.823a.25.25 0 0 1-.109-.11l-1.336.681c.168.33.435.598.765.765l.68-1.336ZM17 19.508a2.5 2.5 0 0 1-2.5-2.5H13a4 4 0 0 0 4 4v-1.5Zm-2.5-2.5a2.5 2.5 0 0 1 2.5-2.5v-1.5a4 4 0 0 0-4 4h1.5Zm2.5-2.5a2.5 2.5 0 0 1 2.5 2.5H21a4 4 0 0 0-4-4v1.5Zm1.772 5.324 1.826 1.827 1.061-1.06-1.827-1.827-1.06 1.06Zm.728-2.824a2.49 2.49 0 0 1-.73 1.765l1.063 1.058A3.99 3.99 0 0 0 21 17.008h-1.5Zm-.73 1.765a2.49 2.49 0 0 1-1.77.735v1.5a3.99 3.99 0 0 0 2.833-1.177l-1.062-1.058Z" />
        </BaseIcon>
    )
})

export const IconArrowRightDown: IconComponent<IconProps> = forwardRef(
    function IconArrowRightDown(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M4 3.75A.75.75 0 0 1 4.75 3h9.5c.966 0 1.75.784 1.75 1.75v13.69l2.72-2.72a.75.75 0 1 1 1.06 1.06l-4 4a.75.75 0 0 1-1.06 0l-4-4a.75.75 0 1 1 1.06-1.06l2.72 2.72V4.75a.25.25 0 0 0-.25-.25h-9.5A.75.75 0 0 1 4 3.75Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconCornerDownRight: IconComponent<IconProps> = forwardRef(
    function IconCornerDownRight(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M3.75 4a.75.75 0 0 1 .75.75V13c0 .69.56 1.25 1.25 1.25h12.94l-2.72-2.72a.75.75 0 1 1 1.06-1.06l3.647 3.646a1.25 1.25 0 0 1 0 1.768L17.03 19.53a.75.75 0 1 1-1.06-1.06l2.72-2.72H5.75A2.75 2.75 0 0 1 3 13V4.75A.75.75 0 0 1 3.75 4Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconAsterisk: IconComponent<IconProps> = forwardRef(function IconAsterisk(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12.003 2a.75.75 0 0 1 .75.75v7.951l6.887-3.976a.75.75 0 0 1 .75 1.3L13.503 12l6.886 3.976a.75.75 0 0 1-.75 1.298L12.753 13.3v7.951a.75.75 0 0 1-1.5 0V13.3l-6.885 3.974a.75.75 0 0 1-.75-1.299L10.503 12 3.617 8.025a.75.75 0 1 1 .75-1.3l6.886 3.976V2.75a.75.75 0 0 1 .75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconArrowLeft: IconComponent<IconProps> = forwardRef(function IconArrowLeft(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="m5.229 11.332 4.97-4.97a.75.75 0 1 0-1.061-1.06l-5.543 5.543a1.75 1.75 0 0 0 0 2.474l5.543 5.543a.75.75 0 1 0 1.06-1.06l-4.97-4.97h14.94a.75.75 0 0 0 0-1.5H5.228Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconArrowCircleLeft: IconComponent<IconProps> = forwardRef(
    function IconArrowCircleLeft(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M3.5 12a8.5 8.5 0 1 0 17 0 8.5 8.5 0 0 0-17 0ZM12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10Zm-4.427-9.116a1.25 1.25 0 0 1 0-1.768L10.47 8.22a.75.75 0 1 1 1.06 1.06l-1.97 1.97h6.69a.75.75 0 0 1 0 1.5H9.56l1.97 1.97a.75.75 0 1 1-1.06 1.06l-2.897-2.896Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconArchive: IconComponent<IconProps> = forwardRef(function IconArchive(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M21 13.748V4.75A1.75 1.75 0 0 0 19.25 3H4.75A1.75 1.75 0 0 0 3 4.75v14.5c0 .966.784 1.75 1.75 1.75h14.5A1.75 1.75 0 0 0 21 19.25v-5.502ZM19.25 4.5H4.75a.25.25 0 0 0-.25.25V13h3.57c.36 0 .67.256.737.61a3.251 3.251 0 0 0 6.386 0 .75.75 0 0 1 .737-.61h3.57V4.75a.25.25 0 0 0-.25-.25Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconArrowRight: IconComponent<IconProps> = forwardRef(function IconArrowRight(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="m18.771 12.832-4.97 4.97a.75.75 0 1 0 1.062 1.06l5.542-5.542a1.75 1.75 0 0 0 0-2.475l-5.543-5.543a.75.75 0 0 0-1.06 1.06l4.97 4.97H3.832a.75.75 0 0 0 0 1.5h14.94Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconArrowCircleRight: IconComponent<IconProps> = forwardRef(
    function IconArrowCircleRight(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M20.5 12a8.5 8.5 0 1 0-17 0 8.5 8.5 0 0 0 17 0ZM12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2Zm4.427 9.116a1.25 1.25 0 0 1 0 1.768L13.53 15.78a.75.75 0 1 1-1.06-1.06l1.97-1.97H7.75a.75.75 0 0 1 0-1.5h6.69l-1.97-1.97a.75.75 0 0 1 1.06-1.06l2.897 2.896Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconArrowUpLeftDiagonal: IconComponent<IconProps> = forwardRef(
    function IconArrowUpLeftDiagonal(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M22.198 10.698a.75.75 0 0 1 0 1.06l-6.718 6.718a1.75 1.75 0 0 1-2.475 0l-9.68-9.68v3.846a.75.75 0 0 1-1.5 0V6.985a.75.75 0 0 1 .75-.75h5.657a.75.75 0 1 1 0 1.5H4.386l9.68 9.68a.25.25 0 0 0 .353 0l6.718-6.717a.75.75 0 0 1 1.06 0Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconArrowUpRight: IconComponent<IconProps> = forwardRef(
    function IconArrowUpRight(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M7.995 5.75a.75.75 0 0 1 .75-.75h8.505c.966 0 1.75.784 1.75 1.75v9.496a.75.75 0 0 1-1.5 0V7.56L7.03 18.03a.75.75 0 0 1-1.06-1.061L16.44 6.5H8.744a.75.75 0 0 1-.75-.75Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconBadge: IconComponent<IconProps> = forwardRef(function IconBadge(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.035 2.44a1.278 1.278 0 0 1 1.93 0l1.842 2.12 2.774-.44a1.278 1.278 0 0 1 1.477 1.24l.049 2.808 2.407 1.446c.666.4.824 1.296.335 1.9l-1.768 2.182.916 2.655a1.278 1.278 0 0 1-.965 1.671l-2.757.535-1.006 2.623a1.278 1.278 0 0 1-1.813.66L12 20.476 9.544 21.84a1.278 1.278 0 0 1-1.813-.66l-1.006-2.622-2.757-.535a1.278 1.278 0 0 1-.965-1.67l.915-2.656-1.767-2.182c-.49-.604-.331-1.5.335-1.9l2.407-1.446.049-2.808A1.278 1.278 0 0 1 6.42 4.12l2.773.44 1.842-2.12Zm1.073 17.977Zm-.215 0ZM12 3.618l-1.755 2.02c-.29.332-.73.493-1.165.424l-2.643-.42-.046 2.676a1.28 1.28 0 0 1-.62 1.074l-2.294 1.377 1.685 2.08c.277.342.358.803.215 1.22l-.872 2.53 2.627.51c.433.084.792.386.95.797l.958 2.499 2.34-1.298a1.278 1.278 0 0 1 1.24 0l2.34 1.298.958-2.499c.158-.412.517-.713.95-.797l2.627-.51-.872-2.53a1.278 1.278 0 0 1 .215-1.22l1.685-2.08-2.294-1.377a1.278 1.278 0 0 1-.62-1.074l-.046-2.676-2.643.42a1.278 1.278 0 0 1-1.165-.424L12 3.617Zm3.184 16.91ZM15.03 10.22a.75.75 0 0 1 0 1.06l-3.5 3.5a.75.75 0 0 1-1.06 0l-1.5-1.5a.75.75 0 1 1 1.06-1.06l.97.97 2.97-2.97a.75.75 0 0 1 1.06 0Z"
            />
        </BaseIcon>
    )
})

export const IconBalance: IconComponent<IconProps> = forwardRef(function IconBalance(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 2a.75.75 0 0 1 .75.75V4h1.014c.272 0 .54.063.783.185l1.577.789a.25.25 0 0 0 .112.026h5.014a.75.75 0 0 1 0 1.5h-1.727l2.448 7.752c.243.768-.066 1.684-.89 2.05-1.746.774-3.416.774-5.163 0-.823-.366-1.132-1.282-.89-2.05L17.478 6.5h-1.24a1.75 1.75 0 0 1-.783-.185l-1.578-.789a.25.25 0 0 0-.112-.026H12.75v14h4.5a.75.75 0 0 1 0 1.5H6.75a.75.75 0 0 1 0-1.5h4.5v-14h-1.014a.25.25 0 0 0-.112.026l-1.577.79a1.75 1.75 0 0 1-.783.184h-1.24l2.447 7.752c.243.768-.066 1.684-.89 2.05-1.746.774-3.416.774-5.163 0-.823-.366-1.132-1.282-.89-2.05L4.478 6.5H2.75a.75.75 0 1 1 0-1.5h5.014a.25.25 0 0 0 .112-.026l1.577-.79A1.75 1.75 0 0 1 10.236 4h1.014V2.75A.75.75 0 0 1 12 2ZM5.5 8.24l-2.041 6.464a.233.233 0 0 0 .003.156.12.12 0 0 0 .065.07c1.359.603 2.587.603 3.946 0a.12.12 0 0 0 .065-.07.233.233 0 0 0 .003-.156L5.5 8.24Zm13 0-2.041 6.464a.233.233 0 0 0 .003.156.121.121 0 0 0 .065.07c1.359.603 2.587.603 3.946 0a.121.121 0 0 0 .065-.07.233.233 0 0 0 .003-.156L18.5 8.24Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconBell: IconComponent<IconProps> = forwardRef(function IconBell(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M5.658 2.232a.75.75 0 0 1-.027 1.06 9.247 9.247 0 0 0-2.527 4.164.75.75 0 0 1-1.442-.411 10.747 10.747 0 0 1 2.936-4.84.75.75 0 0 1 1.06.027Zm12.684 0a.75.75 0 0 1 1.06-.028 10.746 10.746 0 0 1 2.936 4.84.75.75 0 1 1-1.442.412 9.246 9.246 0 0 0-2.527-4.164.75.75 0 0 1-.027-1.06ZM4.304 8.554a7.795 7.795 0 0 1 15.392 0l1.357 8.417A1.75 1.75 0 0 1 19.326 19h-2.834c-.849 1.75-2.512 3-4.492 3s-3.643-1.25-4.492-3H4.674a1.75 1.75 0 0 1-1.727-2.029l1.357-8.417ZM9.246 19c.678.937 1.68 1.5 2.754 1.5s2.076-.563 2.754-1.5H9.245ZM12 3.5a6.295 6.295 0 0 0-6.215 5.293L4.427 17.21a.25.25 0 0 0 .247.29h14.652a.25.25 0 0 0 .247-.29l-1.358-8.417A6.295 6.295 0 0 0 12 3.5Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconBinary: IconComponent<IconProps> = forwardRef(function IconBinary(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M18.959 17.317a6.89 6.89 0 0 1-.223 1.828c-.148.538-.366.992-.654 1.365-.283.368-.632.65-1.047.844-.41.195-.878.292-1.403.292-.457 0-.878-.08-1.263-.241a2.533 2.533 0 0 1-.996-.756c-.28-.347-.498-.789-.654-1.326-.157-.538-.235-1.18-.235-1.93 0-.681.074-1.29.222-1.828.152-.537.37-.99.654-1.358.283-.373.63-.656 1.04-.851a3.293 3.293 0 0 1 1.416-.292c.457 0 .879.08 1.264.241.385.161.715.413.99.755.28.343.497.783.654 1.32.156.538.235 1.184.235 1.937Zm-1.625.05c0-.52-.037-.968-.108-1.345-.072-.38-.178-.692-.318-.933a1.447 1.447 0 0 0-.508-.546 1.301 1.301 0 0 0-.679-.178c-.237 0-.455.06-.654.178a1.46 1.46 0 0 0-.507.54 3.148 3.148 0 0 0-.337.933 6.71 6.71 0 0 0-.114 1.326c0 .52.036.97.108 1.346.072.377.178.688.317.933.14.245.31.428.508.546.199.118.425.178.68.178.236 0 .454-.06.653-.178.199-.119.368-.298.508-.54.144-.245.254-.554.33-.926.08-.377.12-.821.12-1.333ZM5.965 21.5v-1.377h2.114V14.79l-1.841 1.016-.508-1.263 2.634-1.39h1.302v6.97h1.821V21.5H5.965ZM13.112 10.5V9.123h2.114V3.79l-1.84 1.015-.509-1.263 2.635-1.39h1.301v6.97h1.822V10.5h-5.523ZM11.811 6.317a6.89 6.89 0 0 1-.222 1.828c-.148.537-.366.992-.654 1.365-.284.368-.633.65-1.047.844-.41.195-.878.292-1.403.292-.457 0-.878-.08-1.263-.241a2.533 2.533 0 0 1-.997-.756c-.28-.347-.497-.789-.654-1.326-.156-.538-.235-1.18-.235-1.93 0-.681.074-1.29.223-1.828.152-.537.37-.99.653-1.358.284-.373.63-.656 1.041-.851a3.294 3.294 0 0 1 1.416-.292c.457 0 .878.08 1.263.241.385.161.715.413.99.756.28.342.498.782.654 1.32.157.537.235 1.183.235 1.936Zm-1.625.05c0-.52-.036-.968-.108-1.345-.072-.38-.178-.692-.317-.933a1.447 1.447 0 0 0-.508-.546 1.301 1.301 0 0 0-.68-.178c-.236 0-.454.06-.653.178a1.46 1.46 0 0 0-.508.54 3.148 3.148 0 0 0-.336.933 6.705 6.705 0 0 0-.115 1.326c0 .52.036.97.108 1.346.072.377.178.688.318.933.14.246.309.428.508.546.198.118.425.178.679.178.237 0 .455-.06.654-.178.198-.118.368-.298.507-.54.144-.245.254-.554.33-.926.08-.377.121-.821.121-1.333Z" />
        </BaseIcon>
    )
})

export const IconBolt: IconComponent<IconProps> = forwardRef(function IconBolt(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="m12.292 5.034-6.155 8.924h4.321c.69 0 1.25.56 1.25 1.25v3.758l6.155-8.924h-4.321c-.69 0-1.25-.56-1.25-1.25V5.034Zm-.78-1.513c.697-1.01 2.28-.517 2.28.71v4.31h4.548a1.25 1.25 0 0 1 1.029 1.96l-6.882 9.978c-.696 1.01-2.279.517-2.279-.71v-4.31H5.661a1.25 1.25 0 0 1-1.03-1.96l6.882-9.978Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconBook: IconComponent<IconProps> = forwardRef(function IconBook(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 4.75A2.75 2.75 0 0 1 6.75 2h10.5A2.75 2.75 0 0 1 20 4.75v14.5A2.75 2.75 0 0 1 17.25 22H6.75A2.75 2.75 0 0 1 4 19.25V4.75ZM7 20.5a1.5 1.5 0 0 1 0-3h10.25c.45 0 .875-.108 1.25-.3v2.05c0 .69-.56 1.25-1.25 1.25H7Zm11.5-5.75c0 .69-.56 1.25-1.25 1.25H7c-.546 0-1.059.146-1.5.401V4.75c0-.69.56-1.25 1.25-1.25h10.5c.69 0 1.25.56 1.25 1.25v10ZM8 7a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 0 1.5h-6.5A.75.75 0 0 1 8 7Zm0 4a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5A.75.75 0 0 1 8 11Z"
            />
        </BaseIcon>
    )
})

export const IconBottomPanel: IconComponent<IconProps> = forwardRef(function IconBottomPanel(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="m4.296 20.141.34-.668-.34.668Zm-.437-.437-.668.34.668-.34Zm16.282 0-.668-.34.668.34Zm-.437.437-.34-.668.34.668Zm.437-15.845-.668.34.668-.34Zm-.437-.437.34-.668-.34.668Zm-15.845.437-.668-.34.668.34Zm.437-.437-.34-.668.34.668ZM20.25 15H21v-1.5h-.75V15Zm-16.5-1.5H3V15h.75v-1.5ZM19.5 5.35v13.3H21V5.35h-1.5Zm-.85 14.15H5.35V21h13.3v-1.5ZM4.5 18.65V5.35H3v13.3h1.5ZM5.35 4.5h13.3V3H5.35v1.5Zm0 15c-.292 0-.467 0-.596-.011-.12-.01-.134-.025-.117-.016l-.681 1.336c.23.118.463.157.676.175.205.017.45.016.718.016v-1.5ZM3 18.65c0 .268 0 .514.016.718.018.213.057.446.175.677l1.336-.682c.009.017-.006.003-.016-.117a8.337 8.337 0 0 1-.011-.596H3Zm1.636.823a.25.25 0 0 1-.109-.11l-1.336.681c.168.33.435.598.765.765l.68-1.336ZM19.5 18.65c0 .292 0 .467-.011.596-.01.12-.025.134-.016.117l1.336.681c.118-.23.157-.463.175-.676.017-.204.016-.45.016-.718h-1.5ZM18.65 21c.268 0 .514 0 .718-.016.213-.018.446-.057.677-.175l-.682-1.336c.017-.009.003.006-.117.016-.13.01-.304.011-.596.011V21Zm.823-1.637a.25.25 0 0 1-.11.11l.681 1.336a1.75 1.75 0 0 0 .765-.765l-1.336-.68ZM21 5.35c0-.268 0-.513-.016-.718a1.775 1.775 0 0 0-.175-.676l-1.336.68c-.009-.016.006-.002.016.118.01.13.011.304.011.596H21Zm-2.35-.85c.292 0 .467 0 .596.011.12.01.134.025.117.016l.681-1.336a1.774 1.774 0 0 0-.676-.175C19.164 3 18.918 3 18.65 3v1.5Zm2.16-.544a1.75 1.75 0 0 0-.765-.765l-.682 1.336a.25.25 0 0 1 .11.11l1.336-.681ZM4.5 5.35c0-.292 0-.467.011-.596.01-.12.025-.134.016-.117l-1.336-.681c-.118.23-.157.463-.175.676C3 4.837 3 5.082 3 5.35h1.5ZM5.35 3c-.268 0-.513 0-.718.016a1.774 1.774 0 0 0-.676.175l.68 1.336c-.016.009-.002-.006.118-.016.13-.01.304-.011.596-.011V3Zm-.823 1.636a.25.25 0 0 1 .11-.109l-.681-1.336a1.75 1.75 0 0 0-.765.765l1.336.68ZM3.75 15h16.5v-1.5H3.75V15Z" />
        </BaseIcon>
    )
})

export const IconBox: IconComponent<IconProps> = forwardRef(function IconBox(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M21.75 17.34V6.75a.762.762 0 0 0-.052-.263v-.06a.912.912 0 0 0-.143-.21.551.551 0 0 0-.15-.112l-9-5.25a.75.75 0 0 0-.75 0l-9 5.25a.552.552 0 0 0-.15.112.912.912 0 0 0-.143.21v.06a.75.75 0 0 0-.112.263v10.59c-.004.05-.004.1 0 .15.02.044.042.086.067.128.021.042.046.082.075.12l.113.097a.513.513 0 0 0 .075.06l9 5.25h.12c.163.07.347.07.51 0h.12l9-5.25a.51.51 0 0 0 .075-.06l.112-.098a.69.69 0 0 0 .076-.12c.025-.04.047-.083.067-.127.034-.048.064-.098.09-.15Zm-18-9.285 7.5 4.373v8.767l-7.5-4.373V8.055Zm9 4.373 7.5-4.373v8.767l-7.5 4.373v-8.768ZM12 2.37l7.5 4.38-7.5 4.38-7.5-4.38L12 2.37Z" />
        </BaseIcon>
    )
})

export const IconBrackets: IconComponent<IconProps> = forwardRef(function IconBrackets(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 4.75C4 3.784 4.784 3 5.75 3h2.5a.75.75 0 0 1 0 1.5h-2.5a.25.25 0 0 0-.25.25V11c0 .372-.116.716-.314 1 .198.284.314.628.314 1v6.25c0 .138.112.25.25.25h2.5a.75.75 0 0 1 0 1.5h-2.5A1.75 1.75 0 0 1 4 19.25V13a.25.25 0 0 0-.25-.25h-1a.75.75 0 0 1 0-1.5h1A.25.25 0 0 0 4 11V4.75Zm11-1a.75.75 0 0 1 .75-.75h2.5c.966 0 1.75.784 1.75 1.75V11c0 .138.112.25.25.25h1a.75.75 0 0 1 0 1.5h-1A.25.25 0 0 0 20 13v6.25A1.75 1.75 0 0 1 18.25 21h-2.5a.75.75 0 0 1 0-1.5h2.5a.25.25 0 0 0 .25-.25V13c0-.372.116-.716.314-1a1.742 1.742 0 0 1-.314-1V4.75a.25.25 0 0 0-.25-.25h-2.5a.75.75 0 0 1-.75-.75Z"
            />
        </BaseIcon>
    )
})

export const IconBrightness: IconComponent<IconProps> = forwardRef(function IconBrightness(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <g clipPath="url(#icon/brightness__a)">
                <path d="M6.534 12a5.465 5.465 0 1 0 10.93 0 5.465 5.465 0 0 0-10.93 0Zm5.518-4.034a4.085 4.085 0 0 1 3.538 6.13 4.086 4.086 0 0 1-3.538 2.042V7.966Zm.348-2.554V2.96a.46.46 0 1 0-.918 0v2.452a.46.46 0 0 0 .918 0Zm6.274.484a.46.46 0 0 0-.65-.65l-1.727 1.733a.46.46 0 0 0 .65.65l1.727-1.733Zm-.086 6.504h2.451a.46.46 0 1 0 0-.918h-2.451a.46.46 0 0 0 0 .918Zm-2.218 3.897a.458.458 0 0 0 0 .65l1.734 1.735a.462.462 0 0 0 .785-.325.457.457 0 0 0-.136-.325l-1.733-1.735a.46.46 0 0 0-.65 0Zm-4.77 2.291v2.452a.46.46 0 1 0 .918 0v-2.452a.46.46 0 0 0-.918 0Zm-6.282-.484a.461.461 0 0 0 .325.784.455.455 0 0 0 .325-.134l1.734-1.735v.001a.46.46 0 0 0-.65-.65l-1.734 1.734ZM2.5 12.052a.46.46 0 0 0 .46.458h2.45a.46.46 0 0 0 0-.918H2.96a.462.462 0 0 0-.46.46Zm5.13-4.35a.458.458 0 0 0 0-.65L5.894 5.318a.46.46 0 0 0-.65.65L6.98 7.702c.18.178.47.178.65 0Z" />
            </g>
            <defs>
                <clipPath id="icon/brightness__a">
                    <path transform="translate(2 2)" d="M0 0h20v20H0z" />
                </clipPath>
            </defs>
        </BaseIcon>
    )
})

export const IconBrowser: IconComponent<IconProps> = forwardRef(function IconBrowser(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M2 5.75C2 4.784 2.784 4 3.75 4h16.5c.966 0 1.75.784 1.75 1.75v12.5A1.75 1.75 0 0 1 20.25 20H3.75A1.75 1.75 0 0 1 2 18.25V5.75Zm1.75-.25a.25.25 0 0 0-.25.25V10h17V5.75a.25.25 0 0 0-.25-.25H3.75Zm16.75 6h-17v6.75c0 .138.112.25.25.25h16.5a.25.25 0 0 0 .25-.25V11.5Z"
                clipRule="evenodd"
            />
            <path d="M6.25 7.75a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM8.75 7.75a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM11.25 7.75a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z" />
            <path
                fillRule="evenodd"
                d="M6.5 7.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm2.5 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm2.5 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm-5.25 0a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm2.5 0a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm2 .5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconBug: IconComponent<IconProps> = forwardRef(function IconBug(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 3.5A3.5 3.5 0 0 0 8.5 7h7A3.5 3.5 0 0 0 12 3.5ZM17 7h.25c.966 0 1.75.784 1.75 1.75v.138l1.758-.598a.75.75 0 0 1 .484 1.42L19 10.473V13h2.25a.75.75 0 0 1 0 1.5H19v.5a7.01 7.01 0 0 1-.21 1.71.751.751 0 0 1 .082.024l2.37.806a.75.75 0 0 1-.484 1.42l-2.37-.806a.76.76 0 0 1-.113-.05A7 7 0 0 1 12 22a7 7 0 0 1-6.275-3.895.745.745 0 0 1-.113.049l-2.37.806a.75.75 0 1 1-.484-1.42l2.37-.806a.766.766 0 0 1 .083-.023A7.015 7.015 0 0 1 5 15v-.5H2.75a.75.75 0 0 1 0-1.5H5v-2.527L2.758 9.71a.75.75 0 1 1 .484-1.42L5 8.888V8.75C5 7.784 5.784 7 6.75 7H7a5 5 0 0 1 10 0ZM6.75 8.5a.25.25 0 0 0-.25.25V15a5.501 5.501 0 0 0 4.75 5.45v-6.7a.75.75 0 0 1 1.5 0v6.7A5.501 5.501 0 0 0 17.5 15V8.75a.25.25 0 0 0-.25-.25H6.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconBuilding: IconComponent<IconProps> = forwardRef(function IconBuilding(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M5.75 4.5a.25.25 0 0 0-.25.25V19.5h13V4.75a.25.25 0 0 0-.25-.25H5.75ZM20 19.5V4.75A1.75 1.75 0 0 0 18.25 3H5.75A1.75 1.75 0 0 0 4 4.75V19.5H2.75a.75.75 0 0 0 0 1.5h18.5a.75.75 0 0 0 0-1.5H20ZM8 7.75A.75.75 0 0 1 8.75 7h1.5a.75.75 0 0 1 0 1.5h-1.5A.75.75 0 0 1 8 7.75Zm5 0a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75Zm-5 4a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75Zm5 0a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75Zm-5 4a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75Zm5 0a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCake: IconComponent<IconProps> = forwardRef(function IconCake(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M11.998 1.068a.75.75 0 0 1 .53.22l1.592 1.59a3 3 0 0 1-1.37 5.027V9h6c.966 0 1.75.783 1.75 1.75v3.406c0 .469-.187.91-.508 1.233A.76.76 0 0 1 20 15.5v4.75A1.75 1.75 0 0 1 18.25 22H5.75A1.75 1.75 0 0 1 4 20.25V15.5c0-.038.003-.075.008-.11a1.75 1.75 0 0 1-.508-1.234V10.75C3.5 9.783 4.284 9 5.25 9h6V7.905a3 3 0 0 1-1.373-5.027l1.591-1.59a.75.75 0 0 1 .53-.22ZM5.5 16.286v3.964c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25v-3.964l-.638.348a1.75 1.75 0 0 1-1.739-.035l-1.494-.897a.25.25 0 0 0-.258 0l-1.47.882a1.75 1.75 0 0 1-1.801 0l-1.471-.882a.25.25 0 0 0-.258 0l-1.494.897a1.75 1.75 0 0 1-1.739.035l-.638-.348ZM11.998 2.88l-1.06 1.06a1.5 1.5 0 1 0 2.121 0l-1.06-1.06ZM5.25 10.5a.25.25 0 0 0-.25.25v3.406c0 .092.05.176.13.22l1.727.942a.25.25 0 0 0 .248-.006l1.495-.896a1.75 1.75 0 0 1 1.8 0l1.471.882a.25.25 0 0 0 .258 0l1.47-.882a1.75 1.75 0 0 1 1.801 0l1.495.896a.25.25 0 0 0 .248.006l1.727-.942a.25.25 0 0 0 .13-.22V10.75a.25.25 0 0 0-.25-.25H5.25Z" />
        </BaseIcon>
    )
})

export const IconCalculator: IconComponent<IconProps> = forwardRef(function IconCalculator(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M4 3.75C4 2.784 4.784 2 5.75 2h12.5c.966 0 1.75.784 1.75 1.75v16.5A1.75 1.75 0 0 1 18.25 22H5.75A1.75 1.75 0 0 1 4 20.25V3.75Zm1.75-.25a.25.25 0 0 0-.25.25V7h13V3.75a.25.25 0 0 0-.25-.25H5.75Zm12.75 5h-13v11.75c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25V8.5Z"
                clipRule="evenodd"
            />
            <path d="M10 12.25a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM10 16.75a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM15 12.25a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM15 16.75a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z" />
            <path
                fillRule="evenodd"
                d="M10.5 12.25a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm0 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm5-4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm0 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-6-4a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Zm0 4.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Zm5.5-5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm-.5 5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCalendar: IconComponent<IconProps> = forwardRef(function IconCalendar(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3 5.75A2.75 2.75 0 0 1 5.75 3h12.5A2.75 2.75 0 0 1 21 5.75v12.5A2.75 2.75 0 0 1 18.25 21H5.75A2.75 2.75 0 0 1 3 18.25V5.75ZM4.5 8.5v9.75c0 .69.56 1.25 1.25 1.25h12.5c.69 0 1.25-.56 1.25-1.25V8.5h-15Zm15-1.5h-15V5.75c0-.69.56-1.25 1.25-1.25h12.5c.69 0 1.25.56 1.25 1.25V7Z"
            />
            <path d="M7 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm0 4a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm4 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm0-4a1 1 0 1 1 2 0 1 1 0 0 1-2 0Zm4 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z" />
        </BaseIcon>
    )
})

export const IconCart: IconComponent<IconProps> = forwardRef(function IconCart(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M1 2.75A.75.75 0 0 1 1.75 2h.93a1.75 1.75 0 0 1 1.716 1.407L4.715 5h15.553a1.75 1.75 0 0 1 1.712 2.11l-1.579 7.5A1.75 1.75 0 0 1 18.69 16H6.819a1.75 1.75 0 0 1-1.715-1.407L2.925 3.701A.25.25 0 0 0 2.68 3.5h-.93A.75.75 0 0 1 1 2.75ZM5.015 6.5l1.56 7.799a.25.25 0 0 0 .245.201h11.869a.25.25 0 0 0 .244-.198l1.58-7.5a.25.25 0 0 0-.245-.302H5.015ZM8 18.5a.5.5 0 1 0 0 1 .5.5 0 0 0 0-1ZM6 19a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm11-.5a.5.5 0 1 0 0 1 .5.5 0 0 0 0-1Zm-2 .5a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCdCase: IconComponent<IconProps> = forwardRef(function IconCdCase(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.00281 21.6806C3.00094 21.7031 3 21.7265 3 21.75C3 22.1643 3.33562 22.5 3.75 22.5H18.75C19.3472 22.5 19.9191 22.2628 20.3409 21.8409C20.7628 21.419 21 20.8471 21 20.25V6.74995C21 6.15276 20.7628 5.58089 20.3409 5.15902C19.9191 4.73715 19.3472 4.49995 18.75 4.49995H9.75V2.32215C9.75 2.03902 9.59062 1.77934 9.33655 1.65183C9.08436 1.52433 8.78062 1.55058 8.55281 1.72026L3.30281 5.61835C3.1125 5.75991 3 5.98303 3 6.22022V21.6121C3 21.6356 3.00094 21.6581 3.00281 21.6806ZM9.74993 5.99994V7.86839C10.4455 7.58713 11.204 7.43152 11.9999 7.43152C15.3112 7.43152 17.9999 10.1202 17.9999 13.4315C17.9999 16.7428 15.3112 19.4315 11.9999 19.4315C10.2955 19.4315 8.75513 18.719 7.66313 17.5762L5.13473 21H18.7499C18.9487 21 19.1399 20.9213 19.2805 20.7807C19.4212 20.64 19.4999 20.4488 19.4999 20.25V6.75005C19.4999 6.5513 19.4212 6.36005 19.2805 6.21943C19.1399 6.0788 18.9487 6.00006 18.7499 6.00006L9.74993 5.99994ZM4.49993 19.3341V6.59826L8.24993 3.81306V14.2569L4.49993 19.3341ZM8.57153 16.3454C9.39747 17.3157 10.6275 17.9316 11.9999 17.9316C14.4835 17.9316 16.4999 15.9151 16.4999 13.4316C16.4999 10.9482 14.4834 8.93164 11.9999 8.93164C11.1805 8.93164 10.4118 9.15101 9.74993 9.53445V14.5041C9.74993 14.6644 9.69837 14.821 9.60274 14.9494L8.57153 16.3454ZM11.9999 11.4883C10.9274 11.4883 10.0565 12.3592 10.0565 13.4317C10.0565 14.5042 10.9274 15.3752 11.9999 15.3752C13.0724 15.3752 13.9434 14.5042 13.9434 13.4317C13.9434 12.3592 13.0724 11.4883 11.9999 11.4883ZM11.9999 12.9883C12.2446 12.9883 12.4434 13.187 12.4434 13.4317C12.4434 13.6764 12.2446 13.8752 11.9999 13.8752C11.7552 13.8752 11.5565 13.6764 11.5565 13.4317C11.5565 13.187 11.7552 12.9883 11.9999 12.9883Z"
            />
        </BaseIcon>
    )
})

export const IconChat: IconComponent<IconProps> = forwardRef(function IconChat(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.252 4.75c0-.966.784-1.75 1.75-1.75h12.25c.967 0 1.75.784 1.75 1.75v8.5a1.75 1.75 0 0 1-1.75 1.75h-2.25v2.25a1.75 1.75 0 0 1-1.75 1.75h-5.555l-4.33 2.406a.75.75 0 0 1-1.115-.656V19h-1.5a1.75 1.75 0 0 1-1.75-1.75v-8.5c0-.966.784-1.75 1.75-1.75h2.5V4.75Zm-2.5 3.75a.25.25 0 0 0-.25.25v8.5c0 .138.112.25.25.25h2.25a.75.75 0 0 1 .75.75v1.225l3.386-1.88a.75.75 0 0 1 .364-.095h5.75a.25.25 0 0 0 .25-.25v-2.942a.735.735 0 0 1 0-.116V8.75a.25.25 0 0 0-.25-.25h-12.5Zm14.25 5V8.75A1.75 1.75 0 0 0 16.252 7h-8.5V4.75a.25.25 0 0 1 .25-.25h12.25a.25.25 0 0 1 .25.25v8.5a.25.25 0 0 1-.25.25h-2.25Z"
            />
        </BaseIcon>
    )
})

export const IconChatHelp: IconComponent<IconProps> = forwardRef(function IconChatHelp(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M3 4.75C3 3.784 3.784 3 4.75 3h14.5c.966 0 1.75.784 1.75 1.75v12.5A1.75 1.75 0 0 1 19.25 19h-3.874a.25.25 0 0 0-.16.058l-2.098 1.738a1.75 1.75 0 0 1-2.24-.007l-2.066-1.73a.25.25 0 0 0-.16-.059H4.75A1.75 1.75 0 0 1 3 17.25V4.75Zm1.75-.25a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h3.902c.41 0 .809.145 1.124.409l2.065 1.73a.25.25 0 0 0 .32.002l2.098-1.739a1.75 1.75 0 0 1 1.117-.402h3.874a.25.25 0 0 0 .25-.25V4.75a.25.25 0 0 0-.25-.25H4.75Zm5.124 4.928c0-1.244 1-2.265 2.25-2.265s2.25 1.02 2.25 2.265c0 .997-.65 1.52-1.07 1.806a1.424 1.424 0 0 0-.354.32 1.01 1.01 0 0 0-.186.47.75.75 0 1 1-1.484-.222c.145-.96.704-1.485 1.18-1.809l.422.62-.422-.62c.164-.111.265-.199.328-.284a.436.436 0 0 0 .086-.281.757.757 0 0 0-.75-.765.757.757 0 0 0-.75.765.75.75 0 0 1-1.5 0Z"
                clipRule="evenodd"
            />
            <path d="M12 14.75a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Z" />
            <path
                fillRule="evenodd"
                d="M12 15.25a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm-.5-1a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCheck: IconComponent<IconProps> = forwardRef(function IconCheck(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <g clipPath="url(#icon/check__a)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M21.225 3.67a.75.75 0 0 1 .105 1.055L8.87 19.955a.75.75 0 0 1-1.11.056l-5.54-5.538a.75.75 0 0 1 1.061-1.061l4.953 4.952L20.169 3.775a.75.75 0 0 1 1.056-.105Z"
                />
            </g>
            <defs>
                <clipPath id="icon/check__a">
                    <path transform="translate(2 3)" d="M0 0h20v18H0z" />
                </clipPath>
            </defs>
        </BaseIcon>
    )
})

export const IconCheckbox: IconComponent<IconProps> = forwardRef(function IconCheckbox(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="m4.296 20.141.34-.668-.34.668Zm-.437-.437-.668.34.668-.34Zm16.282 0-.668-.34.668.34Zm-.437.437-.34-.668.34.668Zm.437-15.845-.668.34.668-.34Zm-.437-.437.34-.668-.34.668Zm-15.845.437-.668-.34.668.34Zm.437-.437-.34-.668.34.668Zm6.7 10.555-.53.53a.75.75 0 0 0 1.061 0l-.53-.53Zm5.031-3.97a.75.75 0 1 0-1.06-1.06l1.06 1.06Zm-6.5 1.44a.75.75 0 0 0-1.06 1.06l1.06-1.06ZM19.5 5.35v13.3H21V5.35h-1.5Zm-.85 14.15H5.35V21h13.3v-1.5ZM4.5 18.65V5.35H3v13.3h1.5ZM5.35 4.5h13.3V3H5.35v1.5Zm0 15c-.292 0-.467 0-.596-.011-.12-.01-.134-.025-.117-.016l-.681 1.336c.23.118.463.157.676.175.205.017.45.016.718.016v-1.5ZM3 18.65c0 .268 0 .514.016.718.018.213.057.446.175.677l1.336-.682c.009.017-.006.003-.016-.117a8.337 8.337 0 0 1-.011-.596H3Zm1.636.823a.25.25 0 0 1-.109-.11l-1.336.681c.168.33.435.598.765.765l.68-1.336ZM19.5 18.65c0 .292 0 .467-.011.596-.01.12-.025.134-.016.117l1.336.681c.118-.23.157-.463.175-.676.017-.204.016-.45.016-.718h-1.5ZM18.65 21c.268 0 .514 0 .718-.016.213-.018.446-.057.677-.175l-.682-1.336c.017-.009.003.006-.117.016-.13.01-.304.011-.596.011V21Zm.823-1.637a.251.251 0 0 1-.11.11l.681 1.336a1.75 1.75 0 0 0 .765-.765l-1.336-.68ZM21 5.35c0-.268 0-.513-.016-.718a1.775 1.775 0 0 0-.175-.676l-1.336.68c-.009-.016.006-.002.016.118.01.13.011.304.011.596H21Zm-2.35-.85c.292 0 .467 0 .596.011.12.01.134.025.117.016l.681-1.336a1.774 1.774 0 0 0-.676-.175C19.164 3 18.918 3 18.65 3v1.5Zm2.16-.544a1.75 1.75 0 0 0-.765-.765l-.682 1.336a.25.25 0 0 1 .11.11l1.336-.681ZM4.5 5.35c0-.292 0-.467.011-.596.01-.12.025-.134.016-.117l-1.336-.681c-.118.23-.157.463-.175.676C3 4.837 3 5.082 3 5.35h1.5ZM5.35 3c-.268 0-.513 0-.718.016a1.774 1.774 0 0 0-.676.175l.68 1.336c-.016.009-.002-.006.118-.016.13-.01.304-.011.596-.011V3Zm-.823 1.636a.25.25 0 0 1 .11-.109l-.681-1.336a1.75 1.75 0 0 0-.765.765l1.336.68Zm7 10.308 4.5-4.5-1.06-1.06-4.5 4.5 1.06 1.06Zm-3.06-2 2 2 1.06-1.06-2-2-1.06 1.06Z" />
        </BaseIcon>
    )
})

export const IconCheckCircle: IconComponent<IconProps> = forwardRef(function IconCheckCircle(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm13.475-3.08a.75.75 0 0 1 .106 1.055l-4.5 5.5a.75.75 0 0 1-1.111.055l-2-2a.75.75 0 1 1 1.06-1.06l1.414 1.414 3.975-4.859a.75.75 0 0 1 1.056-.105Z"
            />
        </BaseIcon>
    )
})

export const IconChevronDown: IconComponent<IconProps> = forwardRef(function IconChevronDown(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.47 9.47a.75.75 0 0 1 1.06 0L12 12.94l3.47-3.47a.75.75 0 1 1 1.06 1.06l-3.646 3.647a1.25 1.25 0 0 1-1.768 0L7.47 10.53a.75.75 0 0 1 0-1.06Z"
            />
        </BaseIcon>
    )
})

export const IconChevronLeft: IconComponent<IconProps> = forwardRef(function IconChevronLeft(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M16.116 3.22a.75.75 0 0 0-1.06 0l-7.293 7.292a1.75 1.75 0 0 0 0 2.475l7.293 7.293a.75.75 0 1 0 1.06-1.06l-7.293-7.293a.25.25 0 0 1 0-.354l7.293-7.293a.75.75 0 0 0 0-1.06Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconChevronRight: IconComponent<IconProps> = forwardRef(
    function IconChevronRight(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M8.47 3.47a.75.75 0 0 1 1.06 0l7.293 7.292a1.75 1.75 0 0 1 0 2.475L9.53 20.53a.75.75 0 0 1-1.06-1.06l7.293-7.293a.25.25 0 0 0 0-.354L8.47 4.53a.75.75 0 0 1 0-1.06Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconCircleDashed: IconComponent<IconProps> = forwardRef(
    function IconCircleDashed(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    d="M22 12C22 17.5228 17.5228 22 12 22C6.47716 22 2 17.5228 2 12C2 6.47716 6.47716 2 12 2C17.5228 2 22 6.47716 22 12Z"
                    fill="transparent"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="3.5 4.33"
                    transform="rotate(39 12 12)"
                />
            </BaseIcon>
        )
    }
)

export const IconClock: IconComponent<IconProps> = forwardRef(function IconClock(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm10-5a.75.75 0 0 1 .75.75v3.94l2.53 2.53a.75.75 0 1 1-1.06 1.06l-2.75-2.75a.75.75 0 0 1-.22-.53V7.75A.75.75 0 0 1 12 7Z"
            />
        </BaseIcon>
    )
})

export const IconCloud: IconComponent<IconProps> = forwardRef(function IconCloud(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                d="M17.9948 11.002C17.9983 10.9184 18 10.8344 18 10.75C18 7.43629 15.3137 4.75 12 4.75C9.3716 4.75 7.13793 6.44009 6.32647 8.7928C3.74507 9.12339 1.75 11.3287 1.75 14C1.75 16.8995 4.10051 19.25 7 19.25H18.125C20.4032 19.25 22.25 17.4032 22.25 15.125C22.25 12.8468 20.4032 11 18.125 11C18.0814 11 18.038 11.0007 17.9948 11.002Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="transparent"
            />
        </BaseIcon>
    )
})

export const IconCode: IconComponent<IconProps> = forwardRef(function IconCode(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M3 4.75C3 3.784 3.784 3 4.75 3h14.5c.966 0 1.75.784 1.75 1.75v14.5A1.75 1.75 0 0 1 19.25 21H4.75A1.75 1.75 0 0 1 3 19.25V4.75Zm1.75-.25a.25.25 0 0 0-.25.25v14.5c0 .138.112.25.25.25h14.5a.25.25 0 0 0 .25-.25V4.75a.25.25 0 0 0-.25-.25H4.75Zm6.03 3.97a.75.75 0 0 1 0 1.06l-2.293 2.293a.25.25 0 0 0 0 .354l2.293 2.293a.75.75 0 1 1-1.06 1.06l-2.293-2.293a1.75 1.75 0 0 1 0-2.474L9.72 8.47a.75.75 0 0 1 1.06 0Zm2.44 0a.75.75 0 0 1 1.06 0l2.293 2.293a1.75 1.75 0 0 1 0 2.474L14.28 15.53a.75.75 0 1 1-1.06-1.06l2.293-2.293a.25.25 0 0 0 0-.354L13.22 9.53a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCode2: IconComponent<IconProps> = forwardRef(function IconCode2(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M14.447 3.026a.75.75 0 0 1 .527.921l-4.5 16.5a.75.75 0 0 1-1.448-.394l4.5-16.5a.75.75 0 0 1 .921-.527ZM6.264 7.204a.75.75 0 0 1 .032 1.06l-3.355 3.565a.25.25 0 0 0 0 .342l3.355 3.565a.75.75 0 0 1-1.092 1.028l-3.355-3.565a1.75 1.75 0 0 1 0-2.398l3.355-3.565a.75.75 0 0 1 1.06-.032Zm11.472 0a.75.75 0 0 1 1.06.032l3.355 3.565a1.75 1.75 0 0 1 0 2.398l-3.355 3.565a.75.75 0 1 1-1.092-1.028l3.355-3.565a.25.25 0 0 0 0-.342l-3.355-3.565a.75.75 0 0 1 .032-1.06Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCodeInsert: IconComponent<IconProps> = forwardRef(function IconCodeInsert(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M16.245 1.75a.75.75 0 0 1 .75.75V4h3.25c.967 0 1.75.784 1.75 1.75v12.5a1.75 1.75 0 0 1-1.75 1.75h-3.25v1.5a.75.75 0 0 1-1.5 0v-19a.75.75 0 0 1 .75-.75Zm.75 16.75h3.25a.25.25 0 0 0 .25-.25V5.75a.25.25 0 0 0-.25-.25h-3.25v13ZM3.75 5.5a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h9.5a.75.75 0 0 1 0 1.5h-9.5A1.75 1.75 0 0 1 2 18.25V5.75C2 4.784 2.784 4 3.75 4h9.5a.75.75 0 0 1 0 1.5h-9.5Zm4.22 3.47a.75.75 0 0 1 1.06 0l2.5 2.5a.75.75 0 0 1 0 1.06l-2.5 2.5a.75.75 0 0 1-1.06-1.06L9.94 12l-1.97-1.97a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconConfetti: IconComponent<IconProps> = forwardRef(function IconConfetti(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M14.923 1.01a.75.75 0 0 1 .866.614l-.74.126.74-.126v.002l.001.004.002.013.007.044a8.204 8.204 0 0 1 .068.676 6.717 6.717 0 0 1-.08 1.578c-.122.66-.432 1.265-.685 1.68a7.06 7.06 0 0 1-.49.7l-.01.012-.004.004-.001.002s-.001.001-.576-.48l.575.48a.75.75 0 0 1-1.15-.961v-.001l.004-.004.02-.025a5.556 5.556 0 0 0 .352-.51c.211-.346.416-.77.49-1.17a5.24 5.24 0 0 0 .058-1.217 6.745 6.745 0 0 0-.054-.542l-.005-.028v-.004a.75.75 0 0 1 .612-.866Zm5.267 1.575c.39.14.592.57.451.96l-.463 1.284a.75.75 0 1 1-1.41-.509l.462-1.284a.75.75 0 0 1 .96-.45ZM7.635 7.075a1.75 1.75 0 0 1 2.881-.64l8.064 8.052a1.75 1.75 0 0 1-.64 2.883L5.27 21.973c-1.397.508-2.75-.847-2.242-2.243L7.635 7.075Zm1.821.422a.25.25 0 0 0-.411.091L4.438 20.243a.25.25 0 0 0 .32.32l12.67-4.603a.25.25 0 0 0 .092-.412L9.456 7.498Zm13.465-.433a.75.75 0 0 1-.336 1.006l-1.029.513a.75.75 0 1 1-.67-1.342l1.029-.513a.75.75 0 0 1 1.006.336Zm-4.769-.195a.75.75 0 0 1 0 1.06l-1.03 1.028a.75.75 0 0 1-1.06-1.062l1.03-1.027a.75.75 0 0 1 1.06 0Zm.263 4.093a.75.75 0 0 1 .78-.718l-.03.75.03-.75h.016l.033.003.114.008a7.05 7.05 0 0 1 1.577.301c.41.131.844.344 1.155.51a11.812 11.812 0 0 1 .51.29l.032.019.009.005.004.003-.395.637.395-.637a.75.75 0 0 1-.79 1.275h-.001l-.006-.004-.024-.015a5.925 5.925 0 0 0-1.345-.654 5.543 5.543 0 0 0-.93-.201 6.26 6.26 0 0 0-.395-.04l-.02-.001h-.002a.75.75 0 0 1-.717-.781Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCoffee: IconComponent<IconProps> = forwardRef(function IconCoffee(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M7 2a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5A.75.75 0 0 1 7 2Zm4 0a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5A.75.75 0 0 1 11 2Zm4 0a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5A.75.75 0 0 1 15 2ZM4 8.75C4 7.784 4.784 7 5.75 7h10.5c.966 0 1.75.784 1.75 1.75V9h.5a3.5 3.5 0 1 1 0 7H18v4.25A1.75 1.75 0 0 1 16.25 22H5.75A1.75 1.75 0 0 1 4 20.25V8.75Zm12.5 0a.25.25 0 0 0-.25-.25H5.75a.25.25 0 0 0-.25.25v11.5c0 .138.112.25.25.25h10.5a.25.25 0 0 0 .25-.25V8.75ZM18 14.5h.5a2 2 0 1 0 0-4H18v4Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCorrelationAnalysis: IconComponent<IconProps> = forwardRef(
    function IconCorrelationAnalysis(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path d="M7.5 12a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM12 6a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM22.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM17.25 13.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
                <path d="M22.5 2.557 21.442 1.5 3 19.942V1.5H1.5V21c0 .825.675 1.5 1.5 1.5h19.5V21H4.057L22.5 2.557Z" />
            </BaseIcon>
        )
    }
)

export const IconCollapse: IconComponent<IconProps> = forwardRef(function IconCollapse(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.9393 15C11.5251 14.4142 12.4749 14.4142 13.0607 15L16.5303 18.4697C16.8232 18.7626 16.8232 19.2374 16.5303 19.5303C16.2374 19.8232 15.7626 19.8232 15.4697 19.5303L12 16.0607L8.53033 19.5303C8.23744 19.8232 7.76256 19.8232 7.46967 19.5303C7.17678 19.2374 7.17678 18.7626 7.46967 18.4697L10.9393 15ZM7.46967 4.46967C7.76256 4.17678 8.23744 4.17678 8.53033 4.46967L12 7.93934L15.4697 4.46967C15.7626 4.17678 16.2374 4.17678 16.5303 4.46967C16.8232 4.76256 16.8232 5.23744 16.5303 5.53033L13.0607 9C12.4749 9.58579 11.5251 9.58578 10.9393 9L7.46967 5.53033C7.17678 5.23744 7.17678 4.76256 7.46967 4.46967Z"
            />
        </BaseIcon>
    )
})

export const IconCollapse45: IconComponent<IconProps> = forwardRef(function IconCollapse45(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M13.75 3a.75.75 0 0 1 .75.75v4.69l5.22-5.22a.75.75 0 1 1 1.06 1.06L15.56 9.5h4.69a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75v-6.5a.75.75 0 0 1 .75-.75Zm-10 11.5a.75.75 0 0 1 0-1.5h6.5a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0v-4.69l-5.22 5.22a.75.75 0 0 1-1.06-1.06l5.22-5.22H3.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconColumns: IconComponent<IconProps> = forwardRef(function IconColumns(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.75 5.5a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h7.5v-13h-7.5Zm9 0v13h7.5a.25.25 0 0 0 .25-.25V5.75a.25.25 0 0 0-.25-.25h-7.5ZM2 5.75C2 4.784 2.784 4 3.75 4h16.5c.966 0 1.75.784 1.75 1.75v12.5A1.75 1.75 0 0 1 20.25 20H3.75A1.75 1.75 0 0 1 2 18.25V5.75Z"
            />
        </BaseIcon>
    )
})

export const IconCopy: IconComponent<IconProps> = forwardRef(function IconCopy(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M7 4.75C7 3.784 7.784 3 8.75 3h10.5c.966 0 1.75.784 1.75 1.75v10.51a1.75 1.75 0 0 1-1.75 1.75H17v2.24A1.75 1.75 0 0 1 15.25 21H4.75A1.75 1.75 0 0 1 3 19.25V8.75C3 7.784 3.784 7 4.75 7H7V4.75ZM4.75 8.5a.25.25 0 0 0-.25.25v10.5c0 .138.112.25.25.25h10.5a.25.25 0 0 0 .25-.25V8.75a.25.25 0 0 0-.25-.25H4.75ZM17 15.51V8.75A1.75 1.75 0 0 0 15.25 7H8.5V4.75a.25.25 0 0 1 .25-.25h10.5a.25.25 0 0 1 .25.25v10.51a.25.25 0 0 1-.25.25H17Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCreditCard: IconComponent<IconProps> = forwardRef(function IconCreditCard(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M3.75 5.503a.25.25 0 0 0-.25.25V9h16.996c0-1.083 0-2.166-.002-3.249a.25.25 0 0 0-.249-.248H3.75ZM20.496 10.5H3.5v7.746c0 .138.112.25.25.25h16.496a.25.25 0 0 0 .25-.25V10.5ZM2 5.753c0-.967.784-1.75 1.75-1.75h16.495c.964 0 1.748.78 1.75 1.746l.001 3.304v9.193a1.75 1.75 0 0 1-1.75 1.75H3.75A1.75 1.75 0 0 1 2 18.246V5.753Zm4 7.497a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 0 1.5h-3a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCrown: IconComponent<IconProps> = forwardRef(function IconCrown(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 3a.75.75 0 0 1 .628.34l3.86 5.903 5.384-3.14a.75.75 0 0 1 1.113.798l-2.399 11.7A1.75 1.75 0 0 1 18.872 20H5.128a1.75 1.75 0 0 1-1.714-1.399l-2.399-11.7a.75.75 0 0 1 1.113-.799l5.384 3.141 3.86-5.903A.75.75 0 0 1 12 3Zm0 2.12-3.622 5.54a.75.75 0 0 1-1.006.238l-4.55-2.655L4.883 18.3a.25.25 0 0 0 .245.2h13.744a.25.25 0 0 0 .245-.2l2.061-10.057-4.55 2.655a.75.75 0 0 1-1.006-.238L12 5.12Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconCursor: IconComponent<IconProps> = forwardRef(function IconCursor(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="m4.246 4.246 5.732 15.922 2.777-6.734a1.25 1.25 0 0 1 .68-.679l6.733-2.777L4.246 4.246Zm-1.592.008c-.358-.995.605-1.958 1.6-1.6l17.031 6.132c1.076.387 1.11 1.896.053 2.332l-7.236 2.984-2.984 7.236c-.436 1.057-1.945 1.023-2.332-.053L2.654 4.254Z"
            />
        </BaseIcon>
    )
})

export const IconCursorBox: IconComponent<IconProps> = forwardRef(function IconCursorBox(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M3 4.75C3 3.784 3.784 3 4.75 3h14.5c.966 0 1.75.784 1.75 1.75v6a.75.75 0 0 1-1.5 0v-6a.25.25 0 0 0-.25-.25H4.75a.25.25 0 0 0-.25.25v14.5c0 .138.112.25.25.25h6a.75.75 0 0 1 0 1.5h-6A1.75 1.75 0 0 1 3 19.25V4.75Z" />
            <path d="M12.374 11.143a1 1 0 0 0-1.23 1.23l2.48 9.019c.233.844 1.361 1.003 1.818.256l2.355-3.852 3.852-2.355c.747-.457.588-1.585-.256-1.817l-9.02-2.48Z" />
        </BaseIcon>
    )
})

export const IconCursorClick: IconComponent<IconProps> = forwardRef(function IconCursorClick(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M11 1a.75.75 0 0 1 .75.75V3.5a.75.75 0 0 1-1.5 0V1.75A.75.75 0 0 1 11 1ZM4.563 3.563a.75.75 0 0 1 1.06 0l1.311 1.31a.75.75 0 0 1-1.06 1.061l-1.311-1.31a.75.75 0 0 1 0-1.061Zm12.874 0a.75.75 0 0 1 0 1.06l-1.31 1.311a.75.75 0 1 1-1.061-1.06l1.31-1.311a.75.75 0 0 1 1.061 0Zm-5.898 6.76 3.098 8.385 1.673-3.01a1 1 0 0 1 .388-.388l3.059-1.7-8.218-3.287Zm-1.797-.535c-.3-.813.504-1.597 1.309-1.275l10.24 4.095a1 1 0 0 1 .114 1.803l-3.853 2.14-2.132 3.837a1 1 0 0 1-1.812-.139L9.742 9.788Zm7.879 6.639Zm-.194.194ZM2 10a.75.75 0 0 1 .75-.75H4.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 10Zm4.934 4.066a.75.75 0 0 1 0 1.06l-1.31 1.311a.75.75 0 1 1-1.061-1.06l1.31-1.311a.75.75 0 0 1 1.061 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconDashboard: IconComponent<IconProps> = forwardRef(function IconDashboard(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17.657 7.343a8 8 0 0 0-11.62 10.99 1 1 0 0 1-1.49 1.334C1.028 15.74 1.156 9.702 4.929 5.93c3.905-3.905 10.237-3.905 14.142 0 3.773 3.773 3.9 9.811.382 13.738a1 1 0 0 1-1.49-1.334 8 8 0 0 0-.306-10.99Zm-9.365 1.95a1 1 0 0 1 1.414 0l3 3a1 1 0 1 1-1.415 1.414l-3-3a1 1 0 0 1 0-1.414Z"
            />
        </BaseIcon>
    )
})

export const IconDatabase: IconComponent<IconProps> = forwardRef(function IconDatabase(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.801 4.894c-.277.232-.301.38-.301.425 0 .046.024.194.301.426.274.229.714.468 1.323.684 1.212.43 2.936.71 4.876.71 1.94 0 3.664-.28 4.876-.71.61-.216 1.05-.455 1.323-.684.277-.232.301-.38.301-.426 0-.046-.024-.193-.301-.425-.274-.229-.714-.469-1.323-.685C15.664 3.78 13.94 3.5 12 3.5c-1.94 0-3.664.28-4.876.71-.61.215-1.05.455-1.323.684ZM18.5 7.344a7.274 7.274 0 0 1-1.123.5c-1.412.5-3.313.795-5.377.795-2.064 0-3.965-.295-5.377-.796A7.272 7.272 0 0 1 5.5 7.344V12c0 .028.015.165.294.392.273.222.713.456 1.324.667 1.214.418 2.94.691 4.882.691 1.942 0 3.668-.273 4.882-.691.61-.21 1.051-.445 1.324-.667.28-.227.294-.364.294-.392V7.344ZM20 5.32c0-.663-.381-1.192-.838-1.575-.46-.386-1.082-.7-1.785-.948C15.965 2.296 14.064 2 12 2c-2.064 0-3.965.295-5.377.796-.703.249-1.324.562-1.785.948C4.381 4.127 4 4.656 4 5.32v13.362c0 .663.381 1.192.838 1.575.46.386 1.082.7 1.785.948 1.412.5 3.313.796 5.377.796 2.064 0 3.965-.295 5.377-.796.703-.249 1.324-.563 1.785-.948.457-.383.838-.912.838-1.575V5.319Zm-1.5 8.669c-.338.185-.72.348-1.129.489-1.41.486-3.309.773-5.371.773-2.062 0-3.961-.287-5.371-.773a7.35 7.35 0 0 1-1.129-.489v4.693c0 .046.024.193.301.425.274.229.714.468 1.323.684 1.212.43 2.936.71 4.876.71 1.94 0 3.664-.28 4.876-.71.61-.215 1.05-.455 1.323-.684.277-.232.301-.38.301-.425v-4.693Z"
            />
        </BaseIcon>
    )
})

export const IconDay: IconComponent<IconProps> = forwardRef(function IconDay(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                d="M12.0002 3.79083V2.26758M5.8418 18.6586L4.7647 19.7357M12.0002 22.7327V21.2094M19.2353 5.26468L18.1582 6.34179M20.709 12.5001H22.2322M18.1582 18.6586L19.2353 19.7357M1.76758 12.5001H3.29083M4.76462 5.26462L5.84173 6.34173M15.7123 8.78781C17.7626 10.8381 17.7626 14.1622 15.7123 16.2124C13.6621 18.2627 10.3379 18.2627 8.28769 16.2124C6.23744 14.1622 6.23744 10.8381 8.28769 8.78781C10.3379 6.73756 13.6621 6.73756 15.7123 8.78781Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </BaseIcon>
    )
})

export const IconDecisionTree: IconComponent<IconProps> = forwardRef(
    function IconDecisionTree(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path d="M12 6c-1.24 0-2.25-1.01-2.25-2.25S10.76 1.5 12 1.5s2.25 1.01 2.25 2.25S13.24 6 12 6Zm0-3a.751.751 0 0 0 0 1.5.751.751 0 0 0 0-1.5ZM12 15a.753.753 0 0 1-.386-.107l-3.75-2.25a.752.752 0 0 1 0-1.286l3.75-2.25a.75.75 0 0 1 .771 0l3.75 2.25a.751.751 0 0 1 .001 1.286l-3.75 2.25A.752.752 0 0 1 12 15Zm-2.292-3L12 13.375 14.292 12 12 10.625 9.708 12ZM8.25 22.5h-6a.75.75 0 0 1-.75-.75v-3a.75.75 0 0 1 .75-.75h6a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-.75.75ZM3 21h4.5v-1.5H3V21ZM21.75 22.5h-6a.75.75 0 0 1-.75-.75v-3a.75.75 0 0 1 .75-.75h6a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-.75.75ZM16.5 21H21v-1.5h-4.5V21Z" />
                <path d="M12 10.5a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 1 1 1.5 0v4.5a.75.75 0 0 1-.75.75ZM18.75 18.75A.75.75 0 0 1 18 18v-5.25h-2.25a.75.75 0 1 1 0-1.5h3a.75.75 0 0 1 .75.75v6a.75.75 0 0 1-.75.75ZM5.25 18.75A.75.75 0 0 1 4.5 18v-6a.75.75 0 0 1 .75-.75h3a.75.75 0 1 1 0 1.5H6V18a.75.75 0 0 1-.75.75Z" />
            </BaseIcon>
        )
    }
)

export const IconDocument: IconComponent<IconProps> = forwardRef(function IconDocument(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M4 3.75C4 2.784 4.784 2 5.75 2h6.586c.464 0 .909.184 1.237.513M5.75 3.5a.25.25 0 0 0-.25.25v16.5c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25V10h-4.75A1.75 1.75 0 0 1 12 8.25V3.5H5.75Zm7.75 1.06v3.69c0 .138.112.25.25.25h3.69L13.5 4.56Zm.073-2.047 5.914 5.914c.329.328.513.773.513 1.237V20.25A1.75 1.75 0 0 1 18.25 22H5.75A1.75 1.75 0 0 1 4 20.25V3.75m4 9.5a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75Zm0 4a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconDownload: IconComponent<IconProps> = forwardRef(function IconDownload(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 3a.75.75 0 0 1 .75.75v9.44l2.22-2.22a.75.75 0 1 1 1.06 1.06l-3.5 3.5a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06l2.22 2.22V3.75A.75.75 0 0 1 12 3ZM3.75 14a.75.75 0 0 1 .75.75v4.5c0 .138.112.25.25.25h14.5a.25.25 0 0 0 .25-.25v-4.5a.75.75 0 0 1 1.5 0v4.5A1.75 1.75 0 0 1 19.25 21H4.75A1.75 1.75 0 0 1 3 19.25v-4.5a.75.75 0 0 1 .75-.75Z"
            />
        </BaseIcon>
    )
})

export const IconDrag: IconComponent<IconProps> = forwardRef(function IconDrag(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M7.5 5.75a1.75 1.75 0 1 1 3.5 0 1.75 1.75 0 0 1-3.5 0ZM13 5.75a1.75 1.75 0 1 1 3.5 0 1.75 1.75 0 0 1-3.5 0ZM7.5 18.25a1.75 1.75 0 1 1 3.5 0 1.75 1.75 0 0 1-3.5 0ZM13 18.25a1.75 1.75 0 1 1 3.5 0 1.75 1.75 0 0 1-3.5 0ZM7.5 11.9a1.75 1.75 0 1 1 3.5 0v.1a1.75 1.75 0 1 1-3.5 0v-.1ZM13 11.9a1.75 1.75 0 1 1 3.5 0v.1a1.75 1.75 0 1 1-3.5 0v-.1Z" />
        </BaseIcon>
    )
})

export const IconEllipsis: IconComponent<IconProps> = forwardRef(function IconEllipsis(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M3.75 10.25a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5ZM12 10.25a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5ZM20.25 10.25a1.75 1.75 0 1 0 0 3.5 1.75 1.75 0 0 0 0-3.5Z" />
        </BaseIcon>
    )
})

export const IconExpand: IconComponent<IconProps> = forwardRef(function IconExpand(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.9393 5.00001C11.5251 4.41422 12.4749 4.41422 13.0607 5.00001L12.5303 5.53034L13.0607 5.00001L16.5303 8.46968C16.8232 8.76257 16.8232 9.23745 16.5303 9.53034C16.2374 9.82323 15.7626 9.82323 15.4697 9.53034L12 6.06067L8.53033 9.53034C8.23744 9.82323 7.76256 9.82323 7.46967 9.53034C7.17678 9.23745 7.17678 8.76257 7.46967 8.46968L10.9393 5.00001ZM7.46967 14.4697C7.76256 14.1768 8.23744 14.1768 8.53033 14.4697L12 17.9393L15.4697 14.4697C15.7626 14.1768 16.2374 14.1768 16.5303 14.4697C16.8232 14.7626 16.8232 15.2374 16.5303 15.5303L13.0607 19C12.4749 19.5858 11.5251 19.5858 10.9393 19L7.46967 15.5303C7.17678 15.2374 7.17678 14.7626 7.46967 14.4697Z"
            />
        </BaseIcon>
    )
})

export const IconExpand45: IconComponent<IconProps> = forwardRef(function IconExpand45(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M13 3.75a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0V5.56l-5.22 5.22a.75.75 0 1 1-1.06-1.06l5.22-5.22h-4.69a.75.75 0 0 1-.75-.75Zm-10 10a.75.75 0 0 1 1.5 0v4.69l5.22-5.22a.75.75 0 1 1 1.06 1.06L5.56 19.5h4.69a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75v-6.5Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconExternal: IconComponent<IconProps> = forwardRef(function IconExternal(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M13 3.75a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 .75.75v6.5a.75.75 0 0 1-1.5 0V5.56l-7.97 7.97a.75.75 0 1 1-1.06-1.06l7.97-7.97h-4.69a.75.75 0 0 1-.75-.75ZM5.324 5H9.25a.75.75 0 1 1 0 1.5h-3.9c-.292 0-.467 0-.596.011a.628.628 0 0 0-.12.018.25.25 0 0 0-.105.104.626.626 0 0 0-.018.121c-.01.13-.011.304-.011.596v11.3c0 .292 0 .467.011.596a.625.625 0 0 0 .018.12.25.25 0 0 0 .104.105.638.638 0 0 0 .121.018c.13.01.304.011.596.011h11.3c.292 0 .467 0 .596-.011a.636.636 0 0 0 .12-.018.25.25 0 0 0 .105-.105.636.636 0 0 0 .018-.12c.01-.13.011-.304.011-.596V14a.75.75 0 0 1 1.5 0v4.676c0 .258 0 .494-.016.692a1.775 1.775 0 0 1-.175.677 1.75 1.75 0 0 1-.765.764c-.23.118-.463.157-.676.175-.198.016-.434.016-.692.016H5.324c-.258 0-.494 0-.692-.016a1.775 1.775 0 0 1-.676-.175 1.75 1.75 0 0 1-.765-.765 1.774 1.774 0 0 1-.175-.676A9.053 9.053 0 0 1 3 18.676V7.324c0-.258 0-.494.016-.692.018-.213.057-.446.175-.676a1.75 1.75 0 0 1 .765-.765c.23-.118.463-.157.676-.175C4.83 5 5.066 5 5.324 5ZM17.47 19.37v-.002Zm-.1.1h-.003Zm-12.74 0h.002Zm-.1-.102a.02.02 0 0 1 0 .001Zm0-12.737v.001Zm.102-.102h-.001Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconEye: IconComponent<IconProps> = forwardRef(function IconEye(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 5.5c-3.338 0-6.787 2.058-9.107 6.405a.208.208 0 0 0 0 .19C5.213 16.441 8.663 18.5 12 18.5c3.338 0 6.787-2.058 9.107-6.405a.208.208 0 0 0 0-.19C18.787 7.558 15.337 5.5 12 5.5ZM12 4c4.01 0 7.91 2.477 10.43 7.2a1.708 1.708 0 0 1 0 1.6C19.91 17.524 16.01 20 12 20s-7.91-2.477-10.43-7.2a1.708 1.708 0 0 1 0-1.6l.642.342-.642-.343C4.09 6.477 7.99 4 12 4Zm0 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM8 12a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconFastForward: IconComponent<IconProps> = forwardRef(function IconFastForward(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M6.88832 5.34412L12.2424 9.92938V6.6733C12.2424 5.17854 13.9954 4.37182 15.1307 5.34412L21.351 10.6711C22.1665 11.3696 22.1665 12.631 21.351 13.3295L15.1307 18.6565C13.9954 19.6288 12.2424 18.8221 12.2424 17.3273V14.0712L6.88832 18.6565C5.75299 19.6288 4 18.8221 4 17.3273V6.6733C4 5.17854 5.75299 4.37182 6.88832 5.34412Z" />
        </BaseIcon>
    )
})

export const IconFeatures: IconComponent<IconProps> = forwardRef(function IconFeatures(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.5 1a.75.75 0 0 1 .75.75c0 1.558.345 2.508.918 3.082.574.573 1.524.918 3.082.918a.75.75 0 0 1 0 1.5c-1.558 0-2.508.345-3.082.918-.573.574-.918 1.524-.918 3.082a.75.75 0 0 1-1.5 0c0-1.558-.345-2.508-.918-3.082-.574-.573-1.524-.918-3.082-.918a.75.75 0 0 1 0-1.5c1.558 0 2.508-.345 3.082-.918.573-.574.918-1.524.918-3.082A.75.75 0 0 1 6.5 1Zm0 4.106A3.944 3.944 0 0 1 5.106 6.5 3.944 3.944 0 0 1 6.5 7.894 3.944 3.944 0 0 1 7.894 6.5 3.944 3.944 0 0 1 6.5 5.106ZM15 7a.75.75 0 0 1 .75.75c0 2.426.536 4.001 1.517 4.983.982.981 2.557 1.517 4.983 1.517a.75.75 0 0 1 0 1.5c-2.426 0-4.001.536-4.983 1.517-.981.982-1.517 2.557-1.517 4.983a.75.75 0 0 1-1.5 0c0-2.426-.536-4.001-1.517-4.983-.982-.981-2.557-1.517-4.983-1.517a.75.75 0 0 1 0-1.5c2.426 0 4.001-.536 4.983-1.517.981-.982 1.517-2.557 1.517-4.983A.75.75 0 0 1 15 7Zm0 5.009a5.83 5.83 0 0 1-1.207 1.784A5.823 5.823 0 0 1 12.01 15c.68.301 1.277.7 1.784 1.207A5.823 5.823 0 0 1 15 17.99a5.83 5.83 0 0 1 1.207-1.784A5.823 5.823 0 0 1 17.99 15a5.823 5.823 0 0 1-1.784-1.207A5.823 5.823 0 0 1 15 12.01Z"
            />
        </BaseIcon>
    )
})

export const IconFilter: IconComponent<IconProps> = forwardRef(function IconFilter(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 5a1 1 0 0 1 1-1h18a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1Zm3 7a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1Zm3 7a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2H9a1 1 0 0 1-1-1Z"
            />
        </BaseIcon>
    )
})

export const IconFlag: IconComponent<IconProps> = forwardRef(function IconFlag(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M4 3.75C4 2.784 4.784 2 5.75 2h13.631c1.398 0 2.232 1.558 1.457 2.72l-2.594 3.891a.25.25 0 0 0 0 .278l2.594 3.89c.775 1.163-.059 2.721-1.457 2.721H5.5v5.75a.75.75 0 0 1-1.5 0V3.75ZM5.5 14h13.881a.25.25 0 0 0 .209-.389l-2.594-3.89a1.75 1.75 0 0 1 0-1.942l2.594-3.89a.25.25 0 0 0-.209-.389H5.75a.25.25 0 0 0-.25.25V14Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconFolder: IconComponent<IconProps> = forwardRef(function IconFolder(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                d="M10.0742 4H4C3.44772 4 3 4.44772 3 5V18C3 18.5523 3.44772 19 4 19H19C19.5523 19 20 18.5523 20 18V9.73529V7.64706C20 7.09477 19.5523 6.64706 19 6.64706H12.9258C12.5937 6.64706 12.2833 6.48222 12.0973 6.20711L10.9027 4.43995C10.7167 4.16484 10.4063 4 10.0742 4Z"
                stroke="currentColor"
                fill="transparent"
                strokeWidth="1.5"
            />
        </BaseIcon>
    )
})

export const IconFolderOpen: IconComponent<IconProps> = forwardRef(function IconFolderOpen(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                d="M10.0742 4H4C3.44772 4 3 4.44772 3 5V18C3 18.5523 3.44772 19 4 19H19C19.5523 19 20 18.5523 20 18V9.73529V7.64706C20 7.09477 19.5523 6.64706 19 6.64706H12.9258C12.5937 6.64706 12.2833 6.48222 12.0973 6.20711L10.9027 4.43995C10.7167 4.16484 10.4063 4 10.0742 4Z"
                stroke="currentColor"
                fill="transparent"
                strokeWidth="1.5"
            />
            <path
                d="M7.09314 11.4647L5.70425 16.4647C5.35025 17.7391 4.67736 19 6 19H18.5585C19.4193 19 20.1836 18.4491 20.4559 17.6325L22.1225 12.6325C22.5542 11.3374 21.5903 10 20.2252 10H9.02018C8.12176 10 7.3336 10.5991 7.09314 11.4647Z"
                fill="currentColor"
                stroke="transparent"
                strokeWidth="1.5"
            />
        </BaseIcon>
    )
})

export const IconFlask: IconComponent<IconProps> = forwardRef(function IconFlask(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7 6.75A.75.75 0 0 1 7.75 6h8.5a.75.75 0 0 1 0 1.5H16v3.233a31.851 31.851 0 0 0 .885 1.006c.322.358.665.738.995 1.132.72.86 1.434 1.842 1.83 2.948.18.505.29 1.04.29 1.597A4.584 4.584 0 0 1 15.416 22H8.584A4.584 4.584 0 0 1 4 17.416c0-.558.11-1.092.29-1.597.396-1.106 1.11-2.089 1.83-2.948.33-.394.673-.774.995-1.132l.093-.104c.287-.317.553-.616.792-.902V7.5h-.25A.75.75 0 0 1 7 6.75Zm2.5.75v3.763l-.164.206c-.31.386-.662.78-1.013 1.17l-.091.101c-.325.361-.65.722-.962 1.095-.345.41-.66.82-.928 1.234l.098-.015c.895-.137 2.053-.275 2.99-.25 1.137.03 2 .302 2.774.545l.021.007c.779.245 1.469.46 2.384.484.797.02 1.846-.1 2.725-.234.211-.032.41-.065.59-.095-.313-.566-.728-1.12-1.194-1.676-.312-.373-.637-.734-.962-1.095l-.091-.101c-.351-.39-.703-.784-1.013-1.17l-.164-.206V7.5h-5Z"
            />
            <path d="M11 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm4-1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
        </BaseIcon>
    )
})

export const IconFunnels: IconComponent<IconProps> = forwardRef(function IconFunnels(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M3 5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5ZM3 11a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2ZM3 17a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2Z" />
        </BaseIcon>
    )
})

export const IconGanttChart: IconComponent<IconProps> = forwardRef(function IconGanttChart(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M9.105 8.368V6.052a.573.573 0 0 0-.579-.578L1.58 5.473a.573.573 0 0 0-.579.58v2.315c0 .325.255.579.579.579h6.947a.572.572 0 0 0 .579-.58Zm-1.157-.579h-5.79V6.631h5.79v1.158Zm14.473 6.948h-9.263a.573.573 0 0 0-.578.579v2.315c0 .325.255.579.578.579h9.263A.573.573 0 0 0 23 17.63v-2.316a.572.572 0 0 0-.579-.578Zm-.578 2.315h-8.106v-1.158h8.106v1.158Zm-1.737-3.473a.573.573 0 0 0 .578-.579v-2.316a.573.573 0 0 0-.578-.578H11.42V2.579A.573.573 0 0 0 10.843 2a.573.573 0 0 0-.58.579v7.526H7.368a.573.573 0 0 0-.579.578V13c0 .324.255.579.579.579h2.895v7.527c0 .324.255.578.579.578a.573.573 0 0 0 .578-.578v-7.526h8.686ZM7.948 11.263h11.58v1.158H7.947v-1.158Z" />
        </BaseIcon>
    )
})

export const IconGear: IconComponent<IconProps> = forwardRef(function IconGear(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M11.61 3.5a.25.25 0 0 0-.208.111l-1.17 1.755a1.75 1.75 0 0 1-1.85.734L6.59 5.687a.25.25 0 0 0-.233.066l-.604.604a.25.25 0 0 0-.066.233L6.1 8.383a1.75 1.75 0 0 1-.734 1.85l-1.755 1.17a.25.25 0 0 0-.111.207v.78a.25.25 0 0 0 .111.208l1.755 1.17a1.75 1.75 0 0 1 .734 1.85l-.413 1.792a.25.25 0 0 0 .066.233l.604.604a.25.25 0 0 0 .233.066l1.793-.413a1.75 1.75 0 0 1 1.85.734l1.17 1.755a.25.25 0 0 0 .207.111h.78a.25.25 0 0 0 .208-.111l1.17-1.755a1.75 1.75 0 0 1 1.85-.734l1.792.413a.25.25 0 0 0 .233-.066l.604-.604a.25.25 0 0 0 .066-.233l-.413-1.793a1.75 1.75 0 0 1 .734-1.85l1.755-1.17a.25.25 0 0 0 .111-.207v-.78a.25.25 0 0 0-.111-.208l-1.755-1.17a1.75 1.75 0 0 1-.734-1.85l.413-1.792a.25.25 0 0 0-.066-.233l-.604-.604.53-.53-.53.53a.25.25 0 0 0-.233-.066l-1.793.413a1.75 1.75 0 0 1-1.85-.734l-1.17-1.755a.25.25 0 0 0-.207-.111h-.78Zm-1.456-.72A1.75 1.75 0 0 1 11.61 2h.78a1.75 1.75 0 0 1 1.456.78l1.17 1.754a.25.25 0 0 0 .264.105l1.793-.414a1.75 1.75 0 0 1 1.63.468l-.51.51.51-.51.604.604a1.75 1.75 0 0 1 .468 1.63L19.36 8.72a.25.25 0 0 0 .105.264l1.755 1.17c.487.325.779.871.779 1.456v.78a1.75 1.75 0 0 1-.78 1.456l-1.754 1.17a.25.25 0 0 0-.105.264l.414 1.793a1.75 1.75 0 0 1-.468 1.63l-.604.604a1.75 1.75 0 0 1-1.63.468l-1.793-.414a.25.25 0 0 0-.264.105l-1.17 1.755A1.75 1.75 0 0 1 12.39 22h-.78a1.75 1.75 0 0 1-1.456-.78l-1.17-1.754a.25.25 0 0 0-.264-.105l-1.793.414a1.75 1.75 0 0 1-1.63-.468l-.604-.604a1.75 1.75 0 0 1-.468-1.63l.414-1.793a.25.25 0 0 0-.105-.264l-1.755-1.17A1.75 1.75 0 0 1 2 12.39v-.78c0-.585.292-1.131.78-1.456l1.754-1.17a.25.25 0 0 0 .105-.264l-.414-1.793a1.75 1.75 0 0 1 .468-1.63l.604-.604a1.75 1.75 0 0 1 1.63-.468l1.793.414a.25.25 0 0 0 .264-.105l1.17-1.755ZM12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-3.5 2a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconGearFilled: IconComponent<IconProps> = forwardRef(function IconGearFilled(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M10.154 2.78A1.75 1.75 0 0 1 11.61 2h.78a1.75 1.75 0 0 1 1.456.78l1.17 1.754a.25.25 0 0 0 .264.105l1.793-.414a1.75 1.75 0 0 1 1.63.468l.604.604a1.75 1.75 0 0 1 .468 1.63L19.36 8.72a.25.25 0 0 0 .105.264l1.755 1.17c.487.325.779.871.779 1.456v.78a1.75 1.75 0 0 1-.78 1.456l-1.754 1.17a.25.25 0 0 0-.105.264l.414 1.793a1.75 1.75 0 0 1-.468 1.63l-.604.604a1.75 1.75 0 0 1-1.63.468l-1.793-.414a.25.25 0 0 0-.264.105l-1.17 1.755A1.75 1.75 0 0 1 12.39 22h-.78a1.75 1.75 0 0 1-1.456-.78l-1.17-1.754a.25.25 0 0 0-.264-.105l-1.793.414a1.75 1.75 0 0 1-1.63-.468l-.604-.604a1.75 1.75 0 0 1-.468-1.63l.414-1.793a.25.25 0 0 0-.105-.264l-1.755-1.17A1.75 1.75 0 0 1 2 12.39v-.78c0-.585.292-1.131.78-1.456l1.754-1.17a.25.25 0 0 0 .105-.264l-.414-1.793a1.75 1.75 0 0 1 .468-1.63l.604-.604a1.75 1.75 0 0 1 1.63-.468l1.793.414a.25.25 0 0 0 .264-.105l1.17-1.755ZM8.875 12a3.125 3.125 0 1 1 6.25 0 3.125 3.125 0 0 1-6.25 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconGithub: IconComponent<IconProps> = forwardRef(function IconGithub(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M12 2C6.478 2 2 6.477 2 12c0 4.418 2.865 8.167 6.839 9.49.5.092.683-.218.683-.482 0-.238-.01-1.026-.014-1.862-2.782.605-3.369-1.18-3.369-1.18-.455-1.155-1.11-1.463-1.11-1.463-.907-.62.068-.608.068-.608 1.005.07 1.533 1.03 1.533 1.03.892 1.53 2.34 1.088 2.91.832.09-.646.35-1.087.635-1.337-2.221-.253-4.556-1.11-4.556-4.942a3.87 3.87 0 0 1 1.03-2.684c-.103-.252-.446-1.27.097-2.647 0 0 .84-.269 2.751 1.025A9.585 9.585 0 0 1 12 6.836a9.61 9.61 0 0 1 2.505.336c1.909-1.294 2.748-1.025 2.748-1.025.544 1.378.201 2.395.098 2.647.64.7 1.029 1.592 1.029 2.684 0 3.84-2.34 4.686-4.566 4.934.358.31.678.919.678 1.852 0 1.338-.012 2.415-.012 2.744 0 .266.18.578.687.48A10.001 10.001 0 0 0 22 12c0-5.523-4.477-10-10-10ZM5.745 16.245c-.022.05-.1.065-.171.03-.073-.032-.113-.1-.09-.15.022-.05.1-.065.172-.03.073.032.115.1.09.15Zm.492.44c-.047.044-.14.023-.204-.047-.065-.07-.078-.163-.03-.208.05-.044.14-.023.206.046.065.071.078.164.028.208Zm.338.56c-.062.043-.162.003-.224-.085-.061-.09-.061-.196.002-.239.062-.043.16-.004.223.084.061.09.061.197-.001.24Zm.57.651c-.054.06-.171.044-.257-.038-.087-.08-.111-.195-.056-.256.055-.06.173-.043.259.039.086.08.113.195.054.255Zm.738.22c-.024.078-.137.114-.25.08-.113-.034-.187-.126-.164-.205.023-.079.136-.116.25-.08.113.034.188.125.164.205Zm.84.093c.002.082-.094.15-.213.152-.12.003-.216-.064-.217-.145 0-.083.094-.151.213-.153.119-.002.216.064.216.146Zm.824-.032c.014.08-.068.163-.186.185-.117.022-.224-.028-.239-.108-.014-.082.07-.165.186-.186.118-.02.224.027.239.11Z" />
        </BaseIcon>
    )
})

export const IconGlobe: IconComponent<IconProps> = forwardRef(function IconGlobe(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="m4.722 7.604 2.774 2.08a.25.25 0 0 0 .374-.088l.262-.52a1.75 1.75 0 0 1 1.136-.91l2.97-.747a.25.25 0 0 0 .182-.182l.907-3.634a8.495 8.495 0 0 0-8.604 4Zm10.06-3.638L13.875 7.6a1.75 1.75 0 0 1-1.271 1.273l-2.97.748a.25.25 0 0 0-.163.13l-.262.52a1.75 1.75 0 0 1-2.612.613L4.052 8.976a8.5 8.5 0 1 0 10.73-5.01ZM3.081 7.472A10 10 0 0 1 11.999 2c.836 0 1.649.103 2.427.297 4.35 1.084 7.573 5.016 7.573 9.703 0 5.523-4.477 10-10 10s-10-4.477-10-10a9.96 9.96 0 0 1 1.082-4.528Zm7.181 5.21a1.75 1.75 0 0 1 1.347-.507l1.223.078c.31.02.607.121.865.294l1.993 1.34c.73.49.983 1.448.59 2.235l-.33.66a1.75 1.75 0 0 1-1.566.968h-1.847a1.75 1.75 0 0 1-1.456-.78L9.834 15.1a1.75 1.75 0 0 1 .22-2.211l.208-.207Zm1.251.99a.25.25 0 0 0-.192.073l-.208.207a.25.25 0 0 0-.031.315l1.247 1.872a.25.25 0 0 0 .208.111h1.847a.25.25 0 0 0 .223-.138l.33-.66a.25.25 0 0 0-.083-.32l-1.994-1.34a.25.25 0 0 0-.123-.042l-1.224-.078Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconGraduationCap: IconComponent<IconProps> = forwardRef(
    function IconGraduationCap(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path d="M11.956 5.002a.707.707 0 0 0-.19.037L1.468 8.652a.703.703 0 0 0 0 1.323L5.242 11.3v5.098c0 .303.195.571.483.666l6.063 1.945a.701.701 0 0 0 .424 0l6.063-1.945a.702.702 0 0 0 .483-.666v-5.105l.65-.227a.703.703 0 1 0-.467-1.324L12 12.177 3.823 9.31 12 6.443l9.596 3.371v3.409a.702.702 0 1 0 1.404 0V9.31a.703.703 0 0 0-.468-.659L12.234 5.038a.701.701 0 0 0-.278-.036Zm-5.31 6.787 5.12 1.8a.698.698 0 0 0 .468 0l5.12-1.8v4.096L12 17.604l-5.354-1.72V11.79Z" />
            </BaseIcon>
        )
    }
)

export const IconGraph: IconComponent<IconProps> = forwardRef(function IconGraph(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M11 3.001a1.5 1.5 0 0 0-1.5 1.5v15a1.5 1.5 0 0 0 1.5 1.5h2a1.5 1.5 0 0 0 1.5-1.5v-15a1.5 1.5 0 0 0-1.5-1.5h-2Zm-6.5 10a1.5 1.5 0 0 0-1.5 1.5v5a1.5 1.5 0 0 0 1.5 1.5h2a1.5 1.5 0 0 0 1.5-1.5v-5a1.5 1.5 0 0 0-1.5-1.5h-2Zm11.5-3.5a1.5 1.5 0 0 1 1.5-1.5h2a1.5 1.5 0 0 1 1.5 1.5v10a1.5 1.5 0 0 1-1.5 1.5h-2a1.5 1.5 0 0 1-1.5-1.5v-10Z" />
        </BaseIcon>
    )
})

export const IconGroups: IconComponent<IconProps> = forwardRef(function IconGroups(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M2 5.75C2 4.784 2.784 4 3.75 4h3.5a.75.75 0 0 1 0 1.5h-3.5a.25.25 0 0 0-.25.25v3.5a.75.75 0 0 1-1.5 0v-3.5Zm14-1a.75.75 0 0 1 .75-.75h3.5c.966 0 1.75.784 1.75 1.75v3.5a.75.75 0 0 1-1.5 0v-3.5a.25.25 0 0 0-.25-.25h-3.5a.75.75 0 0 1-.75-.75ZM2.75 14a.75.75 0 0 1 .75.75v3.5c0 .138.112.25.25.25h3.5a.75.75 0 0 1 0 1.5h-3.5A1.75 1.75 0 0 1 2 18.25v-3.5a.75.75 0 0 1 .75-.75Zm18.5 0a.75.75 0 0 1 .75.75v3.5A1.75 1.75 0 0 1 20.25 20h-3.5a.75.75 0 0 1 0-1.5h3.5a.25.25 0 0 0 .25-.25v-3.5a.75.75 0 0 1 .75-.75ZM9.754 7.817a1.362 1.362 0 1 0 0 2.724 1.362 1.362 0 0 0 0-2.724ZM7.575 9.18a2.18 2.18 0 1 1 4.359 0 2.18 2.18 0 0 1-4.359 0Zm7.083-.817a1.09 1.09 0 1 0 0 2.18 1.09 1.09 0 0 0 0-2.18Zm-1.907 1.09a1.907 1.907 0 1 1 3.814 0 1.907 1.907 0 0 1-3.814 0Zm-4.957 4.132c-.492.55-.829 1.345-.975 2.294a.083.083 0 0 0 .022.072c.02.023.054.04.094.04h5.638c.041 0 .074-.018.095-.04a.083.083 0 0 0 .022-.072c-.146-.949-.483-1.743-.975-2.294-.484-.541-1.132-.863-1.96-.863-.83 0-1.477.322-1.961.863Zm-.61-.545c.635-.709 1.502-1.136 2.57-1.136 1.069 0 1.936.427 2.57 1.136.626.7 1.01 1.657 1.174 2.714.09.583-.38 1.054-.925 1.054H6.935c-.543 0-1.014-.47-.924-1.054.163-1.057.548-2.014 1.173-2.714Zm7.474-.318c-.212 0-.408.024-.59.068a.409.409 0 0 1-.193-.794c.247-.06.509-.092.783-.092.938 0 1.705.364 2.27.973.558.6.904 1.421 1.059 2.326.1.589-.378 1.06-.92 1.06h-2a.409.409 0 1 1 0-.817h2a.124.124 0 0 0 .094-.04.074.074 0 0 0 .02-.066c-.135-.794-.43-1.453-.851-1.907-.414-.445-.965-.711-1.672-.711Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconGridMasonry: IconComponent<IconProps> = forwardRef(function IconGridMasonry(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.324 3h4.926a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-.75.75h-6.5A.75.75 0 0 1 3 8.25V5.324c0-.258 0-.494.016-.692a1.77 1.77 0 0 1 .175-.676 1.75 1.75 0 0 1 .765-.765 1.77 1.77 0 0 1 .676-.175C4.83 3 5.066 3 5.324 3ZM4.63 4.53h.001Zm.002-.001a.626.626 0 0 1 .121-.018c.13-.01.304-.011.596-.011H9.5v3h-5V5.35c0-.292 0-.467.011-.596a.626.626 0 0 1 .018-.12.25.25 0 0 1 .104-.105Zm-.103.102v.001Zm14.716-.12a8.336 8.336 0 0 0-.596-.011H14.5v7h5V5.35c0-.292 0-.467-.011-.596a.638.638 0 0 0-.018-.12.25.25 0 0 0-.105-.105.625.625 0 0 0-.12-.018Zm.123.019h-.001Zm.101.1v.002Zm-.102-1.614c.213.018.446.057.677.175.329.168.596.435.764.765.118.23.157.463.175.676.016.198.016.434.016.692v6.926a.75.75 0 0 1-.75.75h-6.5a.75.75 0 0 1-.75-.75v-8.5a.75.75 0 0 1 .75-.75h4.926c.258 0 .494 0 .692.016ZM3 11.75a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 .75.75v8.5a.75.75 0 0 1-.75.75H5.324c-.258 0-.494 0-.692-.016a1.775 1.775 0 0 1-.676-.175 1.75 1.75 0 0 1-.765-.765 1.774 1.774 0 0 1-.175-.676A9.053 9.053 0 0 1 3 18.676V11.75Zm1.5.75v6.15c0 .292 0 .467.011.596a.625.625 0 0 0 .018.12.25.25 0 0 0 .104.105.638.638 0 0 0 .121.018c.13.01.304.011.596.011H9.5v-7h-5Zm.13 6.97h.002Zm-.1-.1v-.002ZM13 15.75a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 .75.75v2.926c0 .258 0 .494-.016.692a1.775 1.775 0 0 1-.175.677 1.75 1.75 0 0 1-.765.764 1.77 1.77 0 0 1-.676.175c-.198.016-.434.016-.692.016H13.75a.75.75 0 0 1-.75-.75v-4.5Zm1.5.75v3h4.15c.292 0 .467 0 .596-.011a.636.636 0 0 0 .12-.018.25.25 0 0 0 .105-.105.636.636 0 0 0 .018-.12c.01-.13.011-.304.011-.596V16.5h-5Zm4.87 2.97h-.002Z"
            />
        </BaseIcon>
    )
})

export const IconHandMoney: IconComponent<IconProps> = forwardRef(function IconHandMoney(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M11 2.5a2.5 2.5 0 1 0 .031 5 3.998 3.998 0 0 1 1.988-3.975A2.497 2.497 0 0 0 11 2.5Zm3.484.533a4 4 0 1 0-2.967 5.934 4 4 0 1 0 2.967-5.934ZM15 4.5a2.501 2.501 0 0 0 0 5 2.5 2.5 0 0 0 0-5ZM2 13.75c0-.966.784-1.75 1.75-1.75h2a1.75 1.75 0 0 1 1.614 1.072c2.52-.603 4.64-.776 6.316-.668 1.815.117 3.223.573 4.001 1.297.27.252.488.56.593.913.171-.125.338-.253.497-.38 1.084-.86 2.765-.815 3.694.348.704.882.743 2.183-.06 3.067-2.437 2.683-5.524 4.525-9.447 4.338-.92-.044-2.019-.324-2.847-.573a24.364 24.364 0 0 1-1.351-.452H7.349A1.75 1.75 0 0 1 5.75 22h-2A1.75 1.75 0 0 1 2 20.25v-6.5Zm5.5 5.712h1.396a.75.75 0 0 1 .27.05m0 0 .006.003.018.006a9.506 9.506 0 0 0 .364.133c.247.087.594.204.99.323.808.244 1.759.477 2.485.512 3.343.16 6.027-1.383 8.265-3.848.265-.291.285-.765-.001-1.123-.35-.437-1.07-.521-1.589-.109-.9.715-2.169 1.584-3.437 1.936-.424.118-.846.21-1.237.284-.581.12-1.233.206-1.953.252l-.019.002H13.036l-.042-.748.042.749a.75.75 0 1 1-.052-1.5l.026-.002a15.924 15.924 0 0 0 1.728-.226c.929-.194 1.526-.469 1.849-.73.236-.193.253-.317.255-.34 0-.016.002-.115-.182-.287-.4-.37-1.384-.79-3.077-.898-1.543-.1-3.583.065-6.083.68v4.881M6 14.016v6.234a.25.25 0 0 1-.25.25h-2a.25.25 0 0 1-.25-.25v-6.5a.25.25 0 0 1 .25-.25h2a.25.25 0 0 1 .25.25v.266Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconHandwave: IconComponent<IconProps> = forwardRef(function IconHandwave(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M6.24 5.99a.75.75 0 0 0 0 1.06l4.242 4.243a.75.75 0 0 1-1.06 1.06L5.886 8.819a.75.75 0 0 0-1.06 1.06l4.242 4.243a.75.75 0 0 1-1.06 1.061l-2.83-2.828a.75.75 0 0 0-1.06 1.06l4.596 4.596a6.75 6.75 0 1 0 9.546-9.546L14.735 4.94a2.249 2.249 0 0 0-.448 1.85M3.682 10.853a2.25 2.25 0 0 0-.624 3.623l4.596 4.596A8.25 8.25 0 0 0 19.32 7.404L15.61 3.692a1.25 1.25 0 0 0-1.768 0 3.734 3.734 0 0 0-.931 1.543l-1.72-1.72a2.25 2.25 0 0 0-3.722.865A2.25 2.25 0 0 0 4.63 7.218a2.25 2.25 0 0 0-.949 3.634Zm5.386-5.216a.75.75 0 0 1 1.06-1.06l2.743 2.742c.165.614.489 1.195.97 1.677l.71.71a4.443 4.443 0 0 0 1.058 4.593.75.75 0 1 0 1.06-1.06 2.943 2.943 0 0 1-.623-3.24c.166-.39.08-.84-.22-1.14l-.924-.924a2.237 2.237 0 0 1-.615-1.144m-6.28-.093 3.536 3.535a.75.75 0 0 0 1.06-1.06L9.069 5.636M6.24 5.99a.75.75 0 0 1 1.06 0Zm1.06 0 .707.707Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconHardDrive: IconComponent<IconProps> = forwardRef(function IconHardDrive(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M4 3.75C4 2.784 4.784 2 5.75 2h12.5c.966 0 1.75.784 1.75 1.75v16.5A1.75 1.75 0 0 1 18.25 22H5.75A1.75 1.75 0 0 1 4 20.25V3.75Zm1.5 12v4.5c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25v-4.5a.25.25 0 0 0-.25-.25H5.75a.25.25 0 0 0-.25.25Zm13-1.732a1.762 1.762 0 0 0-.25-.018H5.75c-.085 0-.168.006-.25.018V3.75a.25.25 0 0 1 .25-.25h12.5a.25.25 0 0 1 .25.25v10.268Z"
                clipRule="evenodd"
            />
            <path d="M16 18.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1ZM13 18.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Z" />
            <path
                fillRule="evenodd"
                d="M16 19a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm-3 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm-.5-1a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0Zm3 0a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconHeadset: IconComponent<IconProps> = forwardRef(function IconHeadset(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M4.017 9.012A2.25 2.25 0 0 0 2 11.25v3.5A2.25 2.25 0 0 0 4.25 17h1.5a.75.75 0 0 0 .75-.75v-6.5A.75.75 0 0 0 5.75 9h-.228C5.797 5.961 8.546 3.5 12 3.5c3.454 0 6.203 2.461 6.477 5.5h-.227a.75.75 0 0 0-.75.75v6.5c0 .414.336.75.75.75h.492A3.75 3.75 0 0 1 15 20.5h-2a.25.25 0 0 1-.25-.25v-.607a.75.75 0 1 0-1.5 0v.607c0 .966.784 1.75 1.75 1.75h2a5.25 5.25 0 0 0 5.246-5.055A2.25 2.25 0 0 0 22 14.75v-3.5a2.25 2.25 0 0 0-2.017-2.238C19.715 5.054 16.197 2 12 2S4.286 5.054 4.017 9.012ZM19 10.5h.75a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-.75.75H19v-5Zm-14.75 0a.75.75 0 0 0-.75.75v3.5c0 .414.336.75.75.75H5v-5h-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconHeart: IconComponent<IconProps> = forwardRef(function IconHeart(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M16.925 4.6c-1.37-.306-2.977.06-4.39 1.498a.75.75 0 0 1-1.07 0C10.052 4.66 8.445 4.294 7.075 4.6c-1.39.31-2.61 1.327-3.2 2.802-1.128 2.82-.011 7.786 8.125 12.484 8.136-4.698 9.253-9.663 8.125-12.484-.59-1.475-1.81-2.492-3.2-2.802Zm4.592 2.246c1.57 3.921-.43 9.659-9.15 14.558a.75.75 0 0 1-.734 0c-8.72-4.9-10.72-10.637-9.15-14.558.767-1.918 2.374-3.287 4.264-3.71C8.466 2.754 10.36 3.164 12 4.55c1.64-1.386 3.534-1.796 5.253-1.412 1.89.422 3.497 1.791 4.264 3.709Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconHelmet: IconComponent<IconProps> = forwardRef(function IconHelmet(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M21.75 14.25H21v-1.5c0-3.52-2.036-6.566-4.988-8.045l-.285-1.137A.75.75 0 0 0 15 3H9a.75.75 0 0 0-.728.568l-.284 1.137C5.036 6.184 3 9.23 3 12.75v1.5h-.75a.75.75 0 0 0-.75.75v1.5c0 .827.673 1.5 1.5 1.5h.45c1.853 1.699 5.094 3 7.543 3h2.014c2.449 0 5.69-1.301 7.544-3h.45c.826 0 1.5-.673 1.5-1.5V15a.75.75 0 0 0-.75-.75h-.001ZM4.5 12.75a7.485 7.485 0 0 1 2.967-5.962l-.695 2.78a.749.749 0 1 0 1.455.364l1.041-4.159A7.436 7.436 0 0 1 12 5.25c.965 0 1.884.19 2.733.523l1.04 4.159a.75.75 0 1 0 1.455-.364l-.694-2.78A7.487 7.487 0 0 1 19.5 12.75v1.5h-15v-1.5ZM21 16.5h-.75a.75.75 0 0 0-.53.22c-1.507 1.507-4.581 2.78-6.713 2.78h-2.014c-2.131 0-5.205-1.273-6.713-2.78a.75.75 0 0 0-.53-.22H3v-.75h18v.75Z" />
        </BaseIcon>
    )
})

export const IconHeartFilled: IconComponent<IconProps> = forwardRef(function IconHeartFilled(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M12.367 21.404c8.72-4.9 10.719-10.637 9.15-14.558-.767-1.918-2.375-3.287-4.265-3.71-1.718-.383-3.613.027-5.252 1.414-1.64-1.387-3.535-1.797-5.253-1.413-1.89.422-3.498 1.791-4.265 3.709-1.569 3.921.43 9.659 9.15 14.558a.75.75 0 0 0 .735 0Z" />
        </BaseIcon>
    )
})

export const IconHide: IconComponent<IconProps> = forwardRef(function IconHide(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.21967 2.21967C2.51256 1.92678 2.98744 1.92678 3.28033 2.21967L21.7803 20.7197C22.0732 21.0126 22.0732 21.4874 21.7803 21.7803C21.4874 22.0732 21.0126 22.0732 20.7197 21.7803L17.3707 18.4313C14.7875 20.0232 11.8666 20.4109 9.1046 19.5607C6.16045 18.6544 3.47241 16.3669 1.56842 12.7987C1.30353 12.3023 1.3008 11.7028 1.56734 11.2031C2.62795 9.21493 3.93095 7.62485 5.38795 6.44861L2.21967 3.28033C1.92678 2.98744 1.92678 2.51256 2.21967 2.21967ZM6.45545 7.51611C5.12411 8.55934 3.89968 10.0179 2.89081 11.9091C2.86138 11.9643 2.86066 12.0342 2.8918 12.0925C4.64624 15.3805 7.04916 17.3585 9.54591 18.127C11.7679 18.8111 14.1243 18.5574 16.2759 17.3365L14.2481 15.3088C13.6076 15.7447 12.8332 16 12 16C9.79086 16 8 14.2091 8 12C8 11.1668 8.25531 10.3924 8.6912 9.75186L6.45545 7.51611ZM9.78282 10.8435C9.60199 11.1893 9.5 11.5825 9.5 12C9.5 13.3807 10.6193 14.5 12 14.5C12.4175 14.5 12.8107 14.398 13.1565 14.2172L9.78282 10.8435ZM21.1087 11.9084C18.2369 6.52554 13.6432 4.63944 9.59772 5.85706C9.20109 5.97645 8.78277 5.75169 8.66339 5.35505C8.544 4.95841 8.76876 4.5401 9.1654 4.42071C14.0493 2.95073 19.3057 5.34229 22.4322 11.2023C22.6975 11.6996 22.6987 12.2981 22.4326 12.7968C21.7927 13.9965 21.0647 15.0509 20.2677 15.9571C19.9942 16.2682 19.5203 16.2986 19.2093 16.0251C18.8982 15.7515 18.8678 15.2776 19.1413 14.9666C19.8564 14.1535 20.5197 13.1959 21.1092 12.0908C21.1391 12.0348 21.1394 11.9659 21.1087 11.9084Z"
            />
        </BaseIcon>
    )
})

export const IconHome: IconComponent<IconProps> = forwardRef(function IconHome(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.162 3.12a.25.25 0 0 0-.324 0l-7.25 6.152a.25.25 0 0 0-.088.191v9.787c0 .138.112.25.25.25h4a.25.25 0 0 0 .25-.25v-3.5c0-.966.784-1.75 1.75-1.75h2.5c.966 0 1.75.784 1.75 1.75v3.5c0 .138.112.25.25.25h4a.25.25 0 0 0 .25-.25V9.463a.25.25 0 0 0-.088-.19l-7.25-6.152Zm-1.294-1.143a1.75 1.75 0 0 1 2.264 0l7.25 6.152c.392.332.618.82.618 1.334v9.787A1.75 1.75 0 0 1 19.25 21h-4a1.75 1.75 0 0 1-1.75-1.75v-3.5a.25.25 0 0 0-.25-.25h-2.5a.25.25 0 0 0-.25.25v3.5A1.75 1.75 0 0 1 8.75 21h-4A1.75 1.75 0 0 1 3 19.25V9.463c0-.514.226-1.002.618-1.334l7.25-6.152Z"
            />
        </BaseIcon>
    )
})

export const IconHomeFilled: IconComponent<IconProps> = forwardRef(function IconHomeFilled(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M13.132 1.977a1.75 1.75 0 0 0-2.264 0L3.618 8.13A1.75 1.75 0 0 0 3 9.463v9.787c0 .966.784 1.75 1.75 1.75h3.5A1.75 1.75 0 0 0 10 19.25v-3.99a.25.25 0 0 1 .25-.25h3.5a.25.25 0 0 1 .25.25v3.99c0 .966.784 1.75 1.75 1.75h3.5A1.75 1.75 0 0 0 21 19.25V9.463a1.75 1.75 0 0 0-.618-1.334l-7.25-6.152Z" />
        </BaseIcon>
    )
})

export const IconHogQL: IconComponent<IconProps> = forwardRef(function IconHogQL(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="m21.153 11.076-1.73 1.968.513 2.57-1.788.605v.527l-2.32 1.541.01.084c.045.4.105.93.18 1.059.113.183.242.328.39.434.154.106.32.18.497.221.177.042.36.063.55.063.253 0 .504-.054.753-.16.254-.106.481-.254.682-.443l1.002 1.489c-.373.313-.78.552-1.224.718a3.706 3.706 0 0 1-1.32.248 5.32 5.32 0 0 1-1.498-.195 2.993 2.993 0 0 1-1.126-.603 2.676 2.676 0 0 1-.718-1.02 3.43 3.43 0 0 1-.18-.566l-1.75 1.163-6.072-4.033v-.527l-1.787-.606.513-2.569L3 11.076l1.73-1.968-.513-2.57 2.482-.84.84-2.481 2.57.513L12.078 2l1.968 1.73 2.57-.513.84 2.482 2.48.84-.513 2.569 1.73 1.968Zm-8.553 8.1 4.503-2.99v-2.974c0-2.866-2.01-5.026-4.677-5.026h-.698c-2.666 0-4.677 2.16-4.677 5.026v2.974l4.503 2.99V17.05h-.462a.523.523 0 0 1 0-1.046h1.969a.523.523 0 0 1 0 1.046H12.6v2.128Zm-2.637-4.958c0 1.386-2.079 1.386-2.079 0s2.08-1.386 2.08 0Zm6.311 0c0 1.386-2.079 1.386-2.079 0s2.08-1.386 2.08 0Z"
            />
        </BaseIcon>
    )
})

export const IconHourglass: IconComponent<IconProps> = forwardRef(function IconHourglass(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M6.556 3.5h-.973C5.261 3.5 5 3.164 5 2.75S5.261 2 5.583 2h12.834c.322 0 .583.336.583.75s-.261.75-.583.75h-.973v3.953c0 .596-.236 1.152-.627 1.474L13.082 12l3.735 3.073c.391.322.627.877.627 1.474V20.5h.973c.322 0 .583.336.583.75s-.261.75-.583.75H5.583C5.261 22 5 21.664 5 21.25s.261-.75.583-.75h.973v-3.953c0-.597.236-1.152.627-1.474L10.918 12 7.183 8.927c-.391-.322-.627-.878-.627-1.474V3.5ZM7.722 7h8.556V3.5H7.722V7Zm8.556 9.547V18H7.722v-1.453c0-.085.034-.164.09-.21L12 12.89l4.188 3.447c.056.046.09.125.09.21Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconInfo: IconComponent<IconProps> = forwardRef(function IconInfo(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm8-1a.75.75 0 0 1 .75-.75H12a.75.75 0 0 1 .75.75v5.25a.75.75 0 0 1-1.5 0v-4.5h-.5A.75.75 0 0 1 10 11Z"
                clipRule="evenodd"
            />
            <path d="M11.5 8a.5.5 0 0 1 1 0 .5.5 0 0 1-1 0Z" />
            <path
                fillRule="evenodd"
                d="M12 8.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Zm0-1.25a.5.5 0 1 0 0 1 .5.5 0 0 0 0-1Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconIssue: IconComponent<IconProps> = forwardRef(function IconIssue(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Z"
                clipRule="evenodd"
            />
            <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        </BaseIcon>
    )
})

export const IconKeyboard: IconComponent<IconProps> = forwardRef(function IconKeyboard(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M6.5 10a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM6.5 14.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM19 10a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM19 14.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM10.5 10a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM15 10a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z" />
            <path
                fillRule="evenodd"
                d="M7 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm0 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM19.5 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm0 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM11 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm4.5 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-9.5.5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1ZM6 15a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Zm13-5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm-.5 5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Zm-8-5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm4 .5a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Z"
                clipRule="evenodd"
            />
            <path
                fillRule="evenodd"
                d="M1.25 7c0-.966.784-1.75 1.75-1.75h18.5c.966 0 1.75.784 1.75 1.75v10.5a1.75 1.75 0 0 1-1.75 1.75H3a1.75 1.75 0 0 1-1.75-1.75V7ZM3 6.75a.25.25 0 0 0-.25.25v10.5c0 .138.112.25.25.25h18.5a.25.25 0 0 0 .25-.25V7a.25.25 0 0 0-.25-.25H3Zm6.25 7.75a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5H10a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconLaptop: IconComponent<IconProps> = forwardRef(function IconLaptop(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3 5.75A2.75 2.75 0 0 1 5.75 3h12.5A2.75 2.75 0 0 1 21 5.75V16h1.25a.75.75 0 0 1 .75.75v1.5A2.75 2.75 0 0 1 20.25 21H3.75A2.75 2.75 0 0 1 1 18.25v-1.5a.75.75 0 0 1 .75-.75H3V5.75ZM4.5 16h15V5.75c0-.69-.56-1.25-1.25-1.25H5.75c-.69 0-1.25.56-1.25 1.25V16Zm-2 1.5v.75c0 .69.56 1.25 1.25 1.25h16.5c.69 0 1.25-.56 1.25-1.25v-.75h-19Z"
            />
        </BaseIcon>
    )
})

export const IconLeave: IconComponent<IconProps> = forwardRef(function IconLeave(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.75 4.5C4.61193 4.5 4.5 4.61193 4.5 4.75L4.5 19.25C4.5 19.3881 4.61193 19.5 4.75 19.5H11.25C11.6642 19.5 12 19.8358 12 20.25C12 20.6642 11.6642 21 11.25 21H4.75C3.7835 21 3 20.2165 3 19.25L3 4.75C3 3.7835 3.7835 3 4.75 3L11.25 3C11.6642 3 12 3.33579 12 3.75C12 4.16421 11.6642 4.5 11.25 4.5L4.75 4.5ZM15.2197 6.96967C15.5126 6.67678 15.9874 6.67678 16.2803 6.96967L20.7803 11.4697C21.0732 11.7626 21.0732 12.2374 20.7803 12.5303L16.2803 17.0303C15.9874 17.3232 15.5126 17.3232 15.2197 17.0303C14.9268 16.7374 14.9268 16.2626 15.2197 15.9697L18.4393 12.75L9 12.75C8.58579 12.75 8.25 12.4142 8.25 12C8.25 11.5858 8.58579 11.25 9 11.25L18.4393 11.25L15.2197 8.03033C14.9268 7.73744 14.9268 7.26256 15.2197 6.96967Z"
            />
        </BaseIcon>
    )
})

export const IconLetter: IconComponent<IconProps> = forwardRef(function IconLetter(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.75 3.5C5.61193 3.5 5.5 3.61193 5.5 3.75V10.8534L11.9229 12.9365C11.973 12.9528 12.027 12.9528 12.0771 12.9365L18.5 10.8534V3.75C18.5 3.61193 18.3881 3.5 18.25 3.5H5.75ZM20 10.3929V3.75C20 2.7835 19.2165 2 18.25 2H5.75C4.7835 2 4 2.7835 4 3.75V10.3929C2.96957 10.241 2 11.0387 2 12.1256V19.25C2 20.2165 2.7835 21 3.75 21H20.25C21.2165 21 22 20.2165 22 19.25V12.1256C22 11.0387 21.0304 10.241 20 10.3929ZM9 7.75C9 7.33579 9.33579 7 9.75 7H14.25C14.6642 7 15 7.33579 15 7.75C15 8.16421 14.6642 8.5 14.25 8.5H9.75C9.33579 8.5 9 8.16421 9 7.75ZM3.82712 11.8878C3.66562 11.8354 3.5 11.9558 3.5 12.1256V19.25C3.5 19.3881 3.61193 19.5 3.75 19.5H20.25C20.3881 19.5 20.5 19.3881 20.5 19.25V12.1256C20.5 11.9558 20.3344 11.8354 20.1729 11.8878L12.5399 14.3634C12.189 14.4772 11.811 14.4772 11.4601 14.3634L3.82712 11.8878Z"
            />
        </BaseIcon>
    )
})

export const IconLifecycle: IconComponent<IconProps> = forwardRef(function IconLifecycle(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M7 13.5H5v7h2v-7ZM3.5 12v9a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-9h-5ZM13 13.5h-2v4h2v-4ZM9.5 12v6a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-6h-5ZM19 11.5h-2v4h2v-4ZM15.5 10v6a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-6h-5Z"
                clipRule="evenodd"
            />
            <path d="M3.5 2a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v9h-5V2ZM9.5 5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v6h-5V5ZM15.5 2a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v7h-5V2Z" />
        </BaseIcon>
    )
})

export const IconLightBulb: IconComponent<IconProps> = forwardRef(function IconLightBulb(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.9991 3.5C8.40878 3.5 5.49823 6.41055 5.49823 10.0009C5.49823 12.1006 6.49288 13.9677 8.0398 15.1574C8.21999 15.296 8.4076 15.4253 8.60193 15.5447C9.08959 15.8443 9.45922 16.3712 9.49508 17H14.5032C14.539 16.3712 14.9086 15.8443 15.3963 15.5447C15.5906 15.4253 15.7782 15.296 15.9584 15.1574C17.5053 13.9677 18.5 12.1006 18.5 10.0009C18.5 6.41055 15.5895 3.5 11.9991 3.5ZM14.5003 18.5H9.49793V18.9988C9.49793 20.3802 10.6178 21.5 11.9991 21.5C13.3805 21.5 14.5003 20.3802 14.5003 18.9988V18.5ZM3.99823 10.0009C3.99823 5.58212 7.58035 2 11.9991 2C16.4179 2 20 5.58212 20 10.0009C20 12.5861 18.7731 14.885 16.8729 16.3465C16.6513 16.5169 16.4205 16.6759 16.1814 16.8228C16.0505 16.9032 16.0003 17.0173 16.0003 17.1005V18.9988C16.0003 21.2086 14.2089 23 11.9991 23C9.78932 23 7.99793 21.2086 7.99793 18.9988V17.1005C7.99793 17.0173 7.94769 16.9032 7.81683 16.8228C7.57769 16.6759 7.3469 16.5169 7.12534 16.3465C5.22513 14.885 3.99823 12.5861 3.99823 10.0009Z"
            />
        </BaseIcon>
    )
})

export const IconLineGraph: IconComponent<IconProps> = forwardRef(function IconLineGraph(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M22.712 6.159a.75.75 0 0 1 .13 1.053l-7.514 9.62a1.75 1.75 0 0 1-2.601.175l-5.622-5.484a.25.25 0 0 0-.362.014l-4.43 5.041a.75.75 0 0 1-1.126-.99l4.43-5.041a1.75 1.75 0 0 1 2.536-.098l5.621 5.484a.25.25 0 0 0 .372-.025l7.513-9.62a.75.75 0 0 1 1.053-.13Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconList: IconComponent<IconProps> = forwardRef(function IconList(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.75 6C5.05964 6 4.5 6.55964 4.5 7.25C4.5 7.94036 5.05964 8.5 5.75 8.5C6.44036 8.5 7 7.94036 7 7.25C7 6.55964 6.44036 6 5.75 6ZM3 7.25C3 5.73122 4.23122 4.5 5.75 4.5C7.26878 4.5 8.5 5.73122 8.5 7.25C8.5 8.76878 7.26878 10 5.75 10C4.23122 10 3 8.76878 3 7.25ZM11 7.25C11 6.83579 11.3358 6.5 11.75 6.5H20.25C20.6642 6.5 21 6.83579 21 7.25C21 7.66421 20.6642 8 20.25 8H11.75C11.3358 8 11 7.66421 11 7.25ZM5.75 15.5C5.05964 15.5 4.5 16.0596 4.5 16.75C4.5 17.4404 5.05964 18 5.75 18C6.44036 18 7 17.4404 7 16.75C7 16.0596 6.44036 15.5 5.75 15.5ZM3 16.75C3 15.2312 4.23122 14 5.75 14C7.26878 14 8.5 15.2312 8.5 16.75C8.5 18.2688 7.26878 19.5 5.75 19.5C4.23122 19.5 3 18.2688 3 16.75ZM11 16.75C11 16.3358 11.3358 16 11.75 16H20.25C20.6642 16 21 16.3358 21 16.75C21 17.1642 20.6642 17.5 20.25 17.5H11.75C11.3358 17.5 11 17.1642 11 16.75Z"
            />
        </BaseIcon>
    )
})

export const IconListCheck: IconComponent<IconProps> = forwardRef(function IconListCheck(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M11 7.75a.75.75 0 0 1 .75-.75h8.5a.75.75 0 0 1 0 1.5h-8.5a.75.75 0 0 1-.75-.75ZM5.75 15a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5ZM3 16.25a2.75 2.75 0 1 1 5.5 0 2.75 2.75 0 0 1-5.5 0ZM11 16.25a.75.75 0 0 1 .75-.75h8.5a.75.75 0 0 1 0 1.5h-8.5a.75.75 0 0 1-.75-.75ZM9.196 4.647a.75.75 0 0 1 .157 1.05l-3.334 4.5a.75.75 0 0 1-1.104.11l-1.667-1.5a.75.75 0 0 1 1.004-1.114l1.053.948 2.842-3.837a.75.75 0 0 1 1.05-.157Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconLive: IconComponent<IconProps> = forwardRef(function IconLive(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.98959 5.42963C6.28249 5.72252 6.28249 6.1974 5.98959 6.49029C4.45057 8.02931 3.5 10.1533 3.5 12.5007C3.5 14.8481 4.45057 16.9721 5.98959 18.5111C6.28249 18.804 6.28249 19.2789 5.98959 19.5718C5.6967 19.8647 5.22183 19.8647 4.92893 19.5718C3.12011 17.7629 2 15.2619 2 12.5007C2 9.73948 3.12011 7.23845 4.92893 5.42963C5.22183 5.13674 5.6967 5.13674 5.98959 5.42963ZM18.0104 5.42963C18.3033 5.13674 18.7782 5.13674 19.0711 5.42963C20.8799 7.23845 22 9.73948 22 12.5007C22 15.2619 20.8799 17.7629 19.0711 19.5718C18.7782 19.8647 18.3033 19.8647 18.0104 19.5718C17.7175 19.2789 17.7175 18.804 18.0104 18.5111C19.5494 16.9721 20.5 14.8481 20.5 12.5007C20.5 10.1533 19.5494 8.02931 18.0104 6.49029C17.7175 6.1974 17.7175 5.72252 18.0104 5.42963ZM8.89659 8.33663C9.18948 8.62952 9.18948 9.10439 8.89659 9.39729C8.10153 10.1923 7.61111 11.2885 7.61111 12.5007C7.61111 13.7129 8.10153 14.8091 8.89659 15.6041C9.18948 15.897 9.18948 16.3719 8.89659 16.6648C8.60369 16.9577 8.12882 16.9577 7.83593 16.6648C6.77107 15.5999 6.11111 14.1267 6.11111 12.5007C6.11111 10.8747 6.77107 9.40148 7.83593 8.33663C8.12882 8.04373 8.60369 8.04373 8.89659 8.33663ZM15.1034 8.33663C15.3963 8.04373 15.8712 8.04373 16.1641 8.33663C17.2289 9.40148 17.8889 10.8747 17.8889 12.5007C17.8889 14.1267 17.2289 15.5999 16.1641 16.6648C15.8712 16.9577 15.3963 16.9577 15.1034 16.6648C14.8105 16.3719 14.8105 15.897 15.1034 15.6041C15.8985 14.8091 16.3889 13.7129 16.3889 12.5007C16.3889 11.2885 15.8985 10.1923 15.1034 9.39729C14.8105 9.10439 14.8105 8.62952 15.1034 8.33663ZM10.5 12.5C10.5 11.6715 11.1716 11 12 11C12.8284 11 13.5 11.6715 13.5 12.5C13.5 13.3284 12.8284 14 12 14C11.1716 14 10.5 13.3284 10.5 12.5Z"
            />
        </BaseIcon>
    )
})

export const IconLlmObservability: IconComponent<IconProps> = forwardRef(
    function IconLlmObservability(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M12 9c-1.356 0-2.857.857-4.284 2.9a.174.174 0 0 0 0 .2C9.143 14.142 10.644 15 12 15c1.356 0 2.857-.857 4.284-2.9l.615.428-.615-.429a.174.174 0 0 0 0-.198C14.857 9.857 13.356 9 12 9Zm0-1.5c2.043 0 3.942 1.29 5.514 3.542.401.575.401 1.341 0 1.916C15.942 15.209 14.044 16.5 12 16.5c-2.043 0-3.942-1.29-5.514-3.542a1.674 1.674 0 0 1 0-1.916C8.058 8.791 9.956 7.5 12 7.5Z"
                    clipRule="evenodd"
                />
                <path
                    fillRule="evenodd"
                    d="M18.01 5.99A8.504 8.504 0 0 0 7.836 4.587a.75.75 0 1 1-.735-1.307c3.81-2.143 8.726-1.595 11.97 1.649 3.244 3.244 3.792 8.16 1.65 11.97a.75.75 0 1 1-1.308-.735A8.504 8.504 0 0 0 18.01 5.99ZM4.3 6.815a.75.75 0 0 1 .287 1.021 8.504 8.504 0 0 0 11.577 11.577.75.75 0 0 1 .735 1.308c-3.81 2.142-8.726 1.594-11.97-1.65-3.244-3.244-3.793-8.16-1.65-11.97a.75.75 0 0 1 1.022-.286ZM10.75 12a1.25 1.25 0 1 1 2.5 0 1.25 1.25 0 0 1-2.5 0Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconLlmPromptEvaluation: IconComponent<IconProps> = forwardRef(
    function IconLlmPromptEvaluation(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M4.75 4.453a.297.297 0 0 0-.297.297v3.5a.703.703 0 1 1-1.406 0v-3.5c0-.94.762-1.703 1.703-1.703h3.5a.703.703 0 1 1 0 1.406h-3.5Zm10.297-.703c0-.388.315-.703.703-.703h3.5c.94 0 1.703.762 1.703 1.703v3.5a.703.703 0 1 1-1.406 0v-3.5a.297.297 0 0 0-.297-.297h-3.5a.703.703 0 0 1-.703-.703ZM3.75 15.047c.388 0 .703.315.703.703v3.5c0 .164.133.297.297.297h3.5a.703.703 0 1 1 0 1.406h-3.5c-.94 0-1.703-.762-1.703-1.703v-3.5c0-.388.315-.703.703-.703Zm16.5 0c.388 0 .703.315.703.703v3.5c0 .94-.762 1.703-1.703 1.703h-3.5a.703.703 0 0 1 0-1.406h3.5a.297.297 0 0 0 .297-.297v-3.5c0-.388.315-.703.703-.703ZM12.25 7a.75.75 0 0 1 .67.415l1.389 2.776 2.776 1.388a.75.75 0 0 1 0 1.342l-2.776 1.388-1.388 2.776a.75.75 0 0 1-1.342 0l-1.388-2.776-2.776-1.388a.75.75 0 0 1 0-1.342l2.776-1.388 1.388-2.776A.75.75 0 0 1 12.25 7Zm0 2.427-.83 1.658a.75.75 0 0 1-.335.336l-1.658.829 1.658.83a.75.75 0 0 1 .336.335l.829 1.658.83-1.658a.75.75 0 0 1 .335-.336l1.658-.829-1.658-.83a.75.75 0 0 1-.336-.335l-.829-1.658Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconLlmPromptManagement: IconComponent<IconProps> = forwardRef(
    function IconLlmPromptManagement(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M2 5.75C2 4.784 2.784 4 3.75 4h16.5c.966 0 1.75.784 1.75 1.75v2.5a.75.75 0 0 1-1.5 0v-2.5a.25.25 0 0 0-.25-.25H3.75a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h6.5a.75.75 0 0 1 0 1.5h-6.5A1.75 1.75 0 0 1 2 18.25V5.75ZM18 11a.75.75 0 0 1 .75.75c0 1.384.307 2.21.799 2.701.491.492 1.317.799 2.701.799a.75.75 0 0 1 0 1.5c-1.384 0-2.21.307-2.701.799-.492.491-.799 1.317-.799 2.701a.75.75 0 0 1-1.5 0c0-1.384-.307-2.21-.799-2.701-.491-.492-1.317-.799-2.701-.799a.75.75 0 0 1 0-1.5c1.384 0 2.21-.307 2.701-.799.492-.491.799-1.317.799-2.701A.75.75 0 0 1 18 11Zm0 3.904A3.585 3.585 0 0 1 16.904 16 3.585 3.585 0 0 1 18 17.096 3.585 3.585 0 0 1 19.096 16 3.585 3.585 0 0 1 18 14.904Z"
                    clipRule="evenodd"
                />
                <path d="M7 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM10 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM12 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" />
            </BaseIcon>
        )
    }
)

export const IconLoading: IconComponent<IconProps> = forwardRef(function IconLoading(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M2.581 10.265a.59.59 0 0 0-.586.587v3.199a.587.587 0 0 0 .586.59h18.823a.588.588 0 0 0 .59-.59v-3.2a.589.589 0 0 0-.59-.586H2.58Zm.592 1.177h17.645v2.022l-17.645.001v-2.023Zm1.028.42a.587.587 0 1 0 0 1.178h8.476a.588.588 0 0 0 0-1.177H4.2Z" />
        </BaseIcon>
    )
})

export const IconLock: IconComponent<IconProps> = forwardRef(function IconLock(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M7 7.25a5 5 0 0 1 10 0V9h1.25c.966 0 1.75.784 1.75 1.75v9.5A1.75 1.75 0 0 1 18.25 22H5.75A1.75 1.75 0 0 1 4 20.25v-9.5C4 9.784 4.784 9 5.75 9H7V7.25ZM5.75 10.5a.25.25 0 0 0-.25.25v9.5c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25v-9.5a.25.25 0 0 0-.25-.25H5.75ZM15.5 9h-7V7.25a3.5 3.5 0 1 1 7 0V9ZM12 13.25a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3a.75.75 0 0 1 .75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconLogomark: IconComponent<IconProps> = forwardRef(function IconLogomark(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M2.751 14.926a.44.44 0 0 0-.751.31v2.017c0 .243.197.44.44.44h2.016a.44.44 0 0 0 .311-.751l-2.016-2.016Zm-.622-1.502A.44.44 0 0 1 2 13.113v-2.274a.44.44 0 0 1 .751-.311l6.414 6.414a.44.44 0 0 1-.31.75H6.58a.44.44 0 0 1-.31-.128l-4.141-4.14Zm0-4.398A.44.44 0 0 1 2 8.715V6.44a.44.44 0 0 1 .751-.311l10.812 10.812a.44.44 0 0 1-.31.75h-2.275a.44.44 0 0 1-.311-.128L2.129 9.026Zm4.398 0a.44.44 0 0 1-.13-.311V6.44a.44.44 0 0 1 .752-.311l7.916 7.916a.44.44 0 0 1 .129.31v2.275a.44.44 0 0 1-.751.31L6.527 9.027Zm4.269-2.585a.44.44 0 0 1 .75-.311l3.519 3.518a.44.44 0 0 1 .129.31v2.275a.44.44 0 0 1-.751.311l-3.518-3.518a.44.44 0 0 1-.13-.311V6.44Zm5.77 4.709 4.142 4.14c.532.533 1.224.87 1.963.967.241.031.439.226.439.468v.528a.44.44 0 0 1-.44.44h-6.414a.44.44 0 0 1-.44-.44v-5.792a.44.44 0 0 1 .75-.311Zm.657 4.432a.704.704 0 1 0 1.408 0 .704.704 0 0 0-1.408 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconSparkles: IconComponent<IconProps> = forwardRef(function IconSparkles(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M13.75 7.75a.75.75 0 0 0-1.5 0c0 2.426-.536 4.001-1.517 4.983-.982.981-2.557 1.517-4.983 1.517a.75.75 0 0 0 0 1.5c2.426 0 4.001.536 4.983 1.517.981.982 1.517 2.557 1.517 4.983a.75.75 0 0 0 1.5 0c0-2.426.536-4.001 1.517-4.983.982-.981 2.557-1.517 4.983-1.517a.75.75 0 0 0 0-1.5c-2.426 0-4.001-.536-4.983-1.517-.981-.982-1.517-2.557-1.517-4.983ZM6 5.5a.5.5 0 0 0-1 0c0 .98-.217 1.573-.572 1.928S3.48 8 2.5 8a.5.5 0 0 0 0 1c.98 0 1.573.217 1.928.572S5 10.52 5 11.5a.5.5 0 0 0 1 0c0-.98.217-1.573.572-1.928S7.52 9 8.5 9a.5.5 0 0 0 0-1c-.98 0-1.573-.217-1.928-.572S6 6.48 6 5.5ZM11 1.5a.5.5 0 0 0-1 0c0 .633-.14.975-.333 1.167C9.475 2.86 9.133 3 8.5 3a.5.5 0 0 0 0 1c.633 0 .975.14 1.167.333.192.192.333.534.333 1.167a.5.5 0 0 0 1 0c0-.633.14-.975.333-1.167C11.525 4.14 11.867 4 12.5 4a.5.5 0 0 0 0-1c-.633 0-.975-.14-1.167-.333C11.14 2.475 11 2.133 11 1.5Z" />
        </BaseIcon>
    )
})

export const IconMagic: IconComponent<IconProps> = forwardRef(function IconMagic(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M5 13.163a5.585 5.585 0 0 1-.552-.223 3.094 3.094 0 0 1-.844-.565C3.335 12.113 3 11.65 3 11s.335-1.112.604-1.375c.266-.26.578-.439.844-.565.54-.255 1.227-.446 1.955-.591C7.883 8.173 9.86 8 12 8c2.139 0 4.117.173 5.598.469.727.145 1.413.336 1.954.591.266.126.578.305.844.565.269.263.604.726.604 1.375 0 .65-.335 1.113-.604 1.375-.266.26-.578.44-.844.565a5.58 5.58 0 0 1-.552.223V19c0 .615-.348 1.1-.755 1.447-.41.349-.959.63-1.572.85C15.443 21.74 13.79 22 12 22c-1.79 0-3.442-.26-4.673-.703-.613-.22-1.162-.501-1.572-.85C5.348 20.101 5 19.615 5 19v-5.837Zm13.332-1.359h.003c.738-.233 1.165-.508 1.165-.804 0-.295-.427-.57-1.165-.803l-.003-.001C17.002 9.778 14.662 9.5 12 9.5c-2.663 0-5.001.278-6.332.696l-.003.001c-.738.232-1.165.508-1.165.803 0 .296.427.571 1.165.803l.003.001c1.33.419 3.67.696 6.332.696 2.663 0 5.001-.277 6.332-.696Zm-.832 1.747c-1.47.284-3.408.45-5.5.45s-4.03-.166-5.5-.45V19c0 .006.002.112.227.304.223.19.59.394 1.108.58 1.03.371 2.502.615 4.165.615 1.663 0 3.135-.244 4.165-.614.519-.187.885-.392 1.108-.581.225-.192.227-.298.227-.305v-5.45Z"
                clipRule="evenodd"
            />
            <path d="M13.553 1.895a.5.5 0 0 1 .894 0c.496.991.667 1.162 1.659 1.658a.5.5 0 0 1 0 .894c-.992.496-1.163.667-1.659 1.659a.5.5 0 0 1-.894 0c-.496-.992-.667-1.163-1.659-1.659a.5.5 0 0 1 0-.894c.992-.496 1.163-.667 1.659-1.658ZM8.553 3.895a.5.5 0 0 1 .894 0c.174.348.31.484.659.658a.5.5 0 0 1 0 .894c-.349.175-.485.31-.659.659a.5.5 0 0 1-.894 0c-.174-.349-.31-.484-.659-.659a.5.5 0 0 1 0-.894c.349-.174.485-.31.659-.658Z" />
        </BaseIcon>
    )
})

export const IconMagicWand: IconComponent<IconProps> = forwardRef(function IconMagicWand(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M17.512 2.927a1.75 1.75 0 0 1 2.475 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474L6.487 21.073a1.75 1.75 0 0 1-2.475 0l-1.085-1.086a1.75 1.75 0 0 1 0-2.474L17.512 2.927Zm1.415 1.06a.25.25 0 0 0-.354 0L15.31 7.25l1.44 1.44 3.262-3.263a.25.25 0 0 0 0-.354l-1.085-1.086ZM15.689 9.75l-1.44-1.44L3.988 18.574a.25.25 0 0 0 0 .354l1.086 1.086a.25.25 0 0 0 .354 0L15.689 9.75Z"
                clipRule="evenodd"
            />
            <path d="M9.85 2.075a.5.5 0 0 0 .223-.224l.479-.956a.5.5 0 0 1 .894 0l.479.956a.5.5 0 0 0 .223.224l.957.478a.5.5 0 0 1 0 .894l-.957.479a.5.5 0 0 0-.223.223l-.479.957a.5.5 0 0 1-.894 0l-.479-.957a.5.5 0 0 0-.223-.223l-.957-.479a.5.5 0 0 1 0-.894l.957-.478ZM18.85 13.075a.5.5 0 0 0 .223-.224l.479-.956a.5.5 0 0 1 .894 0l.478.956a.5.5 0 0 0 .224.224l.957.478a.5.5 0 0 1 0 .894l-.957.479a.5.5 0 0 0-.224.223l-.478.957a.5.5 0 0 1-.894 0l-.479-.957a.5.5 0 0 0-.223-.223l-.957-.479a.5.5 0 0 1 0-.894l.957-.478ZM4.85 7.075a.5.5 0 0 0 .224-.224l.478-.956a.5.5 0 0 1 .894 0l.478.956a.5.5 0 0 0 .224.224l.957.478a.5.5 0 0 1 0 .894l-.957.479a.5.5 0 0 0-.224.223l-.478.957a.5.5 0 0 1-.894 0l-.478-.957a.5.5 0 0 0-.224-.223l-.957-.479a.5.5 0 0 1 0-.894l.957-.478Z" />
        </BaseIcon>
    )
})

export const IconMap: IconComponent<IconProps> = forwardRef(function IconMap(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 6.25A3.25 3.25 0 0 1 5.25 3h2.5v3h3a.75.75 0 0 1 0 1.5h-3v7.75A.75.75 0 0 1 7 16H5.25a1.75 1.75 0 1 0 0 3.5h14a.25.25 0 0 0 .25-.25v-6.5a.75.75 0 0 1 1.5 0v6.5A1.75 1.75 0 0 1 19.25 21h-14A3.25 3.25 0 0 1 2 17.75V6.25Zm1.5 8.76a3.235 3.235 0 0 1 1.75-.51h1v-10h-1A1.75 1.75 0 0 0 3.5 6.25v8.76ZM17 4.5c-1.389 0-2.5 1.111-2.5 2.464 0 1.025.626 1.91 1.379 2.592A7.483 7.483 0 0 0 17 10.39l.107-.067a7.483 7.483 0 0 0 1.014-.767c.753-.682 1.379-1.567 1.379-2.592C19.5 5.611 18.389 4.5 17 4.5Zm0 6.75-.333.672h-.002l-.003-.003-.01-.005-.036-.018a8.14 8.14 0 0 1-.525-.305 8.977 8.977 0 0 1-1.22-.924C14 9.877 13 8.617 13 6.964 13 4.767 14.799 3 17 3s4 1.767 4 3.964c0 1.654-.999 2.912-1.871 3.703a8.977 8.977 0 0 1-1.745 1.23l-.035.017-.01.005-.004.002h-.002L17 11.25Zm0 0 .332.672a.751.751 0 0 1-.665 0L17 11.25Z"
            />
        </BaseIcon>
    )
})

export const IconMapPin: IconComponent<IconProps> = forwardRef(function IconMapPin(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 3.5A6.5 6.5 0 0 0 5.5 10c0 2.276 1.13 4.5 2.546 6.378 1.405 1.865 3.025 3.3 3.857 3.984A.15.15 0 0 0 12 20.4a.15.15 0 0 0 .097-.038c.832-.683 2.452-2.119 3.857-3.984C17.37 14.5 18.5 12.276 18.5 10A6.5 6.5 0 0 0 12 3.5ZM4 10a8 8 0 1 1 16 0c0 2.752-1.352 5.296-2.848 7.28-1.505 1.998-3.224 3.52-4.104 4.242a1.642 1.642 0 0 1-2.096 0c-.88-.722-2.599-2.244-4.104-4.241C5.352 15.296 4 12.75 4 10Zm8-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm-3.5 2a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconMarkdown: IconComponent<IconProps> = forwardRef(function IconMarkdown(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M2 5.75C2 4.784 2.784 4 3.75 4h16.5c.966 0 1.75.784 1.75 1.75v12.5A1.75 1.75 0 0 1 20.25 20H3.75A1.75 1.75 0 0 1 2 18.25V5.75Zm1.75-.25a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h16.5a.25.25 0 0 0 .25-.25V5.75a.25.25 0 0 0-.25-.25H3.75Zm2.713 3.557a.75.75 0 0 1 .817.163L9 10.94l1.72-1.72a.75.75 0 0 1 1.28.53v4.5a.75.75 0 0 1-1.5 0v-2.69l-.97.97a.75.75 0 0 1-1.06 0l-.97-.97v2.69a.75.75 0 0 1-1.5 0v-4.5a.75.75 0 0 1 .463-.693ZM15.75 9a.75.75 0 0 1 .75.75v2.69l.47-.47a.75.75 0 1 1 1.06 1.06l-1.75 1.75a.75.75 0 0 1-1.06 0l-1.75-1.75a.75.75 0 1 1 1.06-1.06l.47.47V9.75a.75.75 0 0 1 .75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconMarkdownFilled: IconComponent<IconProps> = forwardRef(
    function IconMarkdownFilled(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M3.75 4A1.75 1.75 0 0 0 2 5.75v12.5c0 .966.784 1.75 1.75 1.75h16.5A1.75 1.75 0 0 0 22 18.25V5.75A1.75 1.75 0 0 0 20.25 4H3.75Zm3.53 5.22A.75.75 0 0 0 6 9.75v4.5a.75.75 0 0 0 1.5 0v-2.69l.97.97a.75.75 0 0 0 1.06 0l.97-.97v2.69a.75.75 0 0 0 1.5 0v-4.5a.75.75 0 0 0-1.28-.53L9 10.94 7.28 9.22Zm9.22.53a.75.75 0 0 0-1.5 0v2.69l-.47-.47a.75.75 0 1 0-1.06 1.06l1.75 1.75a.75.75 0 0 0 1.06 0l1.75-1.75a.75.75 0 1 0-1.06-1.06l-.47.47V9.75Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconMegaphone: IconComponent<IconProps> = forwardRef(function IconMegaphone(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M16.732 2.433a1.75 1.75 0 0 1 2.266 1.672v3.22a3.751 3.751 0 0 1 0 7.35v3.22a1.75 1.75 0 0 1-2.267 1.672l-3.448-1.065a3.751 3.751 0 0 1-7.285-1.252v-.997l-2.766-.854a1.75 1.75 0 0 1-1.234-1.673V8.274c0-.768.5-1.446 1.234-1.673l3.059-.944a.747.747 0 0 1 .504-.156l9.937-3.068ZM6 7.316l-2.326.719a.25.25 0 0 0-.176.239v5.452c0 .11.071.207.176.24L6 14.683V7.317Zm1.5 7.83V6.854l9.674-2.987a.25.25 0 0 1 .324.24v13.789a.25.25 0 0 1-.324.239L7.5 15.147Zm-.002 1.57 4.35 1.343a2.251 2.251 0 0 1-4.35-.809v-.534Zm11.5-3.594a2.251 2.251 0 0 0 0-4.244v4.244Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconMessage: IconComponent<IconProps> = forwardRef(function IconMessage(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.409 5.366C6.273 3.806 8.899 3 12 3s5.727.805 7.591 2.366C21.471 6.941 22.5 9.223 22.5 12s-1.03 5.059-2.909 6.634C17.727 20.194 15.101 21 12 21c-1.693 0-3.57-.162-5.234-.922a6.967 6.967 0 0 1-1.223.568c-.941.328-2.282.548-3.615-.086a.75.75 0 0 1-.277-1.129c1.443-1.912 1.015-3.08.997-3.13l-.006-.014-.008-.019-.012-.028a22.19 22.19 0 0 1-.608-1.665c-.25-.782-.514-1.78-.514-2.575 0-2.777 1.03-5.059 2.909-6.634Zm1.864 13.249a.75.75 0 0 1 .795-.06c1.426.754 3.17.945 4.932.945 2.857 0 5.106-.742 6.628-2.016C20.133 16.222 21 14.379 21 12c0-2.38-.867-4.223-2.372-5.484C17.106 5.242 14.857 4.5 12 4.5s-5.106.742-6.628 2.016C3.867 7.777 3 9.62 3 12c0 .542.196 1.346.443 2.12.239.749.496 1.38.564 1.545l.002.004c.007.017.03.071.047.116.157.427.468 1.776-.618 3.663.56.06 1.117-.046 1.611-.219a5.462 5.462 0 0 0 1.212-.606l.012-.008Zm.001-.001Z"
            />
            <path d="M7.5 12.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm4.5 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm4.5 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Z" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.5 13a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm4.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm4.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm-9-.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm4.5 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm4-.5a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0Z"
            />
        </BaseIcon>
    )
})

export const IconMicrophone: IconComponent<IconProps> = forwardRef(function IconMicrophone(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M7 7a5 5 0 0 1 10 0v4.5a5 5 0 0 1-10 0V7Zm5-3.5A3.5 3.5 0 0 0 8.5 7v4.5a3.5 3.5 0 1 0 7 0V7A3.5 3.5 0 0 0 12 3.5ZM4.824 13.813a.75.75 0 0 1 .987.387 6.752 6.752 0 0 0 12.378 0 .75.75 0 0 1 1.374.6 8.256 8.256 0 0 1-6.813 4.916v1.534a.75.75 0 0 1-1.5 0v-1.534A8.256 8.256 0 0 1 4.437 14.8a.75.75 0 0 1 .387-.987Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconMinus: IconComponent<IconProps> = forwardRef(function IconMinus(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M3 12a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 12Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconMinusSmall: IconComponent<IconProps> = forwardRef(function IconMinusSmall(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M6 12a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H6.75A.75.75 0 0 1 6 12Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconMinusSquare: IconComponent<IconProps> = forwardRef(function IconMinusSquare(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="m4.296 20.141.34-.668-.34.668Zm-.437-.437-.668.34.668-.34Zm16.282 0-.668-.34.668.34Zm-.437.437-.34-.668.34.668Zm.437-15.845-.668.34.668-.34Zm-.437-.437.34-.668-.34.668Zm-15.845.437-.668-.34.668.34Zm.437-.437-.34-.668.34.668ZM15.25 12.75a.75.75 0 0 0 0-1.5v1.5Zm-6.493-1.5a.75.75 0 0 0 0 1.5v-1.5ZM19.5 5.35v13.3H21V5.35h-1.5Zm-.85 14.15H5.35V21h13.3v-1.5ZM4.5 18.65V5.35H3v13.3h1.5ZM5.35 4.5h13.3V3H5.35v1.5Zm0 15c-.292 0-.467 0-.596-.011-.12-.01-.134-.025-.117-.016l-.681 1.336c.23.118.463.157.676.175.205.017.45.016.718.016v-1.5ZM3 18.65c0 .268 0 .514.016.718.018.213.057.446.175.677l1.336-.682c.009.017-.006.003-.016-.117a8.337 8.337 0 0 1-.011-.596H3Zm1.636.823a.25.25 0 0 1-.109-.11l-1.336.681c.168.33.435.598.765.765l.68-1.336ZM19.5 18.65c0 .292 0 .467-.011.596-.01.12-.025.134-.016.117l1.336.681c.118-.23.157-.463.175-.676.017-.204.016-.45.016-.718h-1.5ZM18.65 21c.268 0 .514 0 .718-.016.213-.018.446-.057.677-.175l-.682-1.336c.017-.009.003.006-.117.016-.13.01-.304.011-.596.011V21Zm.823-1.637a.251.251 0 0 1-.11.11l.681 1.336a1.75 1.75 0 0 0 .765-.765l-1.336-.68ZM21 5.35c0-.268 0-.513-.016-.718a1.775 1.775 0 0 0-.175-.676l-1.336.68c-.009-.016.006-.002.016.118.01.13.011.304.011.596H21Zm-2.35-.85c.292 0 .467 0 .596.011.12.01.134.025.117.016l.681-1.336a1.774 1.774 0 0 0-.676-.175C19.164 3 18.918 3 18.65 3v1.5Zm2.16-.544a1.75 1.75 0 0 0-.765-.765l-.682 1.336a.25.25 0 0 1 .11.11l1.336-.681ZM4.5 5.35c0-.292 0-.467.011-.596.01-.12.025-.134.016-.117l-1.336-.681c-.118.23-.157.463-.175.676C3 4.837 3 5.082 3 5.35h1.5ZM5.35 3c-.268 0-.513 0-.718.016a1.774 1.774 0 0 0-.676.175l.68 1.336c-.016.009-.002-.006.118-.016.13-.01.304-.011.596-.011V3Zm-.823 1.636a.25.25 0 0 1 .11-.109l-.681-1.336a1.75 1.75 0 0 0-.765.765l1.336.68ZM15.25 11.25H8.757v1.5h6.493v-1.5Z" />
        </BaseIcon>
    )
})

export const IconMouse: IconComponent<IconProps> = forwardRef(function IconMouse(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M5 9a7 7 0 0 1 14 0v6a7 7 0 1 1-14 0V9Zm7-5.5A5.5 5.5 0 0 0 6.5 9v6a5.5 5.5 0 1 0 11 0V9A5.5 5.5 0 0 0 12 3.5ZM12 6a.75.75 0 0 1 .75.75v2a.75.75 0 0 1-1.5 0v-2A.75.75 0 0 1 12 6Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconMouseScrollDown: IconComponent<IconProps> = forwardRef(
    function IconMouseScrollDown(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M6 7a6 6 0 1 1 12 0v5a6 6 0 0 1-12 0V7Zm6-4.5A4.5 4.5 0 0 0 7.5 7v5a4.5 4.5 0 1 0 9 0V7A4.5 4.5 0 0 0 12 2.5ZM12 5a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5A.75.75 0 0 1 12 5ZM8.376 19.834a.75.75 0 0 1 1.04-.208l2.445 1.63a.25.25 0 0 0 .278 0l2.445-1.63a.75.75 0 1 1 .832 1.248l-2.445 1.63a1.75 1.75 0 0 1-1.942 0l-2.445-1.63a.75.75 0 0 1-.208-1.04Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconMultiply: IconComponent<IconProps> = forwardRef(function IconMultiply(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.22 7.22a.75.75 0 0 1 1.06 0L12 10.94l3.72-3.72a.75.75 0 1 1 1.06 1.06L13.06 12l3.72 3.72a.75.75 0 1 1-1.06 1.06L12 13.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06L10.94 12 7.22 8.28a.75.75 0 0 1 0-1.06Z"
            />
        </BaseIcon>
    )
})

export const IconNewspaper: IconComponent<IconProps> = forwardRef(function IconNewspaper(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 4.75C2 3.784 2.784 3 3.75 3h11.5c.966 0 1.75.784 1.75 1.75v6.5h3.25c.966 0 1.75.784 1.75 1.75v4.75a3.25 3.25 0 0 1-3.419 3.246.773.773 0 0 1-.081.004H5.25A3.25 3.25 0 0 1 2 17.75v-13ZM16.01 19.5a3.235 3.235 0 0 1-.51-1.75v-13a.25.25 0 0 0-.25-.25H3.75a.25.25 0 0 0-.25.25v13c0 .966.784 1.75 1.75 1.75h10.76Zm.99-6.75v5a1.75 1.75 0 1 0 3.5 0V13a.25.25 0 0 0-.25-.25H17Zm-11-5A.75.75 0 0 1 6.75 7h5.5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-.75.75h-5.5a.75.75 0 0 1-.75-.75v-4.5Zm1.5.75v3h4v-3h-4ZM6 15.75a.75.75 0 0 1 .75-.75h5.5a.75.75 0 0 1 0 1.5h-5.5a.75.75 0 0 1-.75-.75Z"
            />
        </BaseIcon>
    )
})

export const IconNotebook: IconComponent<IconProps> = forwardRef(function IconNotebook(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.5 4.25C3.5 3.2835 4.2835 2.5 5.25 2.5H18.75C19.7165 2.5 20.5 3.2835 20.5 4.25V19.75C20.5 20.7165 19.7165 21.5 18.75 21.5H5.25C4.2835 21.5 3.5 20.7165 3.5 19.75V16.5H3.25C2.83579 16.5 2.5 16.1642 2.5 15.75C2.5 15.3358 2.83579 15 3.25 15H3.5V12.75H3.25C2.83579 12.75 2.5 12.4142 2.5 12C2.5 11.5858 2.83579 11.25 3.25 11.25H3.5V9H3.25C2.83579 9 2.5 8.66421 2.5 8.25C2.5 7.83579 2.83579 7.5 3.25 7.5H3.5V4.25ZM5 9H5.25C5.66421 9 6 8.66421 6 8.25C6 7.83579 5.66421 7.5 5.25 7.5H5V4.25C5 4.11193 5.11193 4 5.25 4H18.75C18.8881 4 19 4.11193 19 4.25V19.75C19 19.8881 18.8881 20 18.75 20H5.25C5.11193 20 5 19.8881 5 19.75V16.5H5.25C5.66421 16.5 6 16.1642 6 15.75C6 15.3358 5.66421 15 5.25 15H5V12.75H5.25C5.66421 12.75 6 12.4142 6 12C6 11.5858 5.66421 11.25 5.25 11.25H5V9ZM9 8.25C9 7.83579 9.33579 7.5 9.75 7.5H14.25C14.6642 7.5 15 7.83579 15 8.25C15 8.66421 14.6642 9 14.25 9H9.75C9.33579 9 9 8.66421 9 8.25ZM9 12C9 11.5858 9.33579 11.25 9.75 11.25H12.25C12.6642 11.25 13 11.5858 13 12C13 12.4142 12.6642 12.75 12.25 12.75H9.75C9.33579 12.75 9 12.4142 9 12Z"
            />
        </BaseIcon>
    )
})

export const IconNotification: IconComponent<IconProps> = forwardRef(
    function IconNotification(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M19.7678 4.23223C18.7915 3.25592 17.2085 3.25592 16.2322 4.23223C15.2559 5.20854 15.2559 6.79146 16.2322 7.76777C17.2085 8.74408 18.7915 8.74408 19.7678 7.76777C20.7441 6.79146 20.7441 5.20854 19.7678 4.23223ZM15.1716 3.17157C16.7337 1.60948 19.2663 1.60948 20.8284 3.17157C22.3905 4.73367 22.3905 7.26633 20.8284 8.82843C19.2663 10.3905 16.7337 10.3905 15.1716 8.82843C13.6095 7.26633 13.6095 4.73367 15.1716 3.17157ZM4.75 4.5C4.61193 4.5 4.5 4.61193 4.5 4.75V19.25C4.5 19.3881 4.61193 19.5 4.75 19.5H19.25C19.3881 19.5 19.5 19.3881 19.5 19.25V12.25C19.5 11.8358 19.8358 11.5 20.25 11.5C20.6642 11.5 21 11.8358 21 12.25V19.25C21 20.2165 20.2165 21 19.25 21H4.75C3.7835 21 3 20.2165 3 19.25V4.75C3 3.7835 3.7835 3 4.75 3H11.75C12.1642 3 12.5 3.33579 12.5 3.75C12.5 4.16421 12.1642 4.5 11.75 4.5H4.75Z"
                />
            </BaseIcon>
        )
    }
)

export const IconNight: IconComponent<IconProps> = forwardRef(function IconNight(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <g>
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.8644 4.0752C6.70688 4.63028 3.5 8.19064 3.5 12.5C3.5 17.1944 7.30558 21 12 21C16.3094 21 19.8697 17.7931 20.4248 13.6356C19.5298 14.0306 18.54 14.25 17.5 14.25C13.4959 14.25 10.25 11.0041 10.25 7C10.25 5.95996 10.4694 4.97015 10.8644 4.0752ZM2 12.5C2 6.97715 6.47715 2.5 12 2.5C12.0681 2.5 12.136 2.50068 12.2038 2.50204C12.4814 2.5076 12.7331 2.66599 12.8583 2.91377C12.9834 3.16155 12.9615 3.45819 12.8012 3.68485C12.139 4.62157 11.75 5.76425 11.75 7C11.75 10.1756 14.3244 12.75 17.5 12.75C18.7358 12.75 19.8784 12.361 20.8151 11.6988C21.0418 11.5385 21.3384 11.5166 21.5862 11.6417C21.834 11.7669 21.9924 12.0186 21.998 12.2962C21.9993 12.3639 22 12.4319 22 12.5C22 18.0228 17.5228 22.5 12 22.5C6.47715 22.5 2 18.0228 2 12.5Z"
                />
                <path d="M16.2404 5.51756L17.052 3.89439C17.2363 3.52587 17.7622 3.52587 17.9464 3.89439L18.758 5.51756C18.8064 5.61432 18.8849 5.69278 18.9816 5.74117L20.6048 6.55275C20.9733 6.73701 20.9733 7.26292 20.6048 7.44718L18.9816 8.25876C18.8849 8.30714 18.8064 8.3856 18.758 8.48237L17.9464 10.1055C17.7622 10.4741 17.2363 10.4741 17.052 10.1055L16.2404 8.48237C16.1921 8.3856 16.1136 8.30714 16.0168 8.25876L14.3937 7.44718C14.0251 7.26292 14.0251 6.73701 14.3937 6.55275L16.0168 5.74117C16.1136 5.69278 16.1921 5.61432 16.2404 5.51756Z" />
            </g>
        </BaseIcon>
    )
})

export const IconOpenSidebar: IconComponent<IconProps> = forwardRef(function IconOpenSidebar(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M13 19.25V20a.75.75 0 0 0 .75-.75H13Zm7.5-9a.75.75 0 0 0 1.5 0h-1.5Zm-17 8V5.75H2v12.5h1.5Zm9.5.25H3.75V20H13v-1.5Zm7.5-12.75v4.5H22v-4.5h-1.5ZM3.75 5.5H13V4H3.75v1.5Zm9.25 0h7.25V4H13v1.5Zm.75 13.75V4.75h-1.5v14.5h1.5ZM22 5.75A1.75 1.75 0 0 0 20.25 4v1.5a.25.25 0 0 1 .25.25H22Zm-18.5 0a.25.25 0 0 1 .25-.25V4A1.75 1.75 0 0 0 2 5.75h1.5ZM2 18.25c0 .966.784 1.75 1.75 1.75v-1.5a.25.25 0 0 1-.25-.25H2Z" />
            <path
                fillRule="evenodd"
                d="M16.25 14a.75.75 0 0 1 .75-.75h4a.75.75 0 0 1 0 1.5h-4a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
            />
            <path
                fillRule="evenodd"
                d="M21 18.75a.75.75 0 0 1-.75-.75v-4a.75.75 0 0 1 1.5 0v4a.75.75 0 0 1-.75.75Z"
                clipRule="evenodd"
            />
            <path
                fillRule="evenodd"
                d="M15.47 19.53a.75.75 0 0 1 0-1.06l5-5a.75.75 0 1 1 1.06 1.06l-5 5a.75.75 0 0 1-1.06 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPageChart: IconComponent<IconProps> = forwardRef(function IconPageChart(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M4 3.75C4 2.784 4.784 2 5.75 2h6.586c.464 0 .909.184 1.237.513M5.75 3.5a.25.25 0 0 0-.25.25v16.5c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25V10h-4.75A1.75 1.75 0 0 1 12 8.25V3.5H5.75Zm7.75 1.06v3.69c0 .138.112.25.25.25h3.69L13.5 4.56Zm.073-2.047 5.914 5.914c.329.328.513.773.513 1.237V20.25A1.75 1.75 0 0 1 18.25 22H5.75A1.75 1.75 0 0 1 4 20.25V3.75M12 12a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 12 12Zm3.5 2a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 .75-.75Zm-7 1a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 8.5 15Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPalette: IconComponent<IconProps> = forwardRef(function IconPalette(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 4c-4.734 0-8.5 3.62-8.5 8s3.766 8 8.5 8c.107 0 .193-.004.262-.01a6.097 6.097 0 0 0-.216-.425l-.103-.186c-.22-.396-.502-.906-.679-1.43-.211-.627-.317-1.401.065-2.166.607-1.217 1.772-1.475 2.826-1.46.529.008 1.085.083 1.617.167.183.028.362.058.537.087.352.058.691.114 1.034.16 1.055.137 1.808.108 2.298-.198.416-.26.859-.872.859-2.539 0-4.38-3.766-8-8.5-8ZM2 12c0-5.285 4.517-9.5 10-9.5S22 6.715 22 12c0 1.888-.506 3.152-1.566 3.812-.985.615-2.234.548-3.284.412a33.19 33.19 0 0 1-1.12-.172 65.03 65.03 0 0 0-.492-.08c-.52-.082-.988-.143-1.405-.149-.835-.012-1.24.186-1.462.63-.132.264-.131.583.015 1.018.129.383.335.757.557 1.159l.12.216c.123.226.254.476.345.716.085.226.18.555.108.904-.086.42-.373.705-.727.858-.312.135-.69.176-1.089.176-5.483 0-10-4.215-10-9.5Zm8.25-4.75a.5.5 0 1 0 0 1 .5.5 0 0 0 0-1Zm-2 .5a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm7 1a.5.5 0 1 0 0 1 .5.5 0 0 0 0-1Zm-2 .5a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm-6 2.25a.5.5 0 1 0 0 1 .5.5 0 0 0 0-1Zm-2 .5a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPassword: IconComponent<IconProps> = forwardRef(function IconPassword(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="m4.296 20.141.34-.668-.34.668Zm-.437-.437-.668.34.668-.34Zm16.282 0-.668-.34.668.34Zm-.437.437-.34-.668.34.668Zm.437-15.845-.668.34.668-.34Zm-.437-.437.34-.668-.34.668Zm-15.845.437-.668-.34.668.34Zm.437-.437-.34-.668.34.668ZM17 8.75a.75.75 0 0 0-1.5 0H17Zm-1.5 6.5a.75.75 0 0 0 1.5 0h-1.5Zm4-9.9v13.3H21V5.35h-1.5Zm-.85 14.15H5.35V21h13.3v-1.5ZM4.5 18.65V5.35H3v13.3h1.5ZM5.35 4.5h13.3V3H5.35v1.5Zm0 15c-.292 0-.467 0-.596-.011-.12-.01-.134-.025-.117-.016l-.681 1.336c.23.118.463.157.676.175.205.017.45.016.718.016v-1.5ZM3 18.65c0 .268 0 .514.016.718.018.213.057.446.175.677l1.336-.682c.009.017-.006.003-.016-.117a8.337 8.337 0 0 1-.011-.596H3Zm1.636.823a.25.25 0 0 1-.109-.11l-1.336.681c.168.33.435.598.765.765l.68-1.336ZM19.5 18.65c0 .292 0 .467-.011.596-.01.12-.025.134-.016.117l1.336.681c.118-.23.157-.463.175-.676.017-.204.016-.45.016-.718h-1.5ZM18.65 21c.268 0 .514 0 .718-.016.213-.018.446-.057.677-.175l-.682-1.336c.017-.009.003.006-.117.016-.13.01-.304.011-.596.011V21Zm.823-1.637a.25.25 0 0 1-.11.11l.681 1.336a1.75 1.75 0 0 0 .765-.765l-1.336-.68ZM21 5.35c0-.268 0-.513-.016-.718a1.775 1.775 0 0 0-.175-.676l-1.336.68c-.009-.016.006-.002.016.118.01.13.011.304.011.596H21Zm-2.35-.85c.292 0 .467 0 .596.011.12.01.134.025.117.016l.681-1.336a1.774 1.774 0 0 0-.676-.175C19.164 3 18.918 3 18.65 3v1.5Zm2.16-.544a1.75 1.75 0 0 0-.765-.765l-.682 1.336a.25.25 0 0 1 .11.11l1.336-.681ZM4.5 5.35c0-.292 0-.467.011-.596.01-.12.025-.134.016-.117l-1.336-.681c-.118.23-.157.463-.175.676C3 4.837 3 5.082 3 5.35h1.5ZM5.35 3c-.268 0-.513 0-.718.016a1.774 1.774 0 0 0-.676.175l.68 1.336c-.016.009-.002-.006.118-.016.13-.01.304-.011.596-.011V3Zm-.823 1.636a.25.25 0 0 1 .11-.109l-.681-1.336a1.75 1.75 0 0 0-.765.765l1.336.68ZM15.5 8.75v6.5H17v-6.5h-1.5Z" />
            <path
                strokeWidth=".5"
                d="M8 12.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm4 0a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
            />
        </BaseIcon>
    )
})

export const IconPencil: IconComponent<IconProps> = forwardRef(function IconPencil(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M15.7626 2.67675C16.446 1.99333 17.554 1.99333 18.2374 2.67675L21.3232 5.76253C22.0066 6.44595 22.0066 7.55399 21.3232 8.23741L8.07322 21.4874C7.74503 21.8156 7.29991 22 6.83579 22H2.75C2.33579 22 2 21.6642 2 21.25V17.1642C2 16.7001 2.18437 16.2549 2.51256 15.9267L15.7626 2.67675ZM17.1768 3.73741C17.0791 3.63978 16.9209 3.63978 16.8232 3.73741L14.3107 6.24997L17.75 9.68931L20.2626 7.17675C20.3602 7.07912 20.3602 6.92082 20.2626 6.8232L17.1768 3.73741ZM16.6893 10.75L13.25 7.31063L3.57322 16.9874C3.52634 17.0343 3.5 17.0979 3.5 17.1642V20.5H6.83579C6.90209 20.5 6.96568 20.4736 7.01256 20.4267L16.6893 10.75Z"
            />
        </BaseIcon>
    )
})

export const IconPeople: IconComponent<IconProps> = forwardRef(function IconPeople(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.5 4.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM3.5 7a4 4 0 1 1 8 0 4 4 0 0 1-8 0Zm13-2.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Zm-4 2.5a4 4 0 1 1 8 0 4 4 0 0 1-8 0Zm6.692 7.268c-1.381-.87-3.024-1-4.48-.395a.75.75 0 0 1-.575-1.385 6.342 6.342 0 0 1 5.855.511c1.796 1.133 3.171 3.188 3.628 6.067.17 1.073-.698 1.934-1.693 1.934H16.75a.75.75 0 0 1 0-1.5h5.177a.23.23 0 0 0 .172-.072.145.145 0 0 0 .04-.127c-.4-2.52-1.571-4.165-2.947-5.033ZM7.429 13.5c-2.384 0-4.934 1.811-5.567 5.798a.149.149 0 0 0 .04.13.228.228 0 0 0 .171.072h10.711c.073 0 .133-.03.17-.073a.149.149 0 0 0 .041-.129c-.633-3.987-3.183-5.798-5.566-5.798Zm0-1.5c3.243 0 6.319 2.476 7.047 7.063.17 1.07-.694 1.937-1.692 1.937H2.073c-.998 0-1.862-.867-1.692-1.937C1.109 14.476 4.185 12 7.429 12Z"
            />
        </BaseIcon>
    )
})

export const IconPeopleFilled: IconComponent<IconProps> = forwardRef(
    function IconPeopleFilled(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path d="M3.5 7a4 4 0 1 1 8 0 4 4 0 0 1-8 0ZM15.958 18.827c-.388-2.442-1.392-4.43-2.774-5.851A6.379 6.379 0 0 1 16.568 12c3.243 0 6.32 2.476 7.048 7.063.17 1.07-.694 1.937-1.692 1.937h-6.406c.385-.602.57-1.35.44-2.173ZM16.5 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM7.429 12c3.243 0 6.319 2.476 7.048 7.063.17 1.07-.695 1.937-1.693 1.937H2.074C1.074 21 .21 20.133.38 19.063 1.11 14.476 4.186 12 7.429 12Z" />
            </BaseIcon>
        )
    }
)

export const IconPercentage: IconComponent<IconProps> = forwardRef(function IconPercentage(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm14.53-4.53a.75.75 0 0 1 0 1.06l-8 8a.75.75 0 0 1-1.06-1.06l8-8a.75.75 0 0 1 1.06 0Z"
                clipRule="evenodd"
            />
            <path d="M9 9.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Z" />
            <path
                fillRule="evenodd"
                d="M9 10a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm-.5-1a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0Z"
                clipRule="evenodd"
            />
            <path d="M15 15.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Z" />
            <path
                fillRule="evenodd"
                d="M15 16a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm-.5-1a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPerson: IconComponent<IconProps> = forwardRef(function IconPerson(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 3.5a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm-4.5 3a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM12 14c-3.418 0-5.905 2.253-6.597 5.33a.103.103 0 0 0 0 .052c.004.013.011.03.027.048.033.037.091.07.168.07h12.804a.223.223 0 0 0 .168-.07.103.103 0 0 0 .027-.1C17.905 16.253 15.417 14 12 14Zm-8.06 5.001C4.773 15.294 7.827 12.5 12 12.5s7.227 2.794 8.06 6.501C20.31 20.112 19.4 21 18.402 21H5.598c-.998 0-1.908-.888-1.658-1.999Z"
            />
        </BaseIcon>
    )
})

export const IconPhone: IconComponent<IconProps> = forwardRef(function IconPhone(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M6.727 2.727c.305-.305.72-.477 1.151-.477h9.22a1.628 1.628 0 0 1 1.628 1.628v16.244a1.628 1.628 0 0 1-1.628 1.628h-9.22a1.628 1.628 0 0 1-1.628-1.628V3.878c0-.432.172-.846.477-1.151ZM7.878 3.75a.128.128 0 0 0-.128.128v16.244a.128.128 0 0 0 .128.128h9.22a.128.128 0 0 0 .128-.128V3.878a.128.128 0 0 0-.128-.128h-9.22Zm2.762 1.445a.75.75 0 0 1 .75-.75h2.195a.75.75 0 0 1 0 1.5H11.39a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPieChart: IconComponent<IconProps> = forwardRef(function IconPieChart(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M10.504 5.516a7.5 7.5 0 1 0 7.98 7.984h-5.656c-.258 0-.494 0-.692-.016a1.774 1.774 0 0 1-.676-.175 1.75 1.75 0 0 1-.765-.764 1.773 1.773 0 0 1-.175-.677c-.016-.198-.016-.434-.016-.692v-5.66ZM2 13a9 9 0 0 1 9.277-8.996.75.75 0 0 1 .727.75v6.396c0 .293 0 .467.011.596a.62.62 0 0 0 .018.12.25.25 0 0 0 .105.105.62.62 0 0 0 .12.018c.13.01.304.011.596.011h6.392a.75.75 0 0 1 .75.728A9 9 0 1 1 2 13Zm10.135-1.03.002.001h-.002Zm-.101-.1v-.002.001Z"
                clipRule="evenodd"
            />
            <path
                fillRule="evenodd"
                d="M21.88 8.46c-.02-.13-.06-.26-.09-.38v-.02a8.442 8.442 0 0 0-5.94-5.94h-.02c-.12-.03-.25-.07-.38-.09-.15-.02-.34-.03-.55.03-.29.09-.55.29-.71.55-.12.19-.16.39-.17.54-.01.13-.01.28-.01.42v5.19c.01.13.04.3.13.48.12.24.31.43.55.55.18.09.35.12.48.13h5.19c.14 0 .29 0 .42-.01.15-.02.35-.05.54-.17.26-.16.46-.42.55-.71.06-.21.05-.4.03-.55l-.02-.02Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPiggyBank: IconComponent<IconProps> = forwardRef(function IconPiggyBank(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M5 4.75c0-.92.792-1.861 1.905-1.61A6.3 6.3 0 0 1 10.14 5h3.864a7 7 0 0 1 6.605 9.323 1.25 1.25 0 0 0 1.617-1.854.75.75 0 0 1 1.17-.938 2.75 2.75 0 0 1-3.436 4.148c-.247.4-.534.772-.854 1.113a.35.35 0 0 0-.106.231v2.227A1.75 1.75 0 0 1 17.25 21h-2.5A1.75 1.75 0 0 1 13 19.25a.25.25 0 0 0-.25-.25h-1.5a.25.25 0 0 0-.25.25A1.75 1.75 0 0 1 9.25 21h-2.5A1.75 1.75 0 0 1 5 19.25v-2.235a.35.35 0 0 0-.106-.231 7.015 7.015 0 0 1-1.107-1.564c-.083-.16-.213-.22-.302-.22H2.75A1.75 1.75 0 0 1 1 13.25v-2.5C1 9.783 1.784 9 2.75 9h.735c.089 0 .219-.06.302-.22a7.016 7.016 0 0 1 1.107-1.564.35.35 0 0 0 .106-.23V4.75Zm5.004 1.75c-.34 0-.664-.146-.889-.398a4.802 4.802 0 0 0-2.54-1.499m-.024.007a.192.192 0 0 0-.051.14v2.235c0 .495-.21.935-.511 1.256-.344.367-.637.78-.87 1.23-.298.572-.896 1.029-1.634 1.029H2.75a.25.25 0 0 0-.25.25v2.5c0 .138.112.25.25.25h.735c.738 0 1.336.456 1.634 1.03.233.449.526.862.87 1.229.3.32.511.76.511 1.256v2.235c0 .138.112.25.25.25h2.5a.25.25 0 0 0 .25-.25c0-.967.784-1.75 1.75-1.75h1.5c.966 0 1.75.783 1.75 1.75 0 .138.112.25.25.25h2.5a.25.25 0 0 0 .25-.25v-2.227c0-.496.211-.937.513-1.258A5.48 5.48 0 0 0 19.503 12a5.5 5.5 0 0 0-5.499-5.5h-4M6.551 4.61a.04.04 0 0 1 .012-.009Zm.012-.009Zm0 0Zm-.001 0Z"
                clipRule="evenodd"
            />
            <path d="M9.5 10.75a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Z" />
        </BaseIcon>
    )
})

export const IconPin: IconComponent<IconProps> = forwardRef(function IconPin(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M6.5 3.75C6.5 2.784 7.284 2 8.25 2h7.5c.966 0 1.75.784 1.75 1.75v3.247a6.5 6.5 0 0 0 1.904 4.596l.376.377c.141.14.22.331.22.53v2.75a.75.75 0 0 1-.75.75h-6.5v5.25a.75.75 0 0 1-1.5 0V16h-6.5a.75.75 0 0 1-.75-.75V12.5a.75.75 0 0 1 .22-.53l.376-.377A6.5 6.5 0 0 0 6.5 6.997V3.75Zm1.75-.25a.25.25 0 0 0-.25.25v3.247a8 8 0 0 1-2.343 5.657l-.157.157V14.5h13v-1.69l-.157-.156A8 8 0 0 1 16 6.997V3.75a.25.25 0 0 0-.25-.25h-7.5Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPinFilled: IconComponent<IconProps> = forwardRef(function IconPinFilled(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M8.25 2A1.75 1.75 0 0 0 6.5 3.75v3.247a6.5 6.5 0 0 1-1.904 4.596l-.376.377a.75.75 0 0 0-.22.53v2.75a.75.75 0 0 0 .75.75h6.5v5.25a.75.75 0 0 0 1.5 0V16h6.5a.75.75 0 0 0 .75-.75V12.5a.75.75 0 0 0-.22-.53l-.376-.377A6.5 6.5 0 0 1 17.5 6.997V3.75A1.75 1.75 0 0 0 15.75 2h-7.5Z" />
        </BaseIcon>
    )
})

export const IconPineapple: IconComponent<IconProps> = forwardRef(function IconPineapple(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M12.96 8.72a13.799 13.799 0 0 0-1.567-.027c.144-.345.328-.672.55-.973a.305.305 0 0 0 .07-.09c.627-.797 1.717-1.587 3.543-1.44-.693.413-1.593 1.076-2.026 1.92a.334.334 0 0 0 .136.447.332.332 0 0 0 .45-.144c.634-1.226 2.594-2.08 2.614-2.08a.333.333 0 0 0-.057-.633 5.411 5.411 0 0 0-3.333.143 2.977 2.977 0 0 1-.047-.54c-.002-.3.029-.599.09-.893a5.405 5.405 0 0 1 .67-1.676v-.02a.333.333 0 0 0-.02-.383c-.087-.11-.233-.154-.367-.11a4.033 4.033 0 0 0-1.74 1.182 6.776 6.776 0 0 0-1.113 1.69.177.177 0 0 0 .074.227.173.173 0 0 0 .236-.067 6.27 6.27 0 0 1 1.164-1.493 4.33 4.33 0 0 1 .756-.57c0 .034-.03.063-.043.097a6.036 6.036 0 0 0-.297 1c-.074.34-.11.688-.106 1.037 0 .284.032.568.093.846-.351.217-.67.48-.95.783-1-1.373-2.334-1.503-4.397-1.247v.001a.332.332 0 0 0-.133.617c.02 0 2.04 1.286 2.177 2.83a4.287 4.287 0 0 0-2.334 3.07A19.059 19.059 0 0 0 7 18.037a4.254 4.254 0 0 0 3.72 3.687 13.3 13.3 0 0 0 2.596.012 4.184 4.184 0 0 0 3.731-3.606c.243-2 .163-4.025-.237-6a4.271 4.271 0 0 0-3.85-3.41ZM7.666 18c.335-.031.67-.015 1 .047-.257.3-.495.62-.709.953a3.517 3.517 0 0 1-.29-1Zm-.076-.647c-.03-.316-.057-.63-.074-.94a9.16 9.16 0 0 1 1.218-1.51c.336.052.67.121 1 .21.568.154 1.125.35 1.666.584-.712.447-1.377.965-1.984 1.546-.103.097-.2.2-.296.303a4.413 4.413 0 0 0-1.536-.196l.006.003Zm.153-5.02c.034-.175.08-.348.137-.517.38-.055.765-.06 1.147-.016.432.06.86.148 1.28.266.32.093.637.205.95.334-.134.076-.263.15-.393.23a12.163 12.163 0 0 0-2.124 1.667 5.093 5.093 0 0 0-1.224-.08c.038-.632.114-1.26.227-1.883ZM16 11.667c.073.192.13.39.17.59.12.608.207 1.211.27 1.808a4.637 4.637 0 0 0-1.42.096 11.876 11.876 0 0 0-2.55-1.787c.347-.154.702-.29 1.063-.407.404-.13.818-.23 1.237-.3.4-.068.81-.068 1.21 0H16Zm.537 4.24a8.689 8.689 0 0 0-.96-1.196 3.46 3.46 0 0 1 .916.023c.01.39.027.78.027 1.17l.017.003Zm-3.777-2.85c.64.344 1.248.747 1.813 1.204-.216.06-.43.13-.64.2-.711.248-1.399.562-2.053.937-.643-.33-1.311-.606-2-.824-.208-.063-.414-.12-.624-.173a11.928 11.928 0 0 1 1.873-1.36c.247-.143.5-.277.758-.403.293.13.586.27.873.42Zm-5.273 1.81c.242-.028.486-.035.73-.02-.263.3-.507.616-.73.947a20.357 20.357 0 0 1 0-.927Zm2.196 2.667a12.162 12.162 0 0 1 2.083-1.503l.174-.09.197.093A11.56 11.56 0 0 1 14.3 17.47a11.58 11.58 0 0 0-1.784.77c-.263.143-.52.297-.77.457a10.828 10.828 0 0 0-2.2-1.03l.137-.134Zm5-.167c-.053-.053-.103-.107-.156-.153-.641-.587-1.346-1.1-2.1-1.53.545-.27 1.11-.498 1.69-.683a8.32 8.32 0 0 1 1.03-.253c.518.479.976 1.02 1.363 1.61 0 .266-.013.534-.04.8a4.542 4.542 0 0 0-1.783.223l-.003-.014ZM8.297 6.27c1.597-.103 2.386.23 3.026 1.207-.277.39-.501.817-.666 1.266-.211.03-.418.077-.62.14-.177-1.126-1.08-2.063-1.74-2.613Zm2.726 3.103c.284-.02.574-.03.864-.03.29 0 .666 0 1.02.04a3.611 3.611 0 0 1 2.71 1.587 6.395 6.395 0 0 0-2.284.437c-.514.188-1.014.41-1.496.666a12.668 12.668 0 0 0-1.367-.54 6.9 6.9 0 0 0-2.27-.396 3.578 3.578 0 0 1 2.823-1.764Zm-2.876 10a8.21 8.21 0 0 1 .95-1.226l.186.047v-.001c.632.172 1.248.403 1.837.69l.237.124c-.193.137-.384.277-.57.427-.462.37-.889.783-1.274 1.232a3.552 3.552 0 0 1-1.366-1.306v.014Zm5.107 1.714h-.001c-.82.08-1.646.08-2.466 0a3.342 3.342 0 0 1-.94-.24 9.626 9.626 0 0 1 1.96-1.58c.344.205.678.428 1 .667.414.308.801.654 1.156 1.03-.232.06-.47.096-.71.11v.013Zm.97-.24a8.963 8.963 0 0 0-1.204-1.207c-.263-.22-.534-.424-.816-.617.183-.103.37-.203.557-.296h-.001A10.46 10.46 0 0 1 14.626 18l.19-.05c.426.416.802.88 1.12 1.384a3.537 3.537 0 0 1-1.713 1.5l.001.013Zm1.847-1.737a7.88 7.88 0 0 0-.9-1.217 3.596 3.596 0 0 1 1.239-.053c0 .073 0 .147-.023.216v.001c-.051.361-.158.712-.317 1.04v.013Z" />
        </BaseIcon>
    )
})

export const IconPizza: IconComponent<IconProps> = forwardRef(function IconPizza(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M18.73 3.25a1.75 1.75 0 0 1 2.02 2.02l-2.59 15.108c-.162.95-1.087 1.63-2.079 1.388a18.79 18.79 0 0 1-8.858-4.99 18.791 18.791 0 0 1-4.99-8.857c-.24-.992.44-1.917 1.39-2.08L18.73 3.25Zm.542 1.767a.25.25 0 0 0-.289-.289l-2.19.376a2.81 2.81 0 1 1-5.518.945l-4.052.695a13.683 13.683 0 0 0 3.625 6.409c.474.474.975.907 1.496 1.297a3.84 3.84 0 0 1 5.768-2.665l1.16-6.768Zm-1.447 8.443a2.338 2.338 0 0 0-4.006 1.952c1.09.616 2.248 1.07 3.437 1.365l.569-3.317Zm-.823 4.799a15.168 15.168 0 0 1-7.215-4.046A15.182 15.182 0 0 1 5.74 6.998l-1.865.32c-.15.025-.207.153-.185.245a17.29 17.29 0 0 0 4.593 8.153 17.291 17.291 0 0 0 8.153 4.593c.092.022.22-.036.245-.185l.32-1.865ZM12.765 5.794v.015a1.309 1.309 0 1 0 2.538-.45l-2.538.435Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPointer: IconComponent<IconProps> = forwardRef(function IconPointer(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M9.5 3.5a1 1 0 0 0-1 1V11a.75.75 0 0 1-1.219.586l-.938-.751a1.25 1.25 0 0 0-1.757.195l-.444.555 2.755 5.235A6.42 6.42 0 0 0 19 13.83v-.58A3.25 3.25 0 0 0 15.75 10h-4.5a.75.75 0 0 1-.75-.75V4.5a1 1 0 0 0-1-1ZM7 4.5a2.5 2.5 0 1 1 5 0v4h3.75a4.75 4.75 0 0 1 4.75 4.75v.58a7.92 7.92 0 0 1-14.93 3.688L2.586 11.85a.75.75 0 0 1 .078-.818l.751-.938A2.75 2.75 0 0 1 7 9.467V4.5Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPresent: IconComponent<IconProps> = forwardRef(function IconPresent(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M6.25 4.417A2.417 2.417 0 0 1 8.667 2c1.376 0 2.593.68 3.333 1.724A4.078 4.078 0 0 1 15.333 2a2.417 2.417 0 0 1 2.417 2.417c0 .98-.345 1.88-.92 2.583h2.42c.966 0 1.75.784 1.75 1.75V11c0 .698-.409 1.3-1 1.582v6.668A1.75 1.75 0 0 1 18.25 21H5.75A1.75 1.75 0 0 1 4 19.25v-6.668A1.75 1.75 0 0 1 3 11V8.75C3 7.784 3.784 7 4.75 7h2.42a4.066 4.066 0 0 1-.92-2.583ZM10.333 7h.917v-.917A2.583 2.583 0 0 0 8.667 3.5a.917.917 0 0 0-.917.917A2.583 2.583 0 0 0 10.333 7Zm2.417-.917V7h.917a2.583 2.583 0 0 0 2.583-2.583.917.917 0 0 0-.917-.917 2.583 2.583 0 0 0-2.583 2.583ZM11.25 8.5h-6.5a.25.25 0 0 0-.25.25V11c0 .138.112.25.25.25h6.5V8.5Zm1.5 2.75V8.5h6.5a.25.25 0 0 1 .25.25V11a.25.25 0 0 1-.25.25h-6.5Zm-1.5 1.5H5.5v6.5c0 .138.112.25.25.25h5.5v-6.75Zm1.5 6.75v-6.75h5.75v6.5a.25.25 0 0 1-.25.25h-5.5Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPrivacy: IconComponent<IconProps> = forwardRef(function IconPrivacy(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3 4.75C3 3.784 3.784 3 4.75 3h14.5c.966 0 1.75.784 1.75 1.75v12.406c0 .64-.35 1.23-.912 1.537l-7.25 3.954a1.75 1.75 0 0 1-1.676 0l-7.25-3.954A1.75 1.75 0 0 1 3 17.156V4.75Zm1.75-.25a.25.25 0 0 0-.25.25v12.406c0 .092.05.176.13.22l7.25 3.954a.25.25 0 0 0 .24 0l7.25-3.954a.25.25 0 0 0 .13-.22V4.75a.25.25 0 0 0-.25-.25H4.75Zm10.28 5.22a.75.75 0 0 1 0 1.06l-3.5 3.5a.75.75 0 0 1-1.06 0l-1.5-1.5a.75.75 0 1 1 1.06-1.06l.97.97 2.97-2.97a.75.75 0 0 1 1.06 0Z"
            />
        </BaseIcon>
    )
})

export const IconProfile: IconComponent<IconProps> = forwardRef(function IconProfile(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 2.75A.75.75 0 0 1 2.75 2h14.5a.75.75 0 0 1 .75.75V6h3.25a.75.75 0 0 1 .75.75v14.5a.75.75 0 0 1-.75.75H6.75a.75.75 0 0 1-.75-.75V18H2.75a.75.75 0 0 1-.75-.75V2.75ZM7.5 20.5h1.245c.719-2.3 2.778-4 5.255-4s4.536 1.7 5.255 4H20.5v-13h-13v13Zm9-14.5H6.75a.75.75 0 0 0-.75.75v9.75H3.5v-13h13V6Zm1.158 14.5C17.012 19.008 15.599 18 14 18c-1.599 0-3.012 1.008-3.658 2.5h7.316ZM14 11a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm-3 1.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0Z"
            />
        </BaseIcon>
    )
})

export const IconPullRequest: IconComponent<IconProps> = forwardRef(function IconPullRequest(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M6.5 3.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4ZM3 5.5a3.5 3.5 0 1 1 4.25 3.42v6.16A3.501 3.501 0 0 1 6.5 22a3.5 3.5 0 0 1-.75-6.92V8.92A3.501 3.501 0 0 1 3 5.5Zm12.28-3.28a.75.75 0 0 1 0 1.06l-1.47 1.47h2.69c.966 0 1.75.784 1.75 1.75v8.58A3.501 3.501 0 0 1 17.5 22a3.5 3.5 0 0 1-.75-6.92V6.5a.25.25 0 0 0-.25-.25h-2.69l1.47 1.47a.75.75 0 0 1-1.06 1.06l-2.75-2.75a.75.75 0 0 1 0-1.06l2.75-2.75a.75.75 0 0 1 1.06 0ZM6.5 16.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm11 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPulse: IconComponent<IconProps> = forwardRef(function IconPulse(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M6.936 11.277 8.99 4.77l5.05 16.414c.29.945 1.63.94 1.914-.007l2.55-8.499a.25.25 0 0 1 .239-.178h3.506a.75.75 0 0 0 0-1.5h-3.506a1.75 1.75 0 0 0-1.676 1.247L14.99 19.17 9.953 2.796c-.289-.938-1.614-.943-1.91-.007l-2.537 8.036a.25.25 0 0 1-.239.175H1.75a.75.75 0 0 0 0 1.5h3.517a1.75 1.75 0 0 0 1.669-1.223Z" />
        </BaseIcon>
    )
})

export const IconPuzzle: IconComponent<IconProps> = forwardRef(function IconPuzzle(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M13.6589 0.961912C11.8139 0.961912 10.2999 2.46753 10.2999 4.32095C10.2999 4.65282 10.3767 4.96689 10.4696 5.28189H5.50372C5.12121 5.28189 4.78091 5.62221 4.78091 5.99627V10.9621C4.47436 10.8693 4.15185 10.8009 3.81998 10.8009C1.97404 10.8009 0.460938 12.3149 0.460938 14.1599C0.460938 16.0049 1.975 17.5189 3.81998 17.5189C4.15185 17.5189 4.47436 17.4421 4.78091 17.3493V22.3151C4.78091 22.6976 5.12123 23.0379 5.50372 23.0379H21.8228C22.1968 23.0379 22.5371 22.6976 22.5371 22.3151V15.6738C22.5456 15.3673 22.3328 15.0701 22.044 14.9763C21.7468 14.8826 21.4065 15.0016 21.2275 15.2566C20.8787 15.7498 20.3171 16.0816 19.6628 16.0816C18.5912 16.0816 17.7409 15.2229 17.7409 14.1598C17.7409 13.0882 18.5912 12.2379 19.6628 12.2379C20.3172 12.2379 20.8787 12.5614 21.2275 13.0545C21.3972 13.3095 21.7459 13.4285 22.0356 13.3348C22.3243 13.241 22.5456 12.9438 22.5371 12.6457V5.99603C22.5371 5.62197 22.1968 5.28165 21.8228 5.28165H16.8483C16.942 4.96665 17.018 4.65258 17.018 4.32071C17.018 2.46726 15.5039 0.96167 13.6589 0.96167L13.6589 0.961912ZM13.6589 2.3991C14.7305 2.3991 15.5808 3.24942 15.5808 4.32098C15.5808 4.78878 15.4195 5.20504 15.1467 5.53691C14.9686 5.74129 14.9255 6.05536 15.0445 6.30191C15.1552 6.54846 15.427 6.71817 15.6989 6.71817H21.0989V11.132C20.6649 10.9276 20.1717 10.8001 19.6617 10.8001C17.8167 10.8001 16.3027 12.3142 16.3027 14.1592C16.3027 16.0042 17.8168 17.5182 19.6617 17.5182C20.1717 17.5182 20.6649 17.3823 21.0989 17.1695V21.6001H6.217V16.2001C6.217 15.9282 6.05575 15.6648 5.80919 15.5457C5.56264 15.4351 5.24764 15.4689 5.04419 15.6395C4.71232 15.9198 4.28764 16.082 3.81981 16.082C2.74825 16.082 1.89794 15.2232 1.89794 14.1601C1.89794 13.0886 2.74825 12.2382 3.81981 12.2382C4.28761 12.2382 4.71232 12.3995 5.04419 12.6723C5.24857 12.842 5.56264 12.8851 5.80919 12.7661C6.05574 12.6554 6.217 12.392 6.217 12.1201V6.72012H11.617C11.8973 6.72012 12.1617 6.55043 12.2807 6.30387C12.3914 6.05731 12.3492 5.74231 12.1786 5.53887C11.8982 5.20699 11.736 4.79074 11.736 4.32293C11.736 3.25138 12.5948 2.40106 13.6579 2.40106L13.6589 2.3991Z" />
        </BaseIcon>
    )
})

export const IconQuestion: IconComponent<IconProps> = forwardRef(function IconQuestion(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16ZM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 9a1 1 0 0 0-.879.522 1 1 0 1 1-1.754-.96A3 3 0 0 1 12 7c1.515 0 2.567 1.006 2.866 2.189.302 1.189-.156 2.574-1.524 3.258A.618.618 0 0 0 13 13a1 1 0 1 1-2 0c0-.992.56-1.898 1.447-2.342.455-.227.572-.618.48-.978C12.836 9.314 12.529 9 12 9Z"
            />
            <path d="M13 16a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
        </BaseIcon>
    )
})

export const IconQuote: IconComponent<IconProps> = forwardRef(function IconQuote(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 5.75C2 4.784 2.784 4 3.75 4h5.5c.964 0 1.75.78 1.75 1.748v7.288c0 2.572-1.113 4.31-2.228 5.393a7.929 7.929 0 0 1-1.52 1.154 6.704 6.704 0 0 1-.708.357l-.014.006-.005.002h-.002c-.001.001-.001.001-.273-.698l.272.7a.75.75 0 0 1-1.022-.7v-5.464H3.75A1.75 1.75 0 0 1 2 12.036V5.75Zm5 12.21c.23-.164.479-.366.728-.608.885-.859 1.772-2.228 1.772-4.316V5.748a.248.248 0 0 0-.25-.248h-5.5a.25.25 0 0 0-.25.25v6.286c0 .138.112.25.25.25h2.5a.75.75 0 0 1 .75.75v4.924Zm6-12.21c0-.966.784-1.75 1.75-1.75h5.5c.965 0 1.75.78 1.75 1.748v7.288c0 2.572-1.113 4.31-2.227 5.393a7.927 7.927 0 0 1-1.52 1.154 6.701 6.701 0 0 1-.709.357l-.014.006-.005.002h-.002c-.001.001-.002.001-.273-.698l.271.7a.75.75 0 0 1-1.021-.7v-5.464h-1.75a1.75 1.75 0 0 1-1.75-1.75V5.75Zm5 12.21c.23-.164.479-.366.727-.608.886-.859 1.773-2.228 1.773-4.316V5.748a.248.248 0 0 0-.25-.248h-5.5a.25.25 0 0 0-.25.25v6.286c0 .138.112.25.25.25h2.5a.75.75 0 0 1 .75.75v4.924Z"
            />
        </BaseIcon>
    )
})

export const IconRabbit: IconComponent<IconProps> = forwardRef(function IconRabbit(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M12.36 2.989c-1.156 0-1.835.11-2.295.371-.23.13-.392.314-.484.518-.091.203-.112.41-.112.596 0 .288.164.53.36.686.195.156.433.262.686.338a3.91 3.91 0 0 0 1.598.123c2.119-.295 3.21.138 4.027.81-.248.269-.419.53-.506.743-.124.298-.16.437-.304.562-.169.145-.312.183-.54.169-.228-.014-.526-.101-.877-.225-.704-.247-1.612-.63-2.723-.63a4.483 4.483 0 0 0-4.421 3.679 1.89 1.89 0 0 0-.99-.281c-.51 0-.947.17-1.249.483a1.725 1.725 0 0 0-.461 1.204c0 .972 1.22 1.807 2.756 1.226.312.464.755.776 1.181.934.258.096.51.153.698.191.094.02.166.035.225.045.059.01.12.02.09.011.325.093.71.114 1.012.192a.987.987 0 0 1 .326.135c.053.04.046.028.046.09 0 .188-.126.337-.35.551a3.337 3.337 0 0 0-.348.371.953.953 0 0 0-.225.585c0 .336.17.636.394.81.223.175.48.262.731.315.252.054.51.07.72.09.211.02.387.044.428.056 1.406.434 1.653.924 2.26 1.812a2.625 2.625 0 0 0 1.61 1.057c.285.06.563.072.82-.022.258-.094.496-.386.496-.698 0-1.572-1.091-2.6-2.115-3.195-.198-.115-.352-.149-.54-.236 2.596-1.574 3.345-3.891 3.352-5.895.439.193.893.328 1.361.36.384.027.749-.028 1.058-.135.31-.107.577-.232.731-.529.234-.444.246-.995.203-1.518-.044-.524-.135-.988-.315-1.317-.702-1.285-1.901-1.69-3.116-1.687-.518-.844-1.356-1.512-2.284-1.969-1.004-.495-2.074-.776-2.914-.776Zm0 .742c.67 0 1.675.248 2.588.698.852.42 1.591 1.02 2.002 1.699h-.011c-.97-.93-2.41-1.588-4.928-1.238a3.357 3.357 0 0 1-1.282-.101 1.398 1.398 0 0 1-.439-.203c-.084-.067-.09-.093-.09-.112a.72.72 0 0 1 .045-.293c.03-.064.073-.123.191-.191.238-.135.82-.259 1.924-.259ZM4.463 5.52a.363.363 0 0 0 .078.72H8.22c.13.001.25-.066.316-.179a.363.363 0 0 0 0-.362.362.362 0 0 0-.316-.179H4.541a.24.24 0 0 0-.045 0h-.033Zm12.554.698c.01.016.037.028.046.045a.364.364 0 0 0 .337.202c1.132-.04 2.018.2 2.621 1.305.07.125.2.586.236 1.035.037.449-.01.918-.112 1.114.014-.028-.115.095-.326.168-.211.074-.49.12-.765.102-.568-.04-1.075-.18-1.553-.495a.368.368 0 0 0-.574.326c.134 2.012-.43 4.42-3.33 5.996.625-.55 1.35-1.464 1.35-2.902 0-1.27-.7-2.253-1.71-2.835a.367.367 0 1 0-.37.63c.837.483 1.338 1.192 1.338 2.205 0 1.711-1.17 2.487-1.688 2.812l-.022.597s.066.05.079.056c.025.011.028.01.034.011.012.004.016-.001.022 0l.034.011c.027.007.063.02.112.034.1.028.244.065.417.124.344.118.805.31 1.26.573.908.53 1.755 1.318 1.755 2.554-.006 0-.013-.004-.023 0a.873.873 0 0 1-.416 0c-.388-.081-.876-.357-1.148-.753-.596-.872-1.104-1.615-2.655-2.093-.19-.059-.364-.07-.573-.09-.21-.02-.436-.038-.63-.079-.194-.04-.358-.104-.44-.168-.08-.065-.112-.102-.112-.237 0-.03.009-.059.068-.135.059-.076.162-.178.281-.292.24-.23.585-.568.585-1.08a.891.891 0 0 0-.338-.675c-.184-.145-.392-.207-.596-.259-.406-.104-.842-.138-.99-.18-.071-.021-.098-.024-.157-.034-.06-.01-.131-.028-.214-.045a3.977 3.977 0 0 1-.585-.157c-.416-.155-.804-.404-.967-.799a.368.368 0 0 0-.507-.191c-1.361.673-1.98-.163-1.98-.484 0-.297.093-.526.248-.686.154-.16.374-.27.72-.27.451 0 .844.268.922.54a.368.368 0 0 0 .709-.101c0-2.314 1.776-3.837 3.78-3.837.94 0 1.741.338 2.475.597.367.129.717.237 1.08.258.363.021.745-.067 1.058-.337.314-.272.406-.6.506-.844.1-.245.212-.471.607-.776a.366.366 0 0 0 .101-.461Zm-13.14.742a.362.362 0 0 0 .068.72h2.543c.129.001.25-.066.316-.179a.363.363 0 0 0 0-.362.362.362 0 0 0-.316-.179h-2.61ZM19.38 8.04a.54.54 0 1 0 0 1.08.54.54 0 0 0 0-1.08ZM3.293 8.4a.362.362 0 0 0 .067.72h2.306c.13.001.25-.066.317-.179a.363.363 0 0 0 0-.362.362.362 0 0 0-.317-.179H3.293Z" />
        </BaseIcon>
    )
})

export const IconRefresh: IconComponent<IconProps> = forwardRef(function IconRefresh(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.75 3C5.16421 3 5.5 3.33579 5.5 3.75V5.71122C7.1554 4.03743 9.4791 3 12 3C16.9706 3 21 7.02944 21 12C21 12.3803 20.9764 12.7555 20.9304 13.1241C20.8792 13.5351 20.5044 13.8267 20.0934 13.7755C19.6823 13.7242 19.3907 13.3495 19.4419 12.9384C19.4802 12.6313 19.5 12.3181 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C9.77612 4.5 7.73561 5.46681 6.3448 7H8.75C9.16421 7 9.5 7.33579 9.5 7.75C9.5 8.16421 9.16421 8.5 8.75 8.5H5.25C4.55964 8.5 4 7.94036 4 7.25V3.75C4 3.33579 4.33579 3 4.75 3ZM3.90663 10.2245C4.31766 10.2758 4.60932 10.6505 4.55806 11.0616C4.51977 11.3687 4.5 11.6819 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5C14.2239 19.5 16.2644 18.5332 17.6552 17H15.2618C14.8476 17 14.5118 16.6642 14.5118 16.25C14.5118 15.8358 14.8476 15.5 15.2618 15.5H18.7618C19.4522 15.5 20.0118 16.0596 20.0118 16.75V20.25C20.0118 20.6642 19.6761 21 19.2618 21C18.8476 21 18.5118 20.6642 18.5118 20.25V18.2768C16.8557 19.9576 14.5269 21 12 21C7.02944 21 3 16.9706 3 12C3 11.6197 3.02364 11.2445 3.06959 10.8759C3.12085 10.4649 3.4956 10.1733 3.90663 10.2245Z"
            />
        </BaseIcon>
    )
})

export const IconPause: IconComponent<IconProps> = forwardRef(function IconPause(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 4.75C4 3.7835 4.7835 3 5.75 3H8.25C9.2165 3 10 3.7835 10 4.75V19.25C10 20.2165 9.2165 21 8.25 21H5.75C4.7835 21 4 20.2165 4 19.25V4.75ZM5.75 4.5C5.61193 4.5 5.5 4.61193 5.5 4.75V19.25C5.5 19.3881 5.61193 19.5 5.75 19.5H8.25C8.38807 19.5 8.5 19.3881 8.5 19.25V4.75C8.5 4.61193 8.38807 4.5 8.25 4.5H5.75ZM14 4.75C14 3.7835 14.7835 3 15.75 3H18.25C19.2165 3 20 3.7835 20 4.75V19.25C20 20.2165 19.2165 21 18.25 21H15.75C14.7835 21 14 20.2165 14 19.25V4.75ZM15.75 4.5C15.6119 4.5 15.5 4.61193 15.5 4.75V19.25C15.5 19.3881 15.6119 19.5 15.75 19.5H18.25C18.3881 19.5 18.5 19.3881 18.5 19.25V4.75C18.5 4.61193 18.3881 4.5 18.25 4.5H15.75Z"
            />
        </BaseIcon>
    )
})

export const IconPauseFilled: IconComponent<IconProps> = forwardRef(function IconPauseFilled(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M5.75 3C4.7835 3 4 3.7835 4 4.75V19.25C4 20.2165 4.7835 21 5.75 21H8.25C9.2165 21 10 20.2165 10 19.25V4.75C10 3.7835 9.2165 3 8.25 3H5.75Z" />
            <path d="M15.75 3C14.7835 3 14 3.7835 14 4.75V19.25C14 20.2165 14.7835 21 15.75 21H18.25C19.2165 21 20 20.2165 20 19.25V4.75C20 3.7835 19.2165 3 18.25 3H15.75Z" />
        </BaseIcon>
    )
})

export const IconStopFilled: IconComponent<IconProps> = forwardRef(function IconStopFilled(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M5.324 3h13.352c.258 0 .494 0 .692.016.213.018.446.057.677.175.329.168.596.435.764.765.118.23.157.463.175.676.016.198.016.434.016.692v13.352c0 .258 0 .494-.016.692a1.775 1.775 0 0 1-.175.677 1.75 1.75 0 0 1-.765.764c-.23.118-.463.157-.676.175-.198.016-.434.016-.692.016H5.324c-.258 0-.494 0-.692-.016a1.775 1.775 0 0 1-.676-.175 1.75 1.75 0 0 1-.765-.765 1.774 1.774 0 0 1-.175-.676A9.053 9.053 0 0 1 3 18.676V5.324c0-.258 0-.494.016-.692.018-.213.057-.446.175-.676a1.75 1.75 0 0 1 .765-.765c.23-.118.463-.157.676-.175C4.83 3 5.066 3 5.324 3Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPlay: IconComponent<IconProps> = forwardRef(function IconPlay(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5 3.77537C5 2.41163 6.49076 1.57194 7.65703 2.27877L21.2276 10.5034C22.3516 11.1846 22.3516 12.8154 21.2276 13.4966L7.65703 21.7212C6.49076 22.428 5 21.5884 5 20.2246V3.77537ZM6.87958 3.56157C6.71297 3.4606 6.5 3.58055 6.5 3.77537V20.2246C6.5 20.4194 6.71296 20.5394 6.87958 20.4384L20.4502 12.2138C20.6108 12.1165 20.6108 11.8835 20.4502 11.7862L6.87958 3.56157Z"
            />
        </BaseIcon>
    )
})

export const IconPlayFilled: IconComponent<IconProps> = forwardRef(function IconPlayFilled(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M7.657 2.279C6.491 1.572 5 2.412 5 3.775v16.45c0 1.363 1.49 2.203 2.657 1.496l13.57-8.224a1.75 1.75 0 0 0 0-2.994L7.658 2.28Z" />
        </BaseIcon>
    )
})

export const IconShuffle: IconComponent<IconProps> = forwardRef(function IconShuffle(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17.7197 3.21967C18.0126 2.92678 18.4874 2.92678 18.7803 3.21967L21.7803 6.21967C22.0732 6.51256 22.0732 6.98744 21.7803 7.28033L18.7803 10.2803C18.4874 10.5732 18.0126 10.5732 17.7197 10.2803C17.4268 9.98744 17.4268 9.51256 17.7197 9.21967L19.4393 7.5H16.664C16.5978 7.5 16.5342 7.52632 16.4873 7.57317L5.62858 18.4253C5.30042 18.7533 4.85546 18.9375 4.39152 18.9375H2.75C2.33579 18.9375 2 18.6017 2 18.1875C2 17.7733 2.33579 17.4375 2.75 17.4375H4.39152C4.4578 17.4375 4.52136 17.4112 4.56824 17.3643L15.427 6.51219C15.7551 6.18423 16.2001 6 16.664 6H19.4393L17.7197 4.28033C17.4268 3.98744 17.4268 3.51256 17.7197 3.21967ZM2 5.75C2 5.33579 2.33579 5 2.75 5H4.33579C4.79992 5 5.24503 5.18437 5.57322 5.51256L9.03033 8.96967C9.32322 9.26256 9.32322 9.73744 9.03033 10.0303C8.73744 10.3232 8.26256 10.3232 7.96967 10.0303L4.51256 6.57322C4.46568 6.52634 4.40209 6.5 4.33579 6.5H2.75C2.33579 6.5 2 6.16421 2 5.75ZM17.6372 13.5313C17.9306 13.2389 18.4055 13.2397 18.6979 13.5331L21.7812 16.6268C22.0729 16.9195 22.0729 17.393 21.7812 17.6857L18.6979 20.7794C18.4055 21.0728 17.9306 21.0736 17.6372 20.7812C17.3438 20.4888 17.343 20.014 17.6354 19.7206L19.4436 17.9062H16.5304C16.061 17.9062 15.6114 17.7177 15.2824 17.383L12.9651 15.0258C12.6748 14.7304 12.6788 14.2555 12.9742 13.9651C13.2696 13.6748 13.7445 13.6788 14.0349 13.9742L16.3521 16.3315L15.8172 16.8573L16.3521 16.3315C16.3991 16.3793 16.4633 16.4062 16.5304 16.4062H19.4436L17.6354 14.5919C17.343 14.2985 17.3438 13.8237 17.6372 13.5313Z"
            />
        </BaseIcon>
    )
})

export const IconPlaylist: IconComponent<IconProps> = forwardRef(function IconPlaylist(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <g clipPath="url(#a)">
                <path d="M15.51 12.044a.802.802 0 0 0-1.218.686v7.54a.802.802 0 0 0 1.217.686l6.22-3.77a.802.802 0 0 0 0-1.372l-6.22-3.77Z" />
                <path
                    fillRule="evenodd"
                    d="M2.5 5.75A.75.75 0 0 1 3.25 5h13.51a.75.75 0 0 1 0 1.5H3.25a.75.75 0 0 1-.75-.75Zm0 6.25a.75.75 0 0 1 .75-.75h8.5a.75.75 0 0 1 0 1.5h-8.5A.75.75 0 0 1 2.5 12Zm0 6.25a.75.75 0 0 1 .75-.75h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1-.75-.75Z"
                    clipRule="evenodd"
                />
            </g>
            <defs>
                <clipPath id="a">
                    <path d="M0 0h24v24H0z" />
                </clipPath>
            </defs>
        </BaseIcon>
    )
})

export const IconPlug: IconComponent<IconProps> = forwardRef(function IconPlug(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M4.301 11.658a1.058 1.058 0 0 1 0 1.496L3.18 14.276a1.058 1.058 0 0 0 0 1.495l.155.154a.84.84 0 0 0 1.187 0c.37-.37.986-.313 1.31.099.211.27.447.551.704.834 1.244 1.365 3.193 2.962 5.619 2.962 1.358 0 2.474-.452 3.236-.887a.634.634 0 0 1 .763.09l2.668 2.667a1.058 1.058 0 1 0 1.495-1.495l-2.668-2.668a.634.634 0 0 1-.089-.763c.435-.762.888-1.878.888-3.236 0-2.38-1.454-4.411-2.72-5.75a16.253 16.253 0 0 0-1.018-.985.698.698 0 0 1-.041-1.014.675.675 0 0 0 0-.955l-.27-.27a1.058 1.058 0 0 0-1.496 0l-.748.747a1.058 1.058 0 0 1-1.496 0L7.667 2.31A1.058 1.058 0 1 0 6.17 3.806l2.991 2.991a1.058 1.058 0 0 1 0 1.496l-1.87 1.87a1.058 1.058 0 0 1-1.495 0L2.806 7.17A1.058 1.058 0 1 0 1.31 8.667L4.3 11.658Zm9.052-3.24c-.4-.358-1.005-.32-1.384.06l-5.15 5.15a.107.107 0 0 0-.014.134A13.9 13.9 0 0 0 8.1 15.433c1.14 1.253 2.557 2.272 4.055 2.272.885 0 1.639-.295 2.186-.608a4.897 4.897 0 0 0 .776-.55 1.713 1.713 0 0 0 .057-.057 4.9 4.9 0 0 0 .55-.775c.313-.548.608-1.302.608-2.187 0-1.545-.977-3.065-2.141-4.297-.284-.301-.57-.574-.837-.813Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPlus: IconComponent<IconProps> = forwardRef(function IconPlus(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 3a.75.75 0 0 1 .75.75v7.5h7.5a.75.75 0 0 1 0 1.5h-7.5v7.5a.75.75 0 0 1-1.5 0v-7.5h-7.5a.75.75 0 0 1 0-1.5h7.5v-7.5A.75.75 0 0 1 12 3Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPlusSmall: IconComponent<IconProps> = forwardRef(function IconPlusSmall(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 6a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 12 6Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconPlusSquare: IconComponent<IconProps> = forwardRef(function IconPlusSquare(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="m4.296 20.141.34-.668-.34.668Zm-.437-.437-.668.34.668-.34Zm16.282 0-.668-.34.668.34Zm-.437.437-.34-.668.34.668Zm.437-15.845-.668.34.668-.34Zm-.437-.437.34-.668-.34.668Zm-15.845.437-.668-.34.668.34Zm.437-.437-.34-.668.34.668ZM15.25 12.75a.75.75 0 0 0 0-1.5v1.5Zm-6.493-1.5a.75.75 0 0 0 0 1.5v-1.5Zm2.493 3.993a.75.75 0 0 0 1.5 0h-1.5Zm1.5-6.485a.75.75 0 0 0-1.5 0h1.5ZM19.5 5.35v13.3H21V5.35h-1.5Zm-.85 14.15H5.35V21h13.3v-1.5ZM4.5 18.65V5.35H3v13.3h1.5ZM5.35 4.5h13.3V3H5.35v1.5Zm0 15c-.292 0-.467 0-.596-.011-.12-.01-.134-.025-.117-.016l-.681 1.336c.23.118.463.157.676.175.205.017.45.016.718.016v-1.5ZM3 18.65c0 .268 0 .514.016.718.018.213.057.446.175.677l1.336-.682c.009.017-.006.003-.016-.117a8.337 8.337 0 0 1-.011-.596H3Zm1.636.823a.25.25 0 0 1-.109-.11l-1.336.681c.168.33.435.598.765.765l.68-1.336ZM19.5 18.65c0 .292 0 .467-.011.596-.01.12-.025.134-.016.117l1.336.681c.118-.23.157-.463.175-.676.017-.204.016-.45.016-.718h-1.5ZM18.65 21c.268 0 .514 0 .718-.016.213-.018.446-.057.677-.175l-.682-1.336c.017-.009.003.006-.117.016-.13.01-.304.011-.596.011V21Zm.823-1.637a.251.251 0 0 1-.11.11l.681 1.336a1.75 1.75 0 0 0 .765-.765l-1.336-.68ZM21 5.35c0-.268 0-.513-.016-.718a1.775 1.775 0 0 0-.175-.676l-1.336.68c-.009-.016.006-.002.016.118.01.13.011.304.011.596H21Zm-2.35-.85c.292 0 .467 0 .596.011.12.01.134.025.117.016l.681-1.336a1.774 1.774 0 0 0-.676-.175C19.164 3 18.918 3 18.65 3v1.5Zm2.16-.544a1.75 1.75 0 0 0-.765-.765l-.682 1.336a.25.25 0 0 1 .11.11l1.336-.681ZM4.5 5.35c0-.292 0-.467.011-.596.01-.12.025-.134.016-.117l-1.336-.681c-.118.23-.157.463-.175.676C3 4.837 3 5.082 3 5.35h1.5ZM5.35 3c-.268 0-.513 0-.718.016a1.774 1.774 0 0 0-.676.175l.68 1.336c-.016.009-.002-.006.118-.016.13-.01.304-.011.596-.011V3Zm-.823 1.636a.25.25 0 0 1 .11-.109l-.681-1.336a1.75 1.75 0 0 0-.765.765l1.336.68ZM15.25 11.25H8.757v1.5h6.493v-1.5Zm-2.5 3.993V8.758h-1.5v6.485h1.5Z" />
        </BaseIcon>
    )
})

export const IconReceipt: IconComponent<IconProps> = forwardRef(function IconReceipt(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4 3.75C4 2.784 4.784 2 5.75 2h12.5c.966 0 1.75.784 1.75 1.75v17.5a.75.75 0 0 1-1.243.566l-2.065-1.799-1.838 1.773a.75.75 0 0 1-1.041 0L12 20.042l-1.813 1.748a.75.75 0 0 1-1.04 0l-1.84-1.773-2.064 1.799A.75.75 0 0 1 4 21.25V3.75Zm1.75-.25a.25.25 0 0 0-.25.25v15.852l1.34-1.168a.75.75 0 0 1 1.014.026l1.813 1.748 1.812-1.748a.75.75 0 0 1 1.042 0l1.812 1.748 1.813-1.748a.75.75 0 0 1 1.013-.026l1.341 1.168V3.75a.25.25 0 0 0-.25-.25H5.75ZM8 7.75A.75.75 0 0 1 8.75 7h6.5a.75.75 0 0 1 0 1.5h-6.5A.75.75 0 0 1 8 7.75Zm0 4a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1-.75-.75Z"
            />
        </BaseIcon>
    )
})

export const IconRedo: IconComponent<IconProps> = forwardRef(function IconRedo(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M4.479 12a7.5 7.5 0 0 1 7.5-7.5c2.422 0 4.076.963 5.727 2.75H15.25a.75.75 0 0 0 0 1.5h3.5c.69 0 1.25-.56 1.25-1.25V4a.75.75 0 0 0-1.5 0v1.908C16.738 4.104 14.777 3 11.979 3a9 9 0 1 0 8.487 12 .75.75 0 0 0-1.414-.5A7.5 7.5 0 0 1 4.479 12Z" />
        </BaseIcon>
    )
})

export const IconRecord: IconComponent<IconProps> = forwardRef(function IconRecord(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 16.6944 7.30558 20.5 12 20.5C16.6944 20.5 20.5 16.6944 20.5 12C20.5 7.30558 16.6944 3.5 12 3.5ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z"
            />
            <path d="M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" />
        </BaseIcon>
    )
})

export const IconRetention: IconComponent<IconProps> = forwardRef(function IconRetention(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1v3.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4Z" />
            <path
                fillRule="evenodd"
                d="M7.5 4H4v3.5h3.5V4ZM4 3a1 1 0 0 0-1 1v3.5a1 1 0 0 0 1 1h3.5a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4Z"
                clipRule="evenodd"
            />
            <path d="M3 10.5a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3.5Z" />
            <path
                fillRule="evenodd"
                d="M7.5 10.5H4V14h3.5v-3.5ZM4 9.5a1 1 0 0 0-1 1V14a1 1 0 0 0 1 1h3.5a1 1 0 0 0 1-1v-3.5a1 1 0 0 0-1-1H4ZM7.5 17H4v3.5h3.5V17ZM4 16a1 1 0 0 0-1 1v3.5a1 1 0 0 0 1 1h3.5a1 1 0 0 0 1-1V17a1 1 0 0 0-1-1H4Z"
                clipRule="evenodd"
            />
            <path d="M9.5 4a1 1 0 0 1 1-1H14a1 1 0 0 1 1 1v3.5a1 1 0 0 1-1 1h-3.5a1 1 0 0 1-1-1V4Z" />
            <path
                fillRule="evenodd"
                d="M14 4h-3.5v3.5H14V4Zm-3.5-1a1 1 0 0 0-1 1v3.5a1 1 0 0 0 1 1H14a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1h-3.5ZM14 10.5h-3.5V14H14v-3.5Zm-3.5-1a1 1 0 0 0-1 1V14a1 1 0 0 0 1 1H14a1 1 0 0 0 1-1v-3.5a1 1 0 0 0-1-1h-3.5ZM20.5 4H17v3.5h3.5V4ZM17 3a1 1 0 0 0-1 1v3.5a1 1 0 0 0 1 1h3.5a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H17Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconRevert: IconComponent<IconProps> = forwardRef(function IconRevert(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M13 21a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM21 11a1 1 0 1 0-2 0 1 1 0 0 0 2 0ZM19.93 14.268a1 1 0 1 1-1 1.732 1 1 0 0 1 1-1.732ZM17.368 19.294a1 1 0 1 0-1-1.732 1 1 0 0 0 1 1.732ZM18.927 8a1 1 0 1 1-1-1.732 1 1 0 0 1 1 1.732Z" />
            <path
                fillRule="evenodd"
                d="M12 4.5a7.5 7.5 0 0 0-3.5 14.135V14.75a.75.75 0 0 1 1.5 0v5.5a.75.75 0 0 1-.75.75h-5.5a.75.75 0 0 1 0-1.5h3.274a9 9 0 0 1 8.521-15.775.75.75 0 0 1-.591 1.379A7.473 7.473 0 0 0 12 4.5Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconRewind: IconComponent<IconProps> = forwardRef(function IconRewind(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M13 .786a.785.785 0 0 0-.46-.712.812.812 0 0 0-.852.108l-2.4 1.964A.78.78 0 0 0 9 2.75a.78.78 0 0 0 .288.604l2.4 1.964c.238.195.57.237.851.108A.785.785 0 0 0 13 4.714V3.558A8.501 8.501 0 0 1 12 20.5 8.5 8.5 0 0 1 5.801 6.184a.75.75 0 0 0-1.094-1.026A9.968 9.968 0 0 0 2 12c0 5.523 4.477 10 10 10s10-4.477 10-10c0-5.185-3.947-9.449-9-9.95V.785Z" />
        </BaseIcon>
    )
})

export const IconRewindPlay: IconComponent<IconProps> = forwardRef(function IconRewindPlay(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <g clipPath="url(#icon/rewind__a)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13 .857a.863.863 0 0 0-.46-.776.758.758 0 0 0-.852.118l-2.4 2.143A.883.883 0 0 0 9 3c0 .254.105.496.288.658l2.4 2.143c.238.213.57.26.851.118A.863.863 0 0 0 13 5.143V4.062A8.001 8.001 0 0 1 12 20 8 8 0 0 1 5.777 6.972a1 1 0 0 0-1.554-1.258A9.962 9.962 0 0 0 2 12c0 5.523 4.477 10 10 10s10-4.477 10-10c0-5.185-3.947-9.449-9-9.95V.856ZM9 8.703c0-.547.611-.884 1.09-.6l5.564 3.297a.693.693 0 0 1 0 1.2l-5.565 3.297c-.478.284-1.089-.053-1.089-.6V8.703Z"
                />
            </g>
            <defs>
                <clipPath id="icon/rewind__a">
                    <path d="M0 0h24v24H0z" />
                </clipPath>
            </defs>
        </BaseIcon>
    )
})

export const IconRibbon: IconComponent<IconProps> = forwardRef(function IconRibbon(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 2.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM4 9a8 8 0 1 1 13 6.245v7.005a.75.75 0 0 1-1.07.679L12 21.079l-3.93 1.85A.75.75 0 0 1 7 22.25v-7.005A7.985 7.985 0 0 1 4 9Zm4.5 7.196v4.872l3.18-1.497a.75.75 0 0 1 .64 0l3.18 1.497v-4.872A7.969 7.969 0 0 1 12 17a7.969 7.969 0 0 1-3.5-.804Z"
            />
        </BaseIcon>
    )
})

export const IconRocket: IconComponent<IconProps> = forwardRef(function IconRocket(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M5.05 16.712a.75.75 0 0 1 .016 1.06L2.288 20.63a.75.75 0 0 1-1.076-1.046l2.778-2.857a.75.75 0 0 1 1.06-.015Zm2.223 2.286a.75.75 0 0 1 .015 1.06L5.62 21.773a.75.75 0 1 1-1.075-1.046l1.666-1.714a.75.75 0 0 1 1.06-.015ZM15.5 7.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm-2.5 1a2.5 2.5 0 1 1 5 0 2.5 2.5 0 0 1-5 0Z"
                clipRule="evenodd"
            />
            <path
                fillRule="evenodd"
                d="M12.97 3.469c2.038-1.155 4.69-1.66 8.27-1.404a.75.75 0 0 1 .695.695c.256 3.58-.249 6.232-1.403 8.27-1.08 1.905-2.683 3.193-4.574 4.198v2.073c0 .51-.223.996-.611 1.328l-3.139 2.69a.75.75 0 0 1-1.199-.33l-.002-.007-.01-.029-.04-.115c-.037-.101-.091-.25-.162-.433a24.226 24.226 0 0 0-.604-1.435c-.526-1.142-1.229-2.408-1.99-3.17-.763-.763-2.029-1.465-3.17-1.99a24.308 24.308 0 0 0-1.984-.807l-.029-.01-.006-.002H3.01a.75.75 0 0 1-.33-1.2l2.69-3.138a1.75 1.75 0 0 1 1.328-.61h2.073c1.005-1.892 2.293-3.495 4.198-4.574Zm-8.454 8.486c.331.133.722.299 1.142.492 1.167.538 2.641 1.33 3.603 2.292.962.962 1.755 2.436 2.292 3.603.193.42.359.81.492 1.142l2.326-1.994a.25.25 0 0 0 .087-.19v-2.529a.75.75 0 0 1 .415-.67c1.914-.958 3.394-2.116 4.353-3.81.9-1.587 1.389-3.725 1.251-6.768-3.043-.138-5.18.351-6.768 1.25-1.694.96-2.852 2.44-3.81 4.354a.75.75 0 0 1-.67.415h-2.53a.25.25 0 0 0-.19.087l-1.993 2.326Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconSampling: IconComponent<IconProps> = forwardRef(function IconSampling(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M11 16a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z" />
            <path
                fillRule="evenodd"
                d="M11 4.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM3 11a8 8 0 1 1 14.162 5.102l3.618 3.618a.75.75 0 1 1-1.06 1.06l-3.618-3.618A8 8 0 0 1 3 11Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconSearch: IconComponent<IconProps> = forwardRef(function IconSearch(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11 4.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM3 11a8 8 0 1 1 14.162 5.102l3.618 3.618a.75.75 0 1 1-1.06 1.06l-3.618-3.618A8 8 0 0 1 3 11Z"
            />
        </BaseIcon>
    )
})

export const IconSend: IconComponent<IconProps> = forwardRef(function IconSend(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M19.325 3.097c.974-.32 1.898.604 1.578 1.578l-5.688 17.281c-.383 1.166-2.042 1.139-2.387-.04l-2.432-8.312-8.313-2.432c-1.178-.345-1.205-2.004-.04-2.387l17.282-5.688Zm-.002 1.58L3.277 9.958l7.671 2.245c.41.12.729.44.849.849l2.245 7.671 5.281-16.046Z"
            />
        </BaseIcon>
    )
})

export const IconServer: IconComponent<IconProps> = forwardRef(function IconServer(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 5.75C2 4.784 2.784 4 3.75 4h16.5c.966 0 1.75.784 1.75 1.75v12.5A1.75 1.75 0 0 1 20.25 20H3.75A1.75 1.75 0 0 1 2 18.25V5.75Zm1.75-.25a.25.25 0 0 0-.25.25v5.5h17v-5.5a.25.25 0 0 0-.25-.25H3.75Zm16.75 7.25h-17v5.5c0 .138.112.25.25.25h16.5a.25.25 0 0 0 .25-.25v-5.5Z"
            />
            <path d="M7 8.375a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm0 7.25a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z" />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.5 8.375a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm0 7.25a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-6.75a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Zm0 7.25a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1Z"
            />
        </BaseIcon>
    )
})

export const IconShare: IconComponent<IconProps> = forwardRef(function IconShare(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M16.22 3.72a.75.75 0 0 1 1.06 0l3.5 3.5a.75.75 0 0 1 0 1.06l-3.5 3.5a.75.75 0 1 1-1.06-1.06l2.22-2.22h-5.69a4.25 4.25 0 0 0-4.25 4.25v1a.75.75 0 0 1-1.5 0v-1A5.75 5.75 0 0 1 12.75 7h5.69l-2.22-2.22a.75.75 0 0 1 0-1.06ZM2.75 7a.75.75 0 0 1 .75.75v10.5c0 .138.112.25.25.25h15.5a.75.75 0 0 1 0 1.5H3.75A1.75 1.75 0 0 1 2 18.25V7.75A.75.75 0 0 1 2.75 7Z"
            />
        </BaseIcon>
    )
})

export const IconShield: IconComponent<IconProps> = forwardRef(function IconShield(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.0976 3.10613C12.0352 3.07966 11.9648 3.07965 11.9024 3.10613L4.65236 6.18189C4.55999 6.22107 4.5 6.31169 4.5 6.41203V13C4.5 17.1421 7.85786 20.5 12 20.5C16.1421 20.5 19.5 17.1421 19.5 13V6.41203C19.5 6.31169 19.44 6.22107 19.3476 6.18189L12.0976 3.10613ZM11.3165 1.72526C11.7533 1.53995 12.2467 1.53995 12.6835 1.72525L19.9335 4.80101C20.5801 5.07533 21 5.70965 21 6.41203V13C21 17.9706 16.9706 22 12 22C7.02944 22 3 17.9706 3 13V6.41203C3 5.70965 3.41994 5.07533 4.06654 4.80101L11.3165 1.72526ZM15.7702 9.20976C16.0686 9.49708 16.0776 9.97187 15.7902 10.2702L11.4569 14.7702C11.3155 14.9171 11.1205 15 10.9167 15C10.7128 15 10.5178 14.9171 10.3764 14.7702L8.20976 12.5202C7.92244 12.2219 7.9314 11.7471 8.22977 11.4598C8.52814 11.1724 9.00292 11.1814 9.29024 11.4798L10.9167 13.1688L14.7098 9.22977C14.9971 8.93141 15.4719 8.92245 15.7702 9.20976Z"
            />
        </BaseIcon>
    )
})

export const IconShieldPeople: IconComponent<IconProps> = forwardRef(
    function IconShieldPeople(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M11.447 2.144c.359-.12.747-.12 1.106 0l7.25 2.417A1.75 1.75 0 0 1 21 6.22v5.691c0 2.738-1.183 4.758-2.859 6.348-1.647 1.563-3.8 2.737-5.785 3.808a.75.75 0 0 1-.712 0c-1.984-1.07-4.138-2.245-5.785-3.808C4.184 16.67 3 14.65 3 11.912V6.221a1.75 1.75 0 0 1 1.197-1.66l7.25-2.417Zm.632 1.423a.25.25 0 0 0-.158 0L4.67 5.984a.25.25 0 0 0-.171.237v5.691c0 2.235.941 3.884 2.391 5.26 1.391 1.32 3.219 2.36 5.109 3.383 1.89-1.024 3.718-2.064 5.109-3.383 1.45-1.376 2.391-3.025 2.391-5.26V6.221a.25.25 0 0 0-.17-.237l-7.25-2.417Z"
                    clipRule="evenodd"
                />
                <path
                    fillRule="evenodd"
                    d="M18.164 13.94a5.464 5.464 0 0 0-2.134-1.268.5.5 0 0 0-.304.952c.812.26 1.496.723 1.986 1.292.179-.308.33-.632.452-.976ZM8.75 17.482h6.02c.294 0 .58-.107.787-.305a.942.942 0 0 0 .29-.849c-.4-2.328-2.845-4.114-5.667-4.114-1.715 0-3.29.66-4.353 1.701.12.347.273.675.451.985.838-.986 2.248-1.686 3.902-1.686 2.445 0 4.357 1.53 4.675 3.248a.172.172 0 0 1-.085.02H7.54c.37.357.778.688 1.21 1ZM8.465 9c0-.781.718-1.5 1.715-1.5.997 0 1.716.719 1.716 1.5s-.719 1.5-1.716 1.5S8.465 9.781 8.465 9Zm1.715-2.5C8.73 6.5 7.465 7.572 7.465 9s1.265 2.5 2.715 2.5 2.716-1.072 2.716-2.5-1.265-2.5-2.716-2.5Zm3.64 0a.5.5 0 1 0 0 1c.998 0 1.716.719 1.716 1.5s-.718 1.5-1.716 1.5a.5.5 0 1 0 0 1c1.45 0 2.716-1.072 2.716-2.5s-1.265-2.5-2.716-2.5Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconShieldLock: IconComponent<IconProps> = forwardRef(function IconShieldLock(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M11.447 2.144c.359-.12.747-.12 1.106 0l7.25 2.417A1.75 1.75 0 0 1 21 6.22v5.691c0 2.738-1.183 4.758-2.859 6.348-1.647 1.563-3.8 2.737-5.785 3.808a.75.75 0 0 1-.712 0c-1.984-1.07-4.138-2.245-5.785-3.808C4.184 16.67 3 14.65 3 11.912V6.221a1.75 1.75 0 0 1 1.197-1.66l7.25-2.417Zm.632 1.423a.25.25 0 0 0-.158 0L4.67 5.984a.25.25 0 0 0-.171.237v5.691c0 2.235.941 3.884 2.391 5.26 1.391 1.32 3.219 2.36 5.109 3.383 1.89-1.024 3.718-2.064 5.109-3.383 1.45-1.376 2.391-3.025 2.391-5.26V6.221a.25.25 0 0 0-.17-.237l-7.25-2.417Zm.671 8.805a2.251 2.251 0 1 0-1.5 0v2.378a.75.75 0 0 0 1.5 0v-2.378Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconSidebarClose: IconComponent<IconProps> = forwardRef(
    function IconSidebarClose(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M12.47 9.22a.75.75 0 0 0 0 1.06L14.19 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06l2.25-2.25a.75.75 0 0 0 0-1.06l-2.25-2.25a.75.75 0 0 0-1.06 0Z"
                    clipRule="evenodd"
                />
                <path
                    fillRule="evenodd"
                    d="M4.75 4.5a.25.25 0 0 0-.25.25v14.5c0 .138.112.25.25.25h14.5a.25.25 0 0 0 .25-.25V4.75a.25.25 0 0 0-.25-.25H4.75ZM3 4.75C3 3.784 3.784 3 4.75 3h14.5c.966 0 1.75.784 1.75 1.75v14.5A1.75 1.75 0 0 1 19.25 21H4.75A1.75 1.75 0 0 1 3 19.25V4.75Z"
                    clipRule="evenodd"
                />
                <path fillRule="evenodd" d="M7.5 20.25V3.75H9v16.5H7.5Z" clipRule="evenodd" />
            </BaseIcon>
        )
    }
)

export const IconSidebarOpen: IconComponent<IconProps> = forwardRef(function IconSidebarOpen(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M15.78 9.22a.75.75 0 0 1 0 1.06L14.06 12l1.72 1.72a.75.75 0 1 1-1.06 1.06l-2.25-2.25a.75.75 0 0 1 0-1.06l2.25-2.25a.75.75 0 0 1 1.06 0Z"
                clipRule="evenodd"
            />
            <path
                fillRule="evenodd"
                d="M4.75 4.5a.25.25 0 0 0-.25.25v14.5c0 .138.112.25.25.25h14.5a.25.25 0 0 0 .25-.25V4.75a.25.25 0 0 0-.25-.25H4.75ZM3 4.75C3 3.784 3.784 3 4.75 3h14.5c.966 0 1.75.784 1.75 1.75v14.5A1.75 1.75 0 0 1 19.25 21H4.75A1.75 1.75 0 0 1 3 19.25V4.75Z"
                clipRule="evenodd"
            />
            <path fillRule="evenodd" d="M7.5 20.25V3.75H9v16.5H7.5Z" clipRule="evenodd" />
        </BaseIcon>
    )
})

export const IconSidePanel: IconComponent<IconProps> = forwardRef(function IconSidePanel(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="m4.296 20.141.34-.668-.34.668Zm-.437-.437-.668.34.668-.34Zm16.282 0-.668-.34.668.34Zm-.437.437-.34-.668.34.668Zm.437-15.845-.668.34.668-.34Zm-.437-.437.34-.668-.34.668Zm-15.845.437-.668-.34.668.34Zm.437-.437-.34-.668.34.668ZM13.5 20.25V21H15v-.75h-1.5ZM15 3.75V3h-1.5v.75H15Zm4.5 1.6v13.3H21V5.35h-1.5Zm-.85 14.15H5.35V21h13.3v-1.5ZM4.5 18.65V5.35H3v13.3h1.5ZM5.35 4.5h13.3V3H5.35v1.5Zm0 15c-.292 0-.467 0-.596-.011-.12-.01-.134-.025-.117-.016l-.681 1.336c.23.118.463.157.676.175.205.017.45.016.718.016v-1.5ZM3 18.65c0 .268 0 .514.016.718.018.213.057.446.175.677l1.336-.682c.009.017-.006.003-.016-.117a8.337 8.337 0 0 1-.011-.596H3Zm1.636.823a.25.25 0 0 1-.109-.11l-1.336.681c.168.33.435.598.765.765l.68-1.336ZM19.5 18.65c0 .292 0 .467-.011.596-.01.12-.025.134-.016.117l1.336.681c.118-.23.157-.463.175-.676.017-.204.016-.45.016-.718h-1.5ZM18.65 21c.268 0 .514 0 .718-.016.213-.018.446-.057.677-.175l-.682-1.336c.017-.009.003.006-.117.016-.13.01-.304.011-.596.011V21Zm.823-1.637a.25.25 0 0 1-.11.11l.681 1.336a1.75 1.75 0 0 0 .765-.765l-1.336-.68ZM21 5.35c0-.268 0-.513-.016-.718a1.775 1.775 0 0 0-.175-.676l-1.336.68c-.009-.016.006-.002.016.118.01.13.011.304.011.596H21Zm-2.35-.85c.292 0 .467 0 .596.011.12.01.134.025.117.016l.681-1.336a1.774 1.774 0 0 0-.676-.175C19.164 3 18.918 3 18.65 3v1.5Zm2.16-.544a1.75 1.75 0 0 0-.765-.765l-.682 1.336a.25.25 0 0 1 .11.11l1.336-.681ZM4.5 5.35c0-.292 0-.467.011-.596.01-.12.025-.134.016-.117l-1.336-.681c-.118.23-.157.463-.175.676C3 4.837 3 5.082 3 5.35h1.5ZM5.35 3c-.268 0-.513 0-.718.016a1.774 1.774 0 0 0-.676.175l.68 1.336c-.016.009-.002-.006.118-.016.13-.01.304-.011.596-.011V3Zm-.823 1.636a.25.25 0 0 1 .11-.109l-.681-1.336a1.75 1.75 0 0 0-.765.765l1.336.68ZM15 20.25V3.75h-1.5v16.5H15Z" />
        </BaseIcon>
    )
})

export const IconSort: IconComponent<IconProps> = forwardRef(function IconSort(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M7 3a.75.75 0 0 1 .75.75v14.69l1.72-1.72a.75.75 0 1 1 1.06 1.06l-3 3a.75.75 0 0 1-1.06 0l-3-3a.75.75 0 1 1 1.06-1.06l1.72 1.72V3.75A.75.75 0 0 1 7 3Zm10.5 0a.75.75 0 0 1 .675.424l3.142 6.5a.75.75 0 0 1-1.35.652L18.962 8.5H16.03l-1.008 2.077a.75.75 0 1 1-1.35-.654l3.153-6.5A.75.75 0 0 1 17.5 3Zm-.743 4h1.48L17.5 5.471 16.757 7ZM14 13.75a.75.75 0 0 1 .75-.75h5.5a.75.75 0 0 1 .573 1.235L16.367 19.5h3.883a.75.75 0 0 1 0 1.5h-5.5a.75.75 0 0 1-.572-1.235l4.455-5.265H14.75a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconStack: IconComponent<IconProps> = forwardRef(function IconStack(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12.11 4.526a.245.245 0 0 0-.22 0L3.643 8.63a.261.261 0 0 0 0 .465l8.249 4.104c.069.035.15.035.218 0l8.25-4.104a.261.261 0 0 0 0-.465l-8.25-4.104Zm-.887-1.343a1.744 1.744 0 0 1 1.554 0l8.25 4.104c1.297.646 1.297 2.505 0 3.15L17.887 12l3.14 1.562c1.297.646 1.297 2.505 0 3.15l-8.25 4.105a1.745 1.745 0 0 1-1.554 0l-8.25-4.105c-1.297-.645-1.297-2.504 0-3.15L6.113 12l-3.14-1.562c-1.297-.646-1.297-2.505 0-3.15l8.25-4.105Zm-3.426 9.655-4.155 2.067a.261.261 0 0 0 0 .465l8.249 4.104c.069.035.15.035.218 0l8.25-4.104a.261.261 0 0 0 0-.465l-4.156-2.067-3.426 1.705a1.745 1.745 0 0 1-1.554 0l-3.426-1.705Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconStethoscope: IconComponent<IconProps> = forwardRef(function IconStethoscope(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M17.833 8.667c1 0 2 .667 2.333 1.667.5 1.332-.166 2.75-1.5 3.167v1.416c0 3-2.416 5.417-5.416 5.417-3 0-5.416-2.417-5.416-5.417v-.833L5.25 12c-1-.75-1.584-2-1.584-3.25V4.5c0-.5.334-.834.834-.834h1.666c.5 0 .834.334.835.834 0 .5-.334.834-.834.834h-.833V8.75c0 .75.332 1.5.916 1.917l2.416 1.917 2.418-1.918C11.666 10.25 12 9.5 12 8.75V5.334h-.834c-.5 0-.833-.334-.833-.834 0-.5.333-.834.833-.834h1.667c.5 0 .833.334.833.834v4.25c0 1.25-.582 2.416-1.582 3.25L9.5 14.082v.833a3.735 3.735 0 0 0 3.75 3.751 3.734 3.734 0 0 0 3.75-3.75v-1.416a2.427 2.427 0 0 1-1.667-2.334c0-1.416 1.083-2.5 2.5-2.5Zm.017 3.533a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconSpinner: IconComponent<IconProps> = forwardRef(function Iconpinner(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 2a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 12 2ZM4.93 4.929a.75.75 0 0 1 1.06 0l2.475 2.475a.75.75 0 1 1-1.061 1.06L4.929 5.99a.75.75 0 0 1 0-1.06Zm14.142 0a.75.75 0 0 1 0 1.061l-2.475 2.475a.75.75 0 0 1-1.061-1.06l2.475-2.476a.75.75 0 0 1 1.06 0ZM2 12.001a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5A.75.75 0 0 1 2 12Zm15 0a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5A.75.75 0 0 1 17 12Zm-1.464 3.534a.75.75 0 0 1 1.06 0l2.475 2.475a.75.75 0 0 1-1.06 1.06l-2.475-2.474a.75.75 0 0 1 0-1.06Zm-7.071.001a.75.75 0 0 1 0 1.06L5.99 19.073a.75.75 0 1 1-1.06-1.061l2.474-2.475a.75.75 0 0 1 1.061 0ZM12 17a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 12 17Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconSplitTesting: IconComponent<IconProps> = forwardRef(
    function IconSplitTesting(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path d="m8.456 12.624-1.25-.14a.203.203 0 0 1-.185-.139l-.51-1.134a.398.398 0 0 0-.718 0l-.509 1.111c-.046.07-.116.14-.208.14l-1.25.139c-.324.046-.463.463-.232.671l.927.834c.07.07.092.14.07.232l-.233 1.25c-.07.324.277.579.579.417l1.088-.625c.07-.046.162-.046.255 0l1.088.625c.301.162.649-.092.58-.417l-.256-1.227c-.023-.093 0-.162.07-.232l.926-.834a.383.383 0 0 0-.232-.671Z" />
                <path d="M9.708 6h-7.11A1.6 1.6 0 0 0 1 7.598v9.055c0 1.042.857 1.898 1.899 1.898h6.508a1.907 1.907 0 0 0 1.898-1.898V7.598c0-.88-.718-1.598-1.597-1.598ZM6.604 7.158c.324 0 .579.255.579.579a.574.574 0 0 1-.579.579.574.574 0 0 1-.579-.58c0-.323.255-.578.579-.578Zm-1.783 0c.324 0 .579.255.579.579a.574.574 0 0 1-.579.579.574.574 0 0 1-.579-.58c0-.323.255-.578.58-.578Zm-1.76 0c.324 0 .579.255.579.579a.574.574 0 0 1-.58.579.574.574 0 0 1-.578-.58c-.023-.323.255-.578.579-.578Zm7.226 9.495a.88.88 0 0 1-.88.88H2.898a.88.88 0 0 1-.88-.88V9.914c0-.231.185-.417.417-.417H9.87c.231 0 .417.186.417.417v6.739ZM21.402 6h-7.11a1.6 1.6 0 0 0-1.597 1.598v9.055c0 1.042.856 1.898 1.899 1.898H21.1A1.907 1.907 0 0 0 23 16.653V7.598C23 6.718 22.282 6 21.402 6ZM18.3 7.158c.324 0 .579.255.579.579a.574.574 0 0 1-.58.579.574.574 0 0 1-.578-.58c0-.323.255-.578.579-.578Zm-1.783 0c.324 0 .579.255.579.579a.574.574 0 0 1-.58.579.574.574 0 0 1-.578-.58c0-.323.255-.578.579-.578Zm-1.76 0c.323 0 .578.255.578.579a.574.574 0 0 1-.579.579.574.574 0 0 1-.579-.58c-.022-.323.256-.578.58-.578Zm7.225 9.495a.88.88 0 0 1-.88.88h-6.508a.88.88 0 0 1-.88-.88V9.914c0-.231.186-.417.417-.417h7.434c.232 0 .417.186.417.417v6.739Z" />
                <path d="M18.299 11.558a.525.525 0 0 0-.927 0l-1.829 3.427a.521.521 0 0 0 .463.765h3.682a.52.52 0 0 0 .463-.765l-1.852-3.427Z" />
            </BaseIcon>
        )
    }
)

export const IconSpotlight: IconComponent<IconProps> = forwardRef(function IconSpotlight(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="m13.71 3.223.145-.205c2.122-2.688 10.099.436 7.7 3.7l-6.423 6.002 4.235 8.298c.293.574-.242 1.278-.682.851l-6.693-6.23-6.687 6.23c-.442.425-.977-.277-.683-.85l4.238-8.296L2.377 6.64c-.6-.776-.43-1.768.125-2.515 1.553-2.079 6.245-2.936 7.63-1.108h.003l1.86 3.564 1.715-3.36v.002Zm.103 2.05L6.856 18.89 18.94 7.638c-1.634.004-4.174-.752-5.127-2.365ZM9.39 11.682l2.03-3.973-1.242-2.435c-.95 1.61-3.495 2.37-5.13 2.367l4.343 4.042v-.002l-.001.001Zm3.987 2.673-.56.52 4.318 4.02-2.78-5.452-.978.912ZM18.912 3.73c-1.18-.455-3.628-.8-4.384.175v.004c-.606.8 2.097 2.597 4.657 2.475.79-.04 1.387-.228 1.735-.6.577-.673-1.245-1.76-2.008-2.054ZM7.81 3.284C5.158 3.102 2.38 4.98 3.068 5.782c1.345 1.432 5.995.112 6.47-1.518l-.078-.356.003-.004c-.277-.356-.891-.567-1.653-.62Z" />
        </BaseIcon>
    )
})

export const IconStar: IconComponent<IconProps> = forwardRef(function IconStar(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M10.874 1.709c.453-.945 1.8-.945 2.252 0l2.51 5.231 5.777.757c1.04.137 1.462 1.42.695 2.144l-4.222 3.987 1.06 5.695c.193 1.036-.903 1.822-1.822 1.326L12 18.082 6.876 20.85c-.92.496-2.015-.29-1.822-1.326l1.06-5.695-4.222-3.987c-.767-.724-.345-2.007.695-2.144l5.777-.757 2.51-5.231ZM12 2.829 9.657 7.711a1.249 1.249 0 0 1-.964.698l-5.385.706 3.936 3.716c.308.29.448.718.37 1.135l-.989 5.313 4.781-2.582c.37-.2.817-.2 1.188 0l4.781 2.582-.99-5.313a1.247 1.247 0 0 1 .37-1.135l3.937-3.716-5.385-.706a1.25 1.25 0 0 1-.964-.698L12 2.828Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconStarFilled: IconComponent<IconProps> = forwardRef(function IconStarFilled(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M13.126 1.709c-.454-.945-1.8-.945-2.253 0L8.363 6.94l-5.777.757c-1.04.137-1.462 1.42-.695 2.144l4.222 3.987-1.06 5.695c-.193 1.036.903 1.822 1.822 1.326L12 18.082l5.124 2.767c.92.496 2.015-.29 1.822-1.326l-1.06-5.695 4.223-3.987c.767-.724.344-2.007-.696-2.144l-5.777-.757-2.51-5.231Z" />
        </BaseIcon>
    )
})

export const IconStarHalfFilled: IconComponent<IconProps> = forwardRef(
    function IconStarHalfFilled(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M13.127 1.709c-.454-.945-1.8-.945-2.253 0L8.364 6.94l-5.777.757c-1.04.137-1.462 1.42-.695 2.144l4.222 3.987-1.06 5.695c-.193 1.036.903 1.822 1.822 1.326L12 18.082l5.124 2.767c.92.496 2.015-.29 1.822-1.326l-1.06-5.695 4.222-3.987c.767-.724.345-2.007-.695-2.144l-5.777-.757-2.51-5.231ZM12 2.829l2.343 4.883c.182.38.546.643.964.698l5.385.706-3.936 3.716c-.308.29-.448.718-.37 1.135l.989 5.313-4.781-2.582a1.25 1.25 0 0 0-.594-.15V2.829Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconStickiness: IconComponent<IconProps> = forwardRef(function IconStickiness(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M8.292 11.748a2.77 2.77 0 0 0 0 3.942c1.098 1.08 2.861 1.098 3.941 0l5.193-5.175a.5.5 0 0 1 .705 0l2.333 2.32a.5.5 0 0 1 .001.708l-5.19 5.19c-1.35 1.331-3.131 2.088-5.021 2.088a6.945 6.945 0 0 1-4.986-2.089 7.054 7.054 0 0 1-2.088-5.003c0-1.89.738-3.672 2.088-5.005l5.173-5.172a.5.5 0 0 1 .707 0l2.316 2.316a.5.5 0 0 1 0 .708l-5.172 5.172Zm-2.43-2.412a6.17 6.17 0 0 0-1.819 4.391c.001 1.639.631 3.223 1.819 4.393a6.169 6.169 0 0 0 4.391 1.818c1.639 0 3.223-.63 4.411-1.818l2.598-2.598a.5.5 0 0 0 0-.707l-1.111-1.111a.5.5 0 0 0-.707 0l-2.617 2.616A3.64 3.64 0 1 1 7.68 11.17l2.62-2.634a.5.5 0 0 0-.002-.706L9.185 6.72a.5.5 0 0 0-.707 0L5.862 9.336Z" />
        </BaseIcon>
    )
})

export const IconStore: IconComponent<IconProps> = forwardRef(function IconStore(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M4.939 3.682A1.75 1.75 0 0 1 6.325 3h11.35a1.75 1.75 0 0 1 1.386.682l2.783 3.612a.75.75 0 0 1 .156.458v1c0 .99-.393 1.89-1.028 2.556v7.942a1.75 1.75 0 0 1-1.75 1.75H4.778a1.75 1.75 0 0 1-1.75-1.75v-7.942A3.697 3.697 0 0 1 2 8.752v-1a.75.75 0 0 1 .156-.458l2.783-3.612Zm-.411 8.598v6.97c0 .138.112.25.25.25h4.416v-2.251c0-1.538 1.276-2.751 2.806-2.751s2.806 1.213 2.806 2.75V19.5h4.416a.25.25 0 0 0 .25-.25v-6.97a3.903 3.903 0 0 1-1.305.223 3.863 3.863 0 0 1-3.084-1.52A3.863 3.863 0 0 1 12 12.502a3.863 3.863 0 0 1-3.083-1.52 3.863 3.863 0 0 1-3.084 1.52c-.457 0-.897-.078-1.305-.223Zm5.139-3.528c0 1.224 1.025 2.251 2.333 2.251 1.308 0 2.333-1.027 2.333-2.25v-.251H9.667v.25Zm-1.5-.25v.25c0 1.224-1.026 2.251-2.334 2.251a2.37 2.37 0 0 1-1.563-.58 2.21 2.21 0 0 1-.77-1.67v-.251h4.667Zm11.558-1.5-1.852-2.405a.25.25 0 0 0-.198-.097H6.325a.25.25 0 0 0-.198.097L4.275 7.002h15.45Zm.775 1.5h-4.667v.25c0 1.224 1.026 2.251 2.334 2.251.604 0 1.15-.22 1.563-.58a2.21 2.21 0 0 0 .77-1.67v-.251ZM13.306 19.5v-2.251c0-.672-.566-1.251-1.306-1.251-.74 0-1.306.579-1.306 1.25V19.5h2.611Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconSupport: IconComponent<IconProps> = forwardRef(function IconSupport(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.48256 6.54322C4.24508 8.01967 3.5 9.92282 3.5 12C3.5 14.0772 4.24508 15.9803 5.48256 17.4568L7.9739 14.9654C7.36179 14.1358 7 13.1101 7 12C7 10.8899 7.36179 9.86422 7.9739 9.03456L5.48256 6.54322ZM6.54322 5.48256L9.03456 7.9739C9.86422 7.36179 10.8899 7 12 7C13.1101 7 14.1358 7.36179 14.9654 7.9739L17.4568 5.48256C15.9803 4.24508 14.0772 3.5 12 3.5C9.92282 3.5 8.01967 4.24508 6.54322 5.48256ZM18.5174 6.54322L16.0261 9.03456C16.6382 9.86422 17 10.8899 17 12C17 13.1101 16.6382 14.1358 16.0261 14.9654L18.5174 17.4568C19.7549 15.9803 20.5 14.0772 20.5 12C20.5 9.92282 19.7549 8.01967 18.5174 6.54322ZM17.4568 18.5174L14.9654 16.0261C14.1358 16.6382 13.1101 17 12 17C10.8899 17 9.86422 16.6382 9.03456 16.0261L6.54322 18.5174C8.01967 19.7549 9.92282 20.5 12 20.5C14.0772 20.5 15.9803 19.7549 17.4568 18.5174ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM12 8.5C10.067 8.5 8.5 10.067 8.5 12C8.5 13.933 10.067 15.5 12 15.5C13.933 15.5 15.5 13.933 15.5 12C15.5 10.067 13.933 8.5 12 8.5Z"
            />
        </BaseIcon>
    )
})

export const IconTarget: IconComponent<IconProps> = forwardRef(function IconTarget(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M18.593 2.189a1 1 0 0 0-1.682-.393l-2.932 3.1a1.75 1.75 0 0 0-.479 1.202v3.341l-2.03 2.03a.75.75 0 0 0 1.06 1.061l2.03-2.03h3.342a1.75 1.75 0 0 0 1.203-.479l3.099-2.932a1 1 0 0 0-.393-1.682l-2.46-.757-.758-2.461Z" />
            <path d="M3.5 12a8.5 8.5 0 0 1 8.96-8.488.75.75 0 0 0 .08-1.498C12.36 2.004 12.18 2 12 2 6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10c0-.181-.005-.361-.014-.54a.75.75 0 0 0-1.498.08A8.5 8.5 0 1 1 3.5 12Z" />
            <path d="M11.143 7.557a.75.75 0 1 0-.287-1.473A6.015 6.015 0 0 0 12.014 18a6.015 6.015 0 0 0 5.902-4.856.75.75 0 1 0-1.473-.287 4.515 4.515 0 0 1-8.942-.87 4.515 4.515 0 0 1 3.643-4.43Z" />
        </BaseIcon>
    )
})

export const IconTerminal: IconComponent<IconProps> = forwardRef(function IconTerminal(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3 4.75C3 3.784 3.784 3 4.75 3h14.5c.966 0 1.75.784 1.75 1.75v14.5A1.75 1.75 0 0 1 19.25 21H4.75A1.75 1.75 0 0 1 3 19.25V4.75Zm1.75-.25a.25.25 0 0 0-.25.25v14.5c0 .138.112.25.25.25h14.5a.25.25 0 0 0 .25-.25V4.75a.25.25 0 0 0-.25-.25H4.75Zm1.97 2.72a.75.75 0 0 1 1.06 0l1.75 1.75a.75.75 0 0 1 0 1.06l-1.75 1.75a.75.75 0 0 1-1.06-1.06L7.94 9.5 6.72 8.28a.75.75 0 0 1 0-1.06ZM10 11.25a.75.75 0 0 1 .75-.75h2a.75.75 0 0 1 0 1.5h-2a.75.75 0 0 1-.75-.75Z"
            />
        </BaseIcon>
    )
})

export const IconTestTube: IconComponent<IconProps> = forwardRef(function IconTestTube(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.72 3.22a.75.75 0 0 1 1.06 0l7 7a.75.75 0 1 1-1.06 1.06l-.47-.47-9.97 9.97a4.286 4.286 0 0 1-6.06-6.06l9.97-9.97-.47-.47a.75.75 0 0 1 0-1.06Zm1.53 2.59L8.061 12h7.879l2.25-2.25-3.94-3.94Z"
            />
            <path d="M21.004 6a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-3.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
        </BaseIcon>
    )
})

export const IconTextWidth: IconComponent<IconProps> = forwardRef(function IconTextWidth(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M10.64 11.4V4.62H8.168V3h6.828v1.62h-2.46v6.78H10.64Zm9.252 4.394-3.143-3.143a.857.857 0 1 0-1.212 1.212l1.68 1.68h-5.36a.857.857 0 0 0 0 1.714h5.36l-1.68 1.68a.857.857 0 1 0 1.212 1.212l3.143-3.143a.857.857 0 0 0 0-1.212Z" />
            <path d="m3.25 15.794 3.144-3.143a.857.857 0 1 1 1.212 1.212l-1.68 1.68h5.36a.857.857 0 0 1 0 1.714h-5.36l1.68 1.68a.857.857 0 1 1-1.212 1.212l-3.143-3.143a.857.857 0 0 1 0-1.212Z" />
        </BaseIcon>
    )
})

export const IconTie: IconComponent<IconProps> = forwardRef(function IconTie(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M12.173 1.44c-1.26 0-2.333.945-2.333 2.16 0 .718.379 1.343.945 1.733l-.337 1.852-4.245 2.978a.481.481 0 0 0-.202.427l.225 3.165a.48.48 0 0 0 .442.443l2.468.18-.728 4.012v.001a.48.48 0 0 0 .165.458l4.32 3.6a.481.481 0 0 0 .614 0l4.32-3.6a.48.48 0 0 0 .164-.458L15.614 5.333c.566-.39.945-1.015.945-1.733 0-1.215-1.072-2.16-2.333-2.16h-2.055.002Zm0 .96h2.055c.794 0 1.372.554 1.372 1.2 0 .606-.512 1.137-1.23 1.2h-2.34c-.717-.063-1.23-.594-1.23-1.2 0-.647.579-1.2 1.373-1.2Zm-.495 3.36h3.044l.84 4.635-5.114 2.13.907-4.973a.436.436 0 0 0 .008-.06l.315-1.732ZM10.2 8.535l-.892 4.897-2.153-.157-.172-2.483 3.216-2.257h.001Zm5.542 2.827.262 1.448L9.9 15.547l.27-1.492h.001l.008-.023v-.007l.067-.375a.18.18 0 0 0 .022-.008l5.475-2.28h-.001Zm.435 2.423.3 1.665-6.81 3.052-.262-.217.285-1.59 6.487-2.91Zm.48 2.64.337 1.86L13.2 21.45l-2.707-2.258 6.164-2.767Z" />
        </BaseIcon>
    )
})

export const IconThoughtBubble: IconComponent<IconProps> = forwardRef(
    function IconThoughtBubble(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M2.002 4.75c0-.966.783-1.75 1.75-1.75h16.5c.966 0 1.75.784 1.75 1.75v12.5a1.75 1.75 0 0 1-1.75 1.75h-8.057l-4.832 2.657A.75.75 0 0 1 6.252 21v-2h-2.5a1.75 1.75 0 0 1-1.75-1.75V4.75Zm1.75-.25a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h3.25a.75.75 0 0 1 .75.75v1.482l3.888-2.14a.75.75 0 0 1 .362-.092h8.25a.25.25 0 0 0 .25-.25V4.75a.25.25 0 0 0-.25-.25h-16.5Z"
                    clipRule="evenodd"
                />
                <path d="M7.5 11.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1ZM12 11.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1ZM16.5 11.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Z" />
                <path
                    fillRule="evenodd"
                    d="M7.5 12a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm4.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm4.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm-9-.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm4.5 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm4-.5a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconThumbsDown: IconComponent<IconProps> = forwardRef(function IconThumbsDown(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.586 4.5a2.249 2.249 0 0 0-2.223 1.911l-.837 5.5A2.25 2.25 0 0 0 5.75 14.5h5.24a.75.75 0 0 1 .741.866l-.575 3.691A1.25 1.25 0 0 0 12.39 20.5a.258.258 0 0 0 .228-.136l3.885-7.546V4.5H6.586Zm11.416 0v8h2.248a.25.25 0 0 0 .25-.25v-7.5a.25.25 0 0 0-.25-.25h-2.248Zm-.421 9.5h2.669A1.75 1.75 0 0 0 22 12.25v-7.5A1.75 1.75 0 0 0 20.25 3H6.586A3.749 3.749 0 0 0 2.88 6.186l-.837 5.5A3.75 3.75 0 0 0 5.75 16h4.364l-.44 2.826A2.75 2.75 0 0 0 12.389 22c.65 0 1.26-.362 1.561-.949L17.58 14Z"
            />
        </BaseIcon>
    )
})

export const IconThumbsDownFilled: IconComponent<IconProps> = forwardRef(
    function IconThumbsDownFilled(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M18.002 4.5v8h2.248a.25.25 0 0 0 .25-.25v-7.5a.25.25 0 0 0-.25-.25h-2.248Zm-.421 9.5h2.669A1.75 1.75 0 0 0 22 12.25v-7.5A1.75 1.75 0 0 0 20.25 3H6.586A3.749 3.749 0 0 0 2.88 6.186l-.837 5.5A3.75 3.75 0 0 0 5.75 16h4.364l-.44 2.826A2.75 2.75 0 0 0 12.389 22c.65 0 1.26-.362 1.561-.949L17.58 14Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconThumbsUp: IconComponent<IconProps> = forwardRef(function IconThumbsUp(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.054 2.949A1.75 1.75 0 0 1 11.611 2a2.753 2.753 0 0 1 2.722 3.174L13.893 8h4.366a3.75 3.75 0 0 1 3.707 4.314l-.837 5.5A3.75 3.75 0 0 1 17.422 21H3.75A1.75 1.75 0 0 1 2 19.25v-7.5c0-.966.784-1.75 1.75-1.75h2.671l3.633-7.051ZM6 11.5H3.75a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25H6v-8Zm1.5 8h9.922a2.25 2.25 0 0 0 2.224-1.912l.837-5.5A2.25 2.25 0 0 0 18.26 9.5h-5.243a.75.75 0 0 1-.74-.866l.575-3.691A1.253 1.253 0 0 0 11.61 3.5a.252.252 0 0 0-.224.136L7.5 11.182V19.5Z"
            />
        </BaseIcon>
    )
})

export const IconThumbsUpFilled: IconComponent<IconProps> = forwardRef(
    function IconThumbsUpFilled(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M11.611 2c-.654 0-1.256.365-1.557.949L6.421 10H3.75A1.75 1.75 0 0 0 2 11.75v7.5c0 .966.784 1.75 1.75 1.75h13.672a3.75 3.75 0 0 0 3.707-3.186l.837-5.5A3.75 3.75 0 0 0 18.26 8h-4.367l.44-2.826A2.753 2.753 0 0 0 11.612 2ZM6 11.5v8H3.75a.25.25 0 0 1-.25-.25v-7.5a.25.25 0 0 1 .25-.25H6Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconToggle: IconComponent<IconProps> = forwardRef(function IconToggle(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <g clipPath="url(#icon/toggle__a)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M1.5 11.75A5.75 5.75 0 0 1 7.25 6h10.5a5.75 5.75 0 0 1 5.75 5.75v.5A5.75 5.75 0 0 1 17.75 18H7.25a5.75 5.75 0 0 1-5.75-5.75v-.5Zm16-4.25a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Z"
                />
            </g>
            <defs>
                <clipPath id="icon/toggle__a">
                    <path d="M0 0h24v24H0z" />
                </clipPath>
            </defs>
        </BaseIcon>
    )
})

export const IconToolbar: IconComponent<IconProps> = forwardRef(function IconToolbar(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M6.5 9a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1ZM10 9a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1ZM13.5 9a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Z" />
            <path
                fillRule="evenodd"
                d="M6.5 9.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm3.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm3.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm-7-.5a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1ZM10 9a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm3-.5a.5.5 0 1 0 1 0 .5.5 0 0 0-1 0Z"
                clipRule="evenodd"
            />
            <path
                fillRule="evenodd"
                d="M2 5.75C2 4.784 2.784 4 3.75 4h16.5c.966 0 1.75.784 1.75 1.75v6.5a.75.75 0 0 1-1.5 0v-6.5a.25.25 0 0 0-.25-.25H3.75a.25.25 0 0 0-.25.25v13.5c0 .138.112.25.25.25h8.5a.75.75 0 0 1 0 1.5h-8.5A1.75 1.75 0 0 1 2 19.25V5.75Zm11.47 7.72a.75.75 0 0 1 .75-.187l7 2.154a.75.75 0 0 1 .115 1.388l-3.007 1.503-1.503 3.007a.75.75 0 0 1-1.388-.114l-2.154-7a.75.75 0 0 1 .187-.751Zm1.663 1.663 1.187 3.857.778-1.556a.75.75 0 0 1 .336-.336l1.556-.778-3.856-1.187Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconToolbarFilled: IconComponent<IconProps> = forwardRef(
    function IconToolbarFilled(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path d="M3.75 4A1.75 1.75 0 0 0 2 5.75v13.5c0 .966.784 1.75 1.75 1.75h8.5a.75.75 0 0 0 0-1.5h-8.5a.25.25 0 0 1-.25-.25V5.75a.25.25 0 0 1 .25-.25h16.5a.25.25 0 0 1 .25.25v6.5a.75.75 0 0 0 1.5 0v-6.5A1.75 1.75 0 0 0 20.25 4H3.75Z" />
                <path d="M6.5 9.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2ZM13.47 13.47a.75.75 0 0 1 .75-.187l7 2.154a.75.75 0 0 1 .115 1.388l-3.007 1.503-1.503 3.007a.75.75 0 0 1-1.388-.114l-2.154-7a.75.75 0 0 1 .187-.751ZM10 9.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2ZM13.5 9.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2Z" />
            </BaseIcon>
        )
    }
)

export const IconTortoise: IconComponent<IconProps> = forwardRef(function IconTortoise(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M18.024 6.003c-.224.011-.447.06-.664.158-.679.306-1.047 1.006-1.338 1.709-.29.702-.506 1.459-.708 1.967-.048.118-.181.273-.337.439l-.012-.034c-.11-.422-.515-.698-.99-.753-.328-.603-.875-1.27-1.776-1.676a4.444 4.444 0 0 0-1.731-.37c-.67-.016-1.438.087-2.305.36-1.917.6-2.918 1.788-3.328 2.923a3.933 3.933 0 0 0-.236 1.585c.02.319.085.586.19.82-.08.064-.109.05-.19.158a1.062 1.062 0 0 0-.214.551c-.015.203.053.42.18.63a5.22 5.22 0 0 0-1.473 1.124.364.364 0 0 0 .393.585 3.277 3.277 0 0 1 1.125-.146c.288.01.485.092.674.168-.081.249-.216.558-.37.754-.199.25-.365.455-.315.787a.59.59 0 0 0 .292.404c.14.073.281.09.427.09.406 0 .65.006 1.417 0a1.08 1.08 0 0 0 .73-.314c.195-.19.434-.543.72-.979 2.676.482 4.343-.025 4.779-.18.05.162.065.255.202.518.114.216.258.431.461.618.204.187.485.337.81.337h1.282c.674 0 1.043-.468.877-.877-.383-.937-.582-1.609-.652-2.114-.196-1.405.479-2.927 1.866-4.25.346.3.783.475 1.226.45.595-.034 1.193-.306 1.551-.832.678-.99.45-2.31-.168-3.25v-.01a3.163 3.163 0 0 0-1.316-1.136 2.392 2.392 0 0 0-.855-.214 2.002 2.002 0 0 0-.224 0Zm.19.72c.192.011.389.069.585.157.393.179.768.505 1.012.877.487.74.625 1.774.17 2.44-.209.304-.603.508-.99.529-.388.02-.726-.119-.9-.417a.366.366 0 0 0-.254-.174.366.366 0 0 0-.297.085c-1.68 1.484-2.565 3.342-2.316 5.127.079.56.303 1.27.652 2.147-.017.007-.01.012-.157.012h-1.282a.457.457 0 0 1-.326-.135c-.111-.102-.215-.27-.304-.439-.177-.336-.27-.674-.27-.674a.362.362 0 0 0-.483-.26s-1.954.827-4.97.214a.364.364 0 0 0-.382.158c-.374.579-.604.887-.742 1.023-.138.136-.114.111-.236.113-.713.005-.948 0-1.316 0 .023-.048.003-.02.068-.102.232-.292.649-.798.64-1.428a.363.363 0 0 0-.146-.292c-.366-.267-.851-.363-1.338-.382H4.6c.28-.173.545-.298.697-.338a.386.386 0 0 0 .078-.033c.034.004.068.018.102.022.348.04.749.021 1.203-.034.906-.11 2-.373 3.002-.697 1.96-.632 3.648-1.548 4.655-2.372a.38.38 0 0 0 .078-.056c.064-.054.127-.11.18-.169.532-.438 1.156-.891 1.406-1.518.23-.578.42-1.327.686-1.968.265-.64.603-1.145.978-1.315.177-.08.36-.112.55-.101Zm-7.769 1.439a3.694 3.694 0 0 1 1.45.315 3.15 3.15 0 0 1 1.53 1.473c.073.139.226.216.382.19.301-.046.394.08.45.293.056.214 0 .534-.315.799a.34.34 0 0 0-.056.045c-.897.736-2.544 1.64-4.43 2.248-.964.31-2.018.572-2.856.675-.419.05-.79.062-1.046.034-.255-.028-.368-.113-.36-.102-.081-.12-.082-.196-.078-.247a.299.299 0 0 1 .067-.157c.085-.111.225-.191.225-.191a.367.367 0 0 0 .187-.25.366.366 0 0 0-.074-.302c-.06-.07-.179-.352-.203-.72a3.26 3.26 0 0 1 .203-1.292c.338-.936 1.137-1.93 2.867-2.474.804-.253 1.478-.35 2.057-.337Zm8.928 0a.54.54 0 1 0 0 1.08.54.54 0 0 0 0-1.08Z" />
        </BaseIcon>
    )
})

export const IconTrash: IconComponent<IconProps> = forwardRef(function IconTrash(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M7.583 5H2.75a.75.75 0 0 0 0 1.5h1.297l.894 13.863A1.75 1.75 0 0 0 6.688 22h10.624a1.75 1.75 0 0 0 1.747-1.637L19.953 6.5h1.297a.75.75 0 0 0 0-1.5h-4.833a4.75 4.75 0 0 0-8.834 0Zm1.678 0h5.478A3.248 3.248 0 0 0 12 3.5c-1.15 0-2.162.598-2.74 1.5Zm6.596 1.5H5.55l.888 13.766a.25.25 0 0 0 .25.234h10.624a.25.25 0 0 0 .25-.234L18.45 6.5h-2.593Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconTrends: IconComponent<IconProps> = forwardRef(function IconTrends(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M21.194 6.158a1 1 0 0 1 .146 1.406l-5.387 6.63a2.25 2.25 0 0 1-2.66.637l-2.389-1.061a1 1 0 0 0-1.113.207l-4.7 4.7a1 1 0 0 1-1.414 0l-.354-.354a1 1 0 0 1 0-1.414l5.307-5.307a2.25 2.25 0 0 1 2.505-.465l2.299 1.021a1 1 0 0 0 1.182-.283l4.783-5.887a1 1 0 0 1 1.407-.146l.388.316Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconTrending: IconComponent<IconProps> = forwardRef(function IconTrending(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M15 6.75a.75.75 0 0 1 .75-.75h5.5a.75.75 0 0 1 .75.75v5.5a.75.75 0 0 1-1.5 0V8.57l-6.263 6.255a1.75 1.75 0 0 1-2.474-.001l-2.586-2.587a.25.25 0 0 0-.354 0L3.28 17.78a.75.75 0 0 1-1.06-1.06l5.543-5.543a1.75 1.75 0 0 1 2.474 0l2.587 2.586a.25.25 0 0 0 .353 0L19.45 7.5H15.75a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconTriangleDown: IconComponent<IconProps> = forwardRef(
    function IconTriangleDown(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path
                    fillRule="evenodd"
                    d="M4.187 6.535C3.613 5.388 4.427 4 5.735 4h12.53c1.309 0 2.122 1.388 1.548 2.535L13.547 19.04c-.64 1.28-2.453 1.28-3.094 0L4.187 6.535ZM5.735 5.5c-.16 0-.297.183-.206.363l6.265 12.506a.228.228 0 0 0 .412 0l6.266-12.506c.09-.18-.048-.363-.207-.363H5.735Z"
                    clipRule="evenodd"
                />
            </BaseIcon>
        )
    }
)

export const IconTriangleDownFilled: IconComponent<IconProps> = forwardRef(
    function IconTriangleDownFilled(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path d="M4.187 6.534C3.613 5.388 4.427 4 5.735 4h12.53c1.309 0 2.122 1.388 1.548 2.534L13.547 19.04c-.64 1.28-2.453 1.28-3.094 0L4.187 6.534Z" />
            </BaseIcon>
        )
    }
)

export const IconTriangleUp: IconComponent<IconProps> = forwardRef(function IconTriangleUp(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M10.453 4.96c.64-1.28 2.453-1.28 3.094 0l6.266 12.505c.574 1.147-.24 2.535-1.548 2.535H5.735c-1.309 0-2.122-1.388-1.548-2.535L10.453 4.96Zm1.753.671a.228.228 0 0 0-.412 0L5.529 18.137c-.09.18.047.363.206.363h12.53c.16 0 .297-.183.207-.363L12.206 5.631Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconTriangleUpFilled: IconComponent<IconProps> = forwardRef(
    function IconTriangleUpFilled(props, ref): JSX.Element {
        return (
            <BaseIcon ref={ref} {...props}>
                <path d="M4.187 17.465C3.613 18.612 4.427 20 5.735 20h12.53c1.309 0 2.122-1.388 1.548-2.535L13.547 4.96c-.64-1.28-2.453-1.28-3.094 0L4.187 17.465Z" />
            </BaseIcon>
        )
    }
)

export const IconVideoCamera: IconComponent<IconProps> = forwardRef(function IconVideoCamera(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M2 5.75C2 4.784 2.784 4 3.75 4h10.5c.966 0 1.75.784 1.75 1.75v3.036l4.191-2.095A1.25 1.25 0 0 1 22 7.809v8.382a1.25 1.25 0 0 1-1.809 1.118L16 15.213v3.037A1.75 1.75 0 0 1 14.25 20H3.75A1.75 1.75 0 0 1 2 18.25V5.75Zm14 7.787 4.5 2.25V8.213l-4.5 2.25v3.072ZM14.5 5.75a.25.25 0 0 0-.25-.25H3.75a.25.25 0 0 0-.25.25v12.5c0 .138.112.25.25.25h10.5a.25.25 0 0 0 .25-.25V5.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconUndo: IconComponent<IconProps> = forwardRef(function IconUndo(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path d="M19.521 12a7.5 7.5 0 0 0-7.5-7.5c-2.422 0-4.076.963-5.727 2.75H8.75a.75.75 0 1 1 0 1.5h-3.5C4.56 8.75 4 8.19 4 7.5V4a.75.75 0 0 1 1.5 0v1.908C7.262 4.104 9.223 3 12.021 3a9 9 0 1 1-8.487 12 .75.75 0 1 1 1.414-.5A7.5 7.5 0 0 0 19.521 12Z" />
        </BaseIcon>
    )
})

export const IconUnlock: IconComponent<IconProps> = forwardRef(function IconUnlock(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M7 7a5 5 0 0 1 9.923-.877.75.75 0 0 1-1.477.262A3.501 3.501 0 0 0 8.5 7v2h9.75c.966 0 1.75.784 1.75 1.75v9.5A1.75 1.75 0 0 1 18.25 22H5.75A1.75 1.75 0 0 1 4 20.25v-9.5C4 9.784 4.784 9 5.75 9H7V7Zm-1.25 3.5a.25.25 0 0 0-.25.25v9.5c0 .138.112.25.25.25h12.5a.25.25 0 0 0 .25-.25v-9.5a.25.25 0 0 0-.25-.25H5.75ZM12 13.25a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3a.75.75 0 0 1 .75-.75Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconUpload: IconComponent<IconProps> = forwardRef(function IconUpload(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 5.5a5.253 5.253 0 0 0-4.965 3.537.75.75 0 0 1-.613.5A4.501 4.501 0 0 0 7 18.5h2A.75.75 0 0 1 9 20H7A6 6 0 0 1 5.78 8.124a6.751 6.751 0 0 1 12.954 2.164A4.876 4.876 0 0 1 18.125 20H15a.75.75 0 0 1 0-1.5h3.125a3.375 3.375 0 1 0-.107-6.748.75.75 0 0 1-.773-.781A5.25 5.25 0 0 0 12 5.5Zm-3.03 8.22 2.5-2.5a.75.75 0 0 1 1.06 0l2.5 2.5a.75.75 0 1 1-1.06 1.06l-1.22-1.22v5.69a.75.75 0 0 1-1.5 0v-5.69l-1.22 1.22a.75.75 0 1 1-1.06-1.06Z"
            />
        </BaseIcon>
    )
})

export const IconUser: IconComponent<IconProps> = forwardRef(function IconUser(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 3.5a8.5 8.5 0 0 0-6.223 14.29C7.247 16.074 9.417 15 12 15c2.583 0 4.752 1.074 6.223 2.79A8.5 8.5 0 0 0 12 3.5Zm5.108 15.294C15.917 17.388 14.151 16.5 12 16.5c-2.15 0-3.917.888-5.109 2.294A8.46 8.46 0 0 0 12 20.5a8.46 8.46 0 0 0 5.108-1.706ZM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10c0 2.974-1.3 5.646-3.359 7.476A9.965 9.965 0 0 1 12 22a9.965 9.965 0 0 1-6.641-2.524A9.977 9.977 0 0 1 2 12Zm10-4.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM8 10a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z"
            />
        </BaseIcon>
    )
})

export const IconUserPaths: IconComponent<IconProps> = forwardRef(function IconUserPaths(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18 9.5a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1h-1a1 1 0 0 0-1 1v5.5ZM3 18a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12Zm16 3.5a1 1 0 0 1-1-1V15a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v5.5a1 1 0 0 1-1 1h-1ZM17.5 8a1 1 0 0 1-1 1h-1.992a1 1 0 0 0-.563.174l-3.89 2.652a1 1 0 0 1-.563.174 1 1 0 0 1 .563.174l3.89 2.652a1 1 0 0 0 .563.174H16.5a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1.992a1 1 0 0 1-.563-.174l-3.89-2.652A1 1 0 0 0 9.492 17H7.5a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1.992a1 1 0 0 0 .563-.174l3.89-2.652A1 1 0 0 1 14.508 4H16.5a1 1 0 0 1 1 1v3Z"
            />
        </BaseIcon>
    )
})

export const IconWarning: IconComponent<IconProps> = forwardRef(function IconWarning(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12.216 3.602a.25.25 0 0 0-.432 0L3.28 18.124a.25.25 0 0 0 .216.376h17.01a.25.25 0 0 0 .216-.376L12.216 3.602Zm-1.726-.758a1.75 1.75 0 0 1 3.02 0l8.505 14.522C22.7 18.532 21.857 20 20.505 20H3.495c-1.352 0-2.194-1.468-1.51-2.634L10.49 2.844ZM12 9a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 12 9Z"
                clipRule="evenodd"
            />
            <path d="M13 15.75a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
        </BaseIcon>
    )
})

export const IconWebcam: IconComponent<IconProps> = forwardRef(function IconWebcam(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M12 3.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM4 10a8 8 0 1 1 8.75 7.965V20.5h4.5a.75.75 0 0 1 0 1.5H6.75a.75.75 0 0 1 0-1.5h4.5v-2.535A8 8 0 0 1 4 10Zm8-2.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM8 10a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconWebhooks: IconComponent<IconProps> = forwardRef(function IconWebhooks(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18.147 17.606H7.324v1.16a2.543 2.543 0 0 1-2.507 2.09 2.539 2.539 0 0 1-2.545-2.533c0-.864.434-1.626 1.097-2.083l-.73-1.038A3.792 3.792 0 0 0 1 18.322c0 2.099 1.71 3.8 3.817 3.8 1.89 0 3.46-1.368 3.765-3.164h9.514c.22.377.63.63 1.1.63a1.27 1.27 0 0 0 1.272-1.266 1.273 1.273 0 0 0-2.322-.717Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.678 17.796A1.267 1.267 0 0 1 4.85 17l4.772-8.265a3.803 3.803 0 0 1-.867-4.833 3.802 3.802 0 0 1 7.08 1.571l-1.262.11a2.535 2.535 0 1 0-3.868 2.372l.697.403-5.404 9.361c.154.321.17.701.023 1.045a1.268 1.268 0 1 1-2.342-.969Z"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="m11.935 7.06 5.351 9.266.637-.368.01.02.017-.01c1.213-.7 2.758-.295 3.45.906.694 1.201.273 2.742-.94 3.442a2.538 2.538 0 0 1-2.345.107l-.54 1.143a3.811 3.811 0 0 0 3.513-.163c1.82-1.05 2.451-3.362 1.411-5.163-.94-1.626-2.92-2.28-4.635-1.62l-4.748-8.222a1.252 1.252 0 0 0-.01-1.23 1.262 1.262 0 0 0-1.726-.454 1.262 1.262 0 0 0-.47 1.721c.22.382.613.603 1.025.624Z"
            />
        </BaseIcon>
    )
})

export const IconWrench: IconComponent<IconProps> = forwardRef(function IconWrench(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                d="M14.57 3.5a5.93 5.93 0 0 0-5.476 8.213.75.75 0 0 1-.158.815L3.5 18.049a.25.25 0 0 0 .006.357l2.247 2.125a.25.25 0 0 0 .35-.006l5.36-5.452a.75.75 0 0 1 .825-.167 5.93 5.93 0 0 0 8.17-6.192l-1.761 1.76a3.657 3.657 0 0 1-5.172-5.171l1.76-1.76a6 6 0 0 0-.716-.043ZM7.138 9.43a7.43 7.43 0 0 1 9.876-7.018.75.75 0 0 1 .284 1.239l-2.713 2.713a2.157 2.157 0 1 0 3.05 3.05l2.713-2.713a.75.75 0 0 1 1.239.284 7.43 7.43 0 0 1-9.397 9.488l-5.019 5.104a1.75 1.75 0 0 1-2.45.044l-2.246-2.125a1.75 1.75 0 0 1-.045-2.5l5.1-5.18a7.423 7.423 0 0 1-.392-2.385Z"
                clipRule="evenodd"
            />
        </BaseIcon>
    )
})

export const IconX: IconComponent<IconProps> = forwardRef(function IconX(props, ref): JSX.Element {
    return (
        <BaseIcon ref={ref} {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.75 3.69 12 10.94l7.25-7.25 1.06 1.06L13.06 12l7.25 7.25-1.06 1.06L12 13.06l-7.25 7.25-1.06-1.06L10.94 12 3.69 4.75l1.06-1.06Z"
            />
        </BaseIcon>
    )
})
